import SidePanelTabs from './SidePanelTabs';
import PabsResponse, { PabsMessageText } from './PabsResponse';
import { communicationText, responseChoices } from '../utils/pabsCommunication';
import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import {
  Box, Flex, Text, HStack, VStack, Badge, Spinner, Button, IconButton, Avatar, Checkbox,
  Select, Input, Collapse, useToast, Switch, SimpleGrid, Tooltip, Textarea,
  Divider, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon,
  Menu, MenuButton, MenuList, MenuItem,
  Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody,
  AlertDialog, AlertDialogOverlay, AlertDialogContent,
  AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
  Portal, useBreakpointValue,
} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { CopyableCommand } from './client/CopyableCommand';
import './client/ClientInstallInstructions.css';
import ReactMarkdown from 'react-markdown';
import { UserContext } from './UserContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { getConnectorLogo } from '../utils/connectorLogos';
import CreditTopUpModal from './CreditTopUpModal';
import usePlanLimits from '../hooks/usePlanLimits';
import useJobConversationScroll from '../hooks/useJobConversationScroll';
import { GithubReviewCard } from './AgentToolCallCard';
import GitHubConnectBanner from './GitHubConnectBanner';
import { markdownComponents } from './FloatingChatbot';
import { PulseSettingsForm } from './PulseView';
import { governedTaskStatus, userSafeFailureMessage, linkedWorkStatus, lifecycleStatusLabel, jobResponse, projectRoutineLinkedWork } from '../services/JobLifecycleProjection';

// Markdown in Pabs bubbles is a distinct surface from the light chat canvas.
// Keep shared link behaviour, but define every text role explicitly so nested
// lists and emphasis cannot inherit light-surface colours.
const darkMarkdownComponents = {
  ...markdownComponents,
  p: ({ children }) => <Text mb={3} lineHeight="1.7" color="gray.100" whiteSpace="pre-wrap">{children}</Text>,
  strong: ({ children }) => <Text as="strong" fontWeight="bold" color="white">{children}</Text>,
  ol: ({ children }) => <Box as="ol" pl="24px" mb={4} mt={2} color="gray.100" sx={{ listStyleType: 'decimal' }}>{children}</Box>,
  ul: ({ children }) => <Box as="ul" pl="24px" mb={4} mt={2} color="gray.100" sx={{ listStyleType: 'disc' }}>{children}</Box>,
  li: ({ children }) => <Box as="li" mb={2} lineHeight="1.6" color="gray.100" sx={{ '&::marker': { color: 'var(--chakra-colors-gray-300)' } }}>{children}</Box>,
  h1: ({ children }) => <Text fontSize="xl" fontWeight="bold" mb={3} mt={4} color="white">{children}</Text>,
  h2: ({ children }) => <Text fontSize="lg" fontWeight="bold" mb={3} mt={4} color="white">{children}</Text>,
  h3: ({ children }) => <Text fontSize="md" fontWeight="bold" mb={2} mt={3} color="white">{children}</Text>,
  hr: () => <Box as="hr" my={4} borderColor="whiteAlpha.300" borderTopWidth="1px" borderStyle="solid"/>,
  code: ({ inline, children }) => {
    const value = Array.isArray(children) ? children.join('') : String(children || '');
    const isInline = inline !== undefined ? inline : !value.includes('\n');
    return isInline
      ? <Text as="code" bg="whiteAlpha.200" color="gray.100" px={1} py={0.5} borderRadius="sm" fontSize="sm" display="inline">{children}</Text>
      : <Box as="pre" bg="blackAlpha.300" color="gray.100" p={3} borderRadius="md" overflowX="auto" mb={3}><Text as="code" fontSize="sm">{children}</Text></Box>;
  },
  a: props => {
    const link = markdownComponents.a(props);
    return React.cloneElement(link, {
      color: 'blue.200',
      style: { ...(link.props.style || {}), color: 'var(--chakra-colors-blue-200)' },
      _hover: { color: 'blue.100' },
    });
  },
};

// ---------------------------------------------------------------------------
// Constants — Tasks
// ---------------------------------------------------------------------------
const STATUS_LABEL = {
  suggested:         'Suggested',
  todo:              'Planned',
  pending:           'Coming up soon',
  awaiting_approval: 'Needs review',
  waiting_for_user:  'Needs your input',
  waiting_for_approval: 'Needs review',
  waiting_for_evidence: 'Waiting for evidence',
  waiting_for_credits: 'Needs credits',
  blocked:           'Blocked',
  running:           'In progress',
  completed:         'Done',
  failed:            'Something went wrong',
  active:            'Active',
  paused:            'Paused',
  unscheduled:       'Not scheduled',
  cancelled:         'Cancelled',
};

function isCreditBlockedError(value) {
  const text = String(value || '').toLowerCase();
  return Boolean(text) && (
    text.includes('task limit reached') ||
    text.includes('not enough credits') ||
    text.includes('insufficient_credits') ||
    text.includes('insufficient agent task credits')
  );
}

function dispatchCreditBlockedReply() {
  window.dispatchEvent(new CustomEvent('pabs:assistantMessage', {
    detail: {
      source: 'github_change_credit_block',
      text: [
        "I can’t open this GitHub pull request yet because we’re out of task credits.",
        "- [Top up credits](/topup) if you want me to open it now. This approval needs 1 task credit.",
        "- [Upgrade to Pro](/upgrade) if these agent jobs are becoming part of your regular workflow.",
        "- [Check your usage](/plan-usage) to see the current balance and reset timing.",
        "Once credits are back, approve the card again and I’ll pick it up from there.",
      ].join('\n\n'),
    },
  }));
}

function dispatchGithubDecisionReply(decision, result = {}, assistantMessage = '') {
  if (assistantMessage && String(assistantMessage).trim()) {
    window.dispatchEvent(new CustomEvent('pabs:assistantMessage', {
      detail: {
        source: 'github_change_decision',
        text: String(assistantMessage).trim(),
      },
    }));
    return;
  }


}

function dispatchDecisionCaptureReply(assistantMessage = '') {
  const text = String(assistantMessage || '').trim();
  if (!text) return;
  window.dispatchEvent(new CustomEvent('pabs:assistantMessage', {
    detail: {
      source: 'decision_capture_decision',
      text,
    },
  }));
}

function dispatchDecisionCaptureChatReview(item = {}, candidate = {}, result = {}, parameters = {}) {
  const taskId = item?.id || result?.task_id;
  const planId = item?.plan_id || parameters?.plan_id;
  const message = `I'm not sure yet. Review decision job #${taskId} with me.`;

  window.dispatchEvent(new CustomEvent('pabs:prefillPlanChat', {
    detail: {
      planId,
      source: 'decision_capture_review',
      taskId,
      message,
      newConversation: true,
    },
  }));
  window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
}

function prefillJobChat(item = {}, message = '') {
  window.dispatchEvent(new CustomEvent('pabs:prefillPlanChat', {
    detail: {
      planId: item?.plan_id || item?._plan_id,
      source: 'job_input_review',
      taskId: item?.id,
      message,
      newConversation: true,
    },
  }));
  window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
}
const STATUS_COLOR = {
  suggested:         'purple',
  todo:              'gray',
  pending:           'blue',
  awaiting_approval: 'orange',
  waiting_for_user:  'orange',
  waiting_for_approval: 'orange',
  waiting_for_evidence: 'orange',
  waiting_for_credits: 'purple',
  blocked:           'orange',
  running:           'blue',
  completed:         'green',
  failed:            'red',
  active:            'green',
  paused:            'gray',
  unscheduled:       'gray',
  cancelled:         'gray',
};
const STEP_STATUS_LABEL = {
  not_started: 'Not run',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  skipped: 'Skipped',
  cancelled: 'Cancelled',
};
const STEP_STATUS_COLOR = {
  not_started: 'gray',
  running: 'blue',
  completed: 'green',
  failed: 'red',
  skipped: 'gray',
  cancelled: 'gray',
};
const TASK_TYPE_LABEL = {
  generate_pulse:                'Strategy pulse',
  generate_marketing_engagement: 'Email follow-up',
  agent_mission:                 'Pabs mission',
  github_change_request:         'GitHub change request',
  product_requirement_issue:      'Build Requirement',
  decision_capture:              'Decision needs commitment',
  'Decision needs commitment':   'Decision needs commitment',
  'Decision outcome check':      'Decision outcome check',
};
const TASK_TYPE_DESCRIPTION = {
  generate_pulse:                'A full strategy check-in generated by PABS.',
  generate_marketing_engagement: 'A follow-up email sent to keep you on track.',
  agent_mission:                 'A dynamic Pabs mission that can use multiple capabilities and return for your input.',
  github_change_request:         'A governed GitHub change request with branch, PR, and review steps.',
  product_requirement_issue:      'A product build requirement captured from strategy, learning, decision, or conversation.',
  decision_capture:              'Pabs caught a decision moment that still needs a call.',
  'Decision needs commitment':   'Pabs caught a decision moment that still needs a call.',
  'Decision outcome check':      'A short accountability check linked to a committed decision.',
};
const STATUS_TABS = [
  { label: 'Planned',     statuses: ['todo', 'pending', 'paused'] },
  { label: 'Routines',    routine: true },
  { label: 'In progress', statuses: ['running', 'awaiting_approval', 'waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'waiting_for_credits', 'blocked'] },
  { label: 'Completed',   statuses: ['completed'] },
  { label: 'Failed',      statuses: ['failed'] },
  { label: 'Cancelled',   statuses: ['cancelled'] },
  { label: 'Client activity', activity: true },
];
const WAITING_TASK_STATUSES = ['waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'waiting_for_credits', 'blocked'];
const TERMINAL_TASK_STATUSES = ['completed', 'failed', 'cancelled', 'archived'];

function formatDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }) + ', ' +
    d.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
}

function isDueNow(iso) {
  if (!iso) return false;
  const date = new Date(iso);
  return !Number.isNaN(date.getTime()) && date <= new Date();
}

function effectiveTaskStatus(item = {}) {
  const governedStatus = governedTaskStatus(item);
  if (governedStatus !== item.status) return governedStatus;
  if (TERMINAL_TASK_STATUSES.includes(item.status)) return item.status;
  if (WAITING_TASK_STATUSES.includes(item.status)) return item.status;
  // A saved report describes the previous assessment, not a queued retry or
  // the execution that is currently processing newly submitted evidence.
  if (item.status === 'running' || (['todo','pending'].includes(item.status) && Number(item.attempt_count || 0) > 0)) return item.status;
  if (item.status === 'paused' || item.mission_status === 'paused') return 'paused';
  // A future one-time mission is Planned too. Its result can still contain the
  // previous run's waiting state, but the current mission/task state wins.
  if (item.mission_status === 'waiting_for_trigger') return item.status || 'todo';
  if (WAITING_TASK_STATUSES.includes(item.mission_status)) return item.mission_status;
  const resultState = safeObject(item.result).state;
  if (WAITING_TASK_STATUSES.includes(resultState)) return resultState;
  return item.status;
}

function decisionCaptureStatus(item = {}) {
  const result = safeObject(item.result);
  return result.decision_status || result.decision_candidate?.status || null;
}

function isDecisionCaptureTask(item = {}) {
  const result = safeObject(item.result);
  return item.capability === 'decision_capture'
    || item.task_type === 'decision_capture'
    || item.task_type === 'Decision needs commitment'
    || !!result.decision_candidate;
}

function needsDecisionCommitment(item = {}) {
  if (!isDecisionCaptureTask(item)) return false;
  const status = decisionCaptureStatus(item);
  // committed and rejected are settled decisions; abandoned is a legacy close.
  // candidate and deferred are still the human's open call.
  return !['committed', 'rejected', 'abandoned'].includes(status)
    && !['completed', 'cancelled', 'archived'].includes(item.status);
}

function isSystemNotificationTask(item = {}) {
  const type = String(item.task_type || '').toLowerCase();
  return item.capability === 'send_user_email' && type.startsWith('mission needs your input');
}

function isClientActivity(item = {}) {
  return !item.capability && String(item.task_type || '').startsWith('MCP ');
}
function clientActivityLabel(item = {}) {
  const result=safeObject(item.result),params=safeObject(item.parameters);
  if(Number(result.grouped_count)>1) return `Context checks (${result.grouped_count} calls)`;
  return humanizeMentalModelValue(params.tool_name || result.tool_name || item.task_type || 'Client call');
}

function taskCadence(item = {}) {
  const missionTrigger = safeObject(item.mission_trigger);
  if (['cancelled', 'completed'].includes(item.mission_status)) return null;
  if (missionTrigger.type === 'recurring') {
    return missionTrigger.frequency || missionTrigger.schedule || 'recurring';
  }

  const parameters = safeObject(item.parameters);
  const recurrence = safeObject(parameters.recurrence || parameters.schedule);
  if (recurrence.type === 'recurring' || recurrence.frequency) {
    return recurrence.frequency || recurrence.schedule || 'recurring';
  }

  if (item.record_type === 'routine' && item.task_type === 'generate_pulse' && item.pulse_frequency) {
    return item.pulse_frequency;
  }
  return null;
}

export function findLinkedJobRecord(records, id, recordType = 'task') {
  return records.find(record => String(record.id) === String(id) && record.record_type === recordType);
}

export function findRefreshedJobRecord(records, selected, isRoutine = false) {
  if (!isRoutine && selected.record_type !== 'routine') {
    return findLinkedJobRecord(records, selected.id, selected.record_type);
  }
  return records.find(record => routineKey(record) === routineKey(selected));
}

export function buildRoutinePresentation(item = {}, mission = null, label = 'Routine', linkedWork = []) {
  const routineStatus = mission?.status || item.mission_status || item.status || 'active';
  const paused = routineStatus === 'paused';
  const unscheduled = routineStatus === 'unscheduled';
  const objective = mission?.objective
    || item.objective
    || item.reasoning
    || TASK_TYPE_DESCRIPTION[item.task_type]
    || 'Pabs will run this routine on its schedule and report material changes.';
  const nextRunAt = mission?.next_run_at || item.next_run_at || item.mission_next_run_at || item.scheduled_at || null;

  return {
    version: 1,
    identity: { id: item.id || item.mission_id, title: label, kind: 'routine' },
    now: {
      state: 'scheduled',
      reason: null,
      raw_status: routineStatus,
      display_mode: 'summary',
      title: 'What Pabs will do',
      message: objective,
      schedule_at: paused || unscheduled ? null : nextRunAt,
      related_work_ids: [],
      primary_action: paused
        ? { type: 'resume_routine', label: 'Schedule routine' }
        : unscheduled
          ? { type: 'schedule_routine', label: 'Set schedule' }
          : null,
      secondary_actions: routineStatus === 'active' ? [{ type: 'pause_routine', label: 'Pause routine' }] : [],
    },
    // A routine is a schedule/container. Completed steps belong to individual
    // historical runs and must never be projected as progress on the container.
    work: [],
    activity: [],
    linked_work: linkedWork,
    details: {
      objective,
      request: {
        message: objective,
        requested_by: 'Pabs',
        actor: 'pabs',
        via: null,
        at: item.created_at || mission?.created_at || null,
      },
      schedule: { scheduled_at: nextRunAt },
      routine: true,
      notifications: { configurable: true },
      sources: [],
    },
  };
}

function suggestedJobCadence(item = {}) {
  const trigger = safeObject(item.mission_trigger || safeObject(item.parameters).trigger);
  if (trigger.type === 'recurring') return trigger.frequency || trigger.schedule || 'recurring';
  return 'once';
}

function cadenceLabel(value) {
  return {
    once: 'One-time job',
    daily: 'Daily routine',
    weekdays: 'Weekday routine',
    weekly: 'Weekly routine',
    monthly: 'Monthly routine',
    recurring: 'Recurring routine',
  }[value] || humanizeMentalModelValue(value);
}

function isCreditLimitError(message = '') {
  const text = String(message).toLowerCase();
  return text.includes('credit') || text.includes('task limit') || text.includes('run more agent tasks');
}

function parseNeededCredits(message = '') {
  const match = String(message).match(/needed:\s*(\d+)/i);
  return match ? Number(match[1]) : null;
}

function creditWord(amount) {
  return `${amount} credit${amount === 1 ? '' : 's'}`;
}

function formatCreditReset(resetAt) {
  if (!resetAt) return null;
  return new Date(resetAt).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function ActivateJobButton({
  children = 'Schedule now',
  onActivate,
  isLoading,
  creditBalance,
  creditCost = 1,
  creditGated = true,
  creditsResetAt,
  onTopUp,
  stopPropagation = false,
  buttonProps = {},
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [serverNeededCredits, setServerNeededCredits] = useState(null);
  const knownBalance = typeof creditBalance === 'number';
  const requiredCredits = serverNeededCredits || creditCost || 1;
  const needsTopUp = creditGated && knownBalance && creditBalance < requiredCredits;
  const resetLabel = formatCreditReset(creditsResetAt);

  const openCreditPrompt = () => setIsOpen(true);

  const handleClick = async (event) => {
    if (stopPropagation) event?.stopPropagation();
    if (needsTopUp) {
      openCreditPrompt();
      return;
    }
    const result = await onActivate?.();
    if (result?.neededCredits) setServerNeededCredits(result.neededCredits);
    if (result?.needsCredits) openCreditPrompt();
  };

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="bottom-end" closeOnBlur>
      <PopoverTrigger>
        <Button
          {...buttonProps}
          isLoading={isLoading}
          onClick={handleClick}
          aria-label={buttonProps['aria-label'] || 'Schedule this suggested job'}
          title={buttonProps.title || 'Schedule this suggested job'}
        >
          {children}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        w="300px"
        borderColor="gray.200"
        boxShadow="lg"
        borderRadius="md"
        _focus={{ boxShadow: 'lg' }}
        onClick={event => event.stopPropagation()}
      >
        <PopoverArrow />
        <PopoverBody p={4}>
          <VStack align="stretch" spacing={3}>
            <HStack align="start" spacing={2.5}>
              <Box
                w="28px"
                h="28px"
                borderRadius="full"
                bg="gray.900"
                color="white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <span className="material-icons-outlined" style={{ fontSize: '16px' }}>bolt</span>
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.900">
                  Run this job with {creditWord(requiredCredits)}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="1.45">
                  Top up to schedule it now, or keep the suggestion here and run it after your credits reset{resetLabel ? ` on ${resetLabel}` : ''}.
                </Text>
              </Box>
            </HStack>
            <HStack justify="space-between" bg="gray.50" borderRadius="md" px={3} py={2}>
              <Text fontSize="xs" color="gray.500">Current balance</Text>
              <Text fontSize="xs" fontWeight="700" color={needsTopUp ? 'orange.600' : 'gray.800'}>
                {knownBalance ? `${creditBalance} credit${creditBalance === 1 ? '' : 's'}` : 'Checking...'}
              </Text>
            </HStack>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => {
                setIsOpen(false);
                onTopUp?.();
              }}
            >
              Top up credits
            </Button>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}

function routineDisplayStatus(item = {}) {
  if (item.status === 'unscheduled') return 'unscheduled';
  if (item.status === 'paused') return 'paused';
  if (item.mission_status === 'paused') return 'paused';
  if (item.mission_id || (item.task_type === 'generate_pulse' && item.pulse_frequency)) return 'active';
  return effectiveTaskStatus(item);
}

function isQueuedBlockerHandoff(item = {}) {
  return item.capability === 'external_blocker_work' && item.status === 'waiting_for_evidence' && safeObject(item.result).state === 'ready_for_ai_tool';
}
function statusTabForItem(item = {}, isRoutine = false) {
  if (isQueuedBlockerHandoff(item)) return 'Planned';
  if(isClientActivity(item)) return 'Client activity';
  if (isRoutine || item.record_type === 'routine') return 'Routines';
  const status = item.record_type === 'pulse' ? 'completed' : effectiveTaskStatus(item);
  if (isProductRequirementItem(item) && ['todo', 'pending'].includes(status)) return 'In progress';
  const tab = STATUS_TABS.find(candidate => candidate.statuses && candidate.statuses.includes(status));
  return tab?.label || 'Planned';
}

function routineKey(item = {}) {
  if (item.mission_id) return `mission:${item.mission_id}`;
  if (item.record_type === 'routine' && item.task_type === 'generate_pulse' && item._plan_id) {
    return `pulse:${item._plan_id}`;
  }
  const parameters = safeObject(item.parameters);
  const explicitId = parameters.routine_id || parameters.schedule_id || parameters.recurrence_id;
  return explicitId ? `task:${explicitId}` : `task-run:${item.id}`;
}

function jobRecordKey(item = {}) {
  return `${item.record_type || 'task'}:${item.id}`;
}

function scheduledDisplayDate(item = {}, { routine = false, tabLabel = null } = {}) {
  if (tabLabel === 'Completed') {
    return item.completed_at || item.updated_at || item.created_at;
  }
  if (routine || item.record_type === 'routine' || taskCadence(item)) {
    return item.mission_next_run_at || item.next_run_at || item.scheduled_at || item.created_at;
  }
  return item.completed_at || item.scheduled_at || item.created_at;
}

function runStepStatusLabel(step = {}) {
  if (step.status !== 'skipped') {
    return STEP_STATUS_LABEL[step.status] || String(step.status || '').replace(/_/g, ' ');
  }
  const reason = step.output?.reason;
  if (reason === 'plan_revised_step_not_needed') return 'Not needed';
  if (reason === 'run_failed_before_step' || reason === 'run_ended_before_synthesis') return 'Not reached';
  if (reason === 'handoff_not_delivered') return 'Not delivered';
  return 'Skipped';
}

function safeObject(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

function formatConfidence(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const percentage = numeric > 0 && numeric <= 1 ? numeric * 100 : numeric;
  return `${Math.round(percentage)}% confidence`;
}

const DESIGN_REVIEW_DECISIONS = {
  promote: { short: 'Move forward', leadSentence: 'I’d move this forward.', firstPerson: 'move this design forward', help: 'Accept this version as the direction the team should continue with.' },
  promote_with_constraints: { short: 'Move forward with constraints', leadSentence: 'I’d move this forward with constraints.', firstPerson: 'move forward with explicit constraints', help: 'Continue, while recording conditions the team must respect.' },
  revise: { short: 'Revise first', leadSentence: 'I’d revise this before continuing.', firstPerson: 'revise this design before continuing', help: 'Keep this direction, but address the review findings before continuing.' },
  reject: { short: 'Stop this version', leadSentence: 'I’d stop this version here.', firstPerson: 'stop this version', help: 'Do not use this version as product direction. Rework or replace it before returning.' },
};

function ReviewSource({ source }) {
  const rawType = source.source_type || source.provenance?.provider || source.id || source.connector_id || 'manual';
  const sourceType = String(rawType).split('/')[0].replace(/[-_ ]search$/i, '') || 'manual';
  const logo = getConnectorLogo(sourceType);
  const resourceLabel = source.resource_label || source.label || source.reference || humanizeMentalModelValue(rawType);
  const sourceDetail = source.tool
    ? humanizeMentalModelValue(source.tool)
    : humanizeMentalModelValue(source.source_type || source.provenance?.provider || source.connector_id || source.id || 'manual');
  return <HStack spacing={2.5} minW={0}>
    <Flex w="24px" h="24px" flexShrink={0} borderRadius="full" bg={logo?.bg || 'gray.500'} align="center" justify="center">
      {logo ? <logo.Logo size={13}/> : <Text color="white" fontSize="10px" fontWeight="800">{String(sourceType).charAt(0).toUpperCase()}</Text>}
    </Flex>
    <Box minW={0}>
      <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={1}>{resourceLabel}</Text>
      <Text fontSize="xs" color="gray.500">{sourceDetail}</Text>
    </Box>
  </HStack>;
}

function artifactName(reference = '') {
  const value = String(reference || '').trim();
  if (!value) return 'Submitted design';
  try {
    const url = new URL(value);
    const parts = url.pathname.split('/').filter(Boolean);
    return decodeURIComponent(parts[parts.length - 1] || url.hostname);
  } catch {
    return value.length > 90 ? `${value.slice(0, 87)}...` : value;
  }
}

function designReviewCountSummary(reviewValue = {}) {
  const review = safeObject(reviewValue);
  const findings = Array.isArray(review.findings) ? [...review.findings] : [];
  const strengths = Array.isArray(review.strengths) ? [...review.strengths] : [];
  const brand = safeObject(review.brand_assessment);
  if (brand.status === 'aligned') strengths.push(brand);
  if (['needs_change', 'could_not_verify'].includes(brand.status) && !findings.some(item => item.dimension === 'brand')) {
    findings.push({
      type: brand.status === 'needs_change' ? 'needs_attention' : 'could_not_verify',
      severity: brand.status === 'needs_change' ? 'major' : 'minor',
      dimension: 'brand',
      finding: brand.summary,
    });
  }
  const typeOf = finding => finding.type === 'change_required' && finding.demonstrated_failure !== true
    ? 'needs_attention'
    : finding.type || ((finding.dimension === 'evidence' || /cannot|could not|unable|not verif/i.test(finding.finding || ''))
      ? 'could_not_verify'
      : finding.severity === 'minor' ? 'minor_issue' : 'change_required');
  const attention = findings.filter(finding => ['needs_attention', 'change_required'].includes(typeOf(finding))).length;
  const unverifiable = findings.filter(finding => typeOf(finding) === 'could_not_verify').length;
  const minor = findings.filter(finding => typeOf(finding) === 'minor_issue').length;
  return [
    strengths.length ? `${strengths.length} working well` : null,
    attention ? `${attention} need${attention === 1 ? 's' : ''} attention` : null,
    unverifiable ? `${unverifiable} couldn’t be verified` : null,
    minor ? `${minor} minor issue${minor === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
}

function DesignRemediationWork({ workItem, taskId, planId, onRefresh }) {
  const toast = useToast();
  const payload = safeObject(workItem.payload);
  const remediation = safeObject(payload.remediation);
  const actions = Array.isArray(remediation.actions) ? remediation.actions : [];
  const evidence = Array.isArray(remediation.evidence_to_return) ? remediation.evidence_to_return : [];
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftActions, setDraftActions] = useState(actions);
  const isConfirmed = Boolean(payload.handoff_confirmed_at);
  const saveHandoff = async decision => {
    setSaving(true);
    try {
      const response = await fetchWithAuth(`/api/agent/tasks/${taskId}/design-remediation-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, actions: draftActions }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Could not update the correction handoff');
      setEditing(false);
      await onRefresh?.();
      toast({ status: 'success', title: decision === 'confirm_handoff' ? 'Corrections ready for the builder' : 'Corrections saved' });
    } catch (error) {
      toast({ status: 'error', title: error.message });
    } finally {
      setSaving(false);
    }
  };
  return <VStack align="stretch" spacing={4}>
    <PabsResponse communication={remediation.communication} fallback={remediation.summary}>
        {isConfirmed&&<AiBuilderMcpBanner
          embedded
          planId={planId}
          subjectLabel={`Correction brief ${payload.remediation_reference || `#${taskId}`}`}
          prompt={`Connect to Pabs via MCP. Call get_design_remediation with remediation_reference "${payload.remediation_reference || ''}". Implement only the confirmed corrections, then call review_design_promotion with the returned remediation_reference and supersedes_review_reference, a new immutable artifact_version, and your verification evidence.`}
        />}
        <Box mt={3}>
          <SpeechBubbleWidget title="Corrections for the builder" summary={`${actions.length} correction${actions.length === 1 ? '' : 's'}`} icon="construction">
            {!isConfirmed&&<HStack justify="flex-end" mb={2}>{editing?<><Button size="xs" variant="ghost" isDisabled={saving} onClick={()=>{setDraftActions(actions);setEditing(false);}}>Cancel</Button><Button size="xs" variant="outline" isLoading={saving} loadingText="Saving…" onClick={()=>saveHandoff('save_review')}>Save</Button></>:<Button size="xs" variant="ghost" onClick={()=>{setExpanded(true);setEditing(true);}}>Edit</Button>}</HStack>}
            <Box position="relative" maxH={expanded?'none':'420px'} overflow="hidden">
              {actions.map((action,index)=><Box key={action.id||index} py={3} borderTop={index?'1px solid':'none'} borderColor="gray.100">
                <Badge mb={1.5} colorScheme={(action.work_type || (/^(prove|verify|check|document)/i.test(action.change_required || action.action || '') ? 'verify' : 'change')) === 'change' ? 'orange' : 'gray'}>{(action.work_type || (/^(prove|verify|check|document)/i.test(action.change_required || action.action || '') ? 'verify' : 'change')) === 'change' ? 'Change required' : 'Verification required'}</Badge>
                {editing ? <Textarea className="editable-field-textarea" size="sm" minH="112px" value={draftActions[index]?.change_required || ''} onChange={event=>setDraftActions(current=>current.map((entry,entryIndex)=>entryIndex===index?{...entry,change_required:event.target.value}:entry))}/> : <><Text fontSize="sm" fontWeight="700" color="gray.800">{action.title || `${(action.work_type === 'change' ? 'Change' : 'Verification')} ${index + 1}`}</Text><Text fontSize="sm" color="gray.700" lineHeight="1.55" mt={1}>{action.change_required || action.action}</Text></>}
                {action.user_or_product_consequence&&<Text fontSize="sm" color="gray.600" lineHeight="1.55" mt={1}>{action.user_or_product_consequence}</Text>}
                <Text fontSize="xs" color="gray.500" mt={2}>For {humanizeMentalModelValue(action.owner || 'builder')}</Text>
              </Box>)}
              {!expanded&&actions.length>1&&<Box position="absolute" left={0} right={0} bottom={0} h="110px" bgGradient="linear(to-b, transparent, white)" pointerEvents="none"/>}
            </Box>
            {actions.length>1&&<Button w="100%" variant="ghost" size="sm" borderTop="1px solid" borderColor="gray.100" onClick={()=>setExpanded(value=>!value)}>{expanded?'Show less':`View all ${actions.length} corrections`}</Button>}
            {!isConfirmed&&<Button mt={3} size="sm" minH="40px" px={4} colorScheme="brand" isLoading={saving} loadingText="Confirming corrections…" onClick={()=>saveHandoff('confirm_handoff')}>Confirm corrections</Button>}
          </SpeechBubbleWidget>
        </Box>
        {(evidence.length>0 || actions.some(action=>action.verification) || remediation.resume_condition)&&<Box mt={2}>
          <SpeechBubbleWidget title="Builder evidence and completion details" summary={evidence.length ? `${evidence.length} item${evidence.length === 1 ? '' : 's'} to return` : 'Verification and completion criteria'} icon="fact_check">
            {evidence.length>0&&<Box><Text fontSize="sm" fontWeight="700" color="gray.800">What the builder should return</Text><VStack align="stretch" spacing={1.5} mt={2}>{evidence.map((item,index)=><HStack key={index} align="flex-start"><Text color="gray.400">•</Text><Text fontSize="sm" color="gray.700" lineHeight="1.55">{item}</Text></HStack>)}</VStack></Box>}
            {actions.some(action=>action.verification)&&<Box mt={evidence.length?4:0}><Text fontSize="sm" fontWeight="700" color="gray.800">How Pabs will check it</Text><VStack align="stretch" spacing={2} mt={2}>{actions.filter(action=>action.verification).map((action,index)=><Text key={action.id||index} fontSize="sm" color="gray.700" lineHeight="1.55">{action.verification}</Text>)}</VStack></Box>}
            {remediation.resume_condition&&<Text fontSize="sm" color="gray.600" lineHeight="1.55" mt={4}>I’ll review it again when {String(remediation.resume_condition).replace(/^when\s+/i,'')}</Text>}
          </SpeechBubbleWidget>
        </Box>}
    </PabsResponse>
  </VStack>;
}

function DesignExceptionWork({ workItem, planId, onRefresh }) {
  const toast = useToast();
  const payload = safeObject(workItem.payload);
  const request = safeObject(payload.request);
  const assessment = safeObject(payload.assessment);
  const decision = safeObject(payload.decision);
  const history = Array.isArray(payload.lifecycle_history) ? payload.lifecycle_history : [];
  const [saving, setSaving] = useState(false);
  const [rationale, setRationale] = useState('');
  const [expiresAt, setExpiresAt] = useState(decision.expires_at || request.expires_at || '');
  const act = async action => {
    setSaving(true);
    try {
      const response = await fetchWithAuth(`/api/design-authority/${planId}/exceptions/${encodeURIComponent(payload.exception_reference)}/decision`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, rationale, expires_at: expiresAt || null }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not update this exception');
      toast({ status: 'success', title: action === 'grant' ? 'Exception granted' : action === 'renew' ? 'Exception renewed' : action === 'revoke' ? 'Exception revoked' : action === 'deny' ? 'Exception denied' : 'Changes requested' });
      await onRefresh?.();
    } catch (error) { toast({ status: 'error', title: error.message }); }
    finally { setSaving(false); }
  };
  const pending = ['pending_decision', 'reassessment_due'].includes(payload.status);
  const active = payload.status === 'active';
  return <VStack align="stretch" spacing={4}>
    <PabsResponse communication={assessment.communication} fallback={assessment.rationale}>
        {assessment.accepted_risk&&<Text fontSize="sm" color="orange.100" lineHeight="1.6" mt={2}>Risk being accepted: {assessment.accepted_risk}</Text>}
    </PabsResponse>
    <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <Box px={3.5} py={3} bg="gray.50"><Text fontSize="sm" fontWeight="700">Requested departure</Text></Box>
      <VStack align="stretch" spacing={2} px={3.5} py={3}>
        <Text fontSize="sm"><strong>Standard:</strong> {request.standard_reference}</Text>
        <Text fontSize="sm"><strong>Scope:</strong> {request.affected_scope}</Text>
        <Text fontSize="sm"><strong>Why:</strong> {request.justification}</Text>
        {request.compensating_constraint&&<Text fontSize="sm"><strong>Constraint:</strong> {request.compensating_constraint}</Text>}
        {decision.expires_at&&<Text fontSize="sm"><strong>Review by:</strong> {new Date(decision.expires_at).toLocaleString()}</Text>}
      </VStack>
    </Box>
    {(pending || active)&&<Box border="1px solid" borderColor="orange.200" bg="orange.50" borderRadius="lg" p={3.5}>
      <Text fontSize="sm" fontWeight="700">{active ? 'Manage this exception' : 'What should happen?'}</Text>
      {request.exception_type !== 'permanent'&&<Input mt={2} size="sm" bg="white" type="datetime-local" value={expiresAt ? String(expiresAt).slice(0,16) : ''} onChange={event=>setExpiresAt(event.target.value)} aria-label="Exception review date"/>}
      <Textarea mt={2} size="sm" bg="white" placeholder="Add the reason for this decision" value={rationale} onChange={event=>setRationale(event.target.value)}/>
      <HStack mt={3} flexWrap="wrap">
        {pending&&<><Button size="sm" isLoading={saving} onClick={()=>act(payload.status === 'reassessment_due' ? 'renew' : 'grant')}>{payload.status === 'reassessment_due' ? 'Renew exception' : 'Grant exception'}</Button><Button size="sm" variant="outline" isDisabled={saving} onClick={()=>act('request_changes')}>Request changes</Button><Button size="sm" variant="ghost" isDisabled={saving} onClick={()=>act('deny')}>Deny</Button></>}
        {active&&<><Button size="sm" variant="outline" isLoading={saving} onClick={()=>act('renew')}>Renew</Button><Button size="sm" variant="ghost" colorScheme="red" isDisabled={saving} onClick={()=>act('revoke')}>Revoke</Button></>}
      </HStack>
    </Box>}
    {history.length>0&&<Accordion allowToggle><AccordionItem border="none"><AccordionButton px={0}><Text flex="1" textAlign="left" fontSize="sm" fontWeight="600">Exception history</Text><AccordionIcon/></AccordionButton><AccordionPanel px={0}><VStack align="stretch" spacing={2}>{history.map((event,index)=><Text key={`${event.occurred_at}-${index}`} fontSize="sm" color="gray.600">{humanizeMentalModelValue(event.action)} · {event.actor === 'pabs_design_authority' ? 'Pabs' : 'Design Authority'} · {new Date(event.occurred_at).toLocaleString()}</Text>)}</VStack></AccordionPanel></AccordionItem></Accordion>}
  </VStack>;
}

function ReportDocuments({ documents, onOpen }) {
  return documents.map(document => <Box key={document.id} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
    <HStack px={3.5} py={3} bg="gray.50" align="flex-start"><Box as="span" className="material-icons-outlined" fontSize="18px" lineHeight="1.25" color="gray.500" flexShrink={0}>description</Box><Text fontSize="sm" fontWeight="600" lineHeight="1.45">{document.title}</Text></HStack>
    <Box p={3.5}><Button size="xs" colorScheme="brand" variant="outline" leftIcon={<span className="material-icons-outlined" style={{fontSize:'14px'}}>open_in_new</span>} onClick={()=>onOpen(document)}>Open document</Button></Box>
  </Box>);
}

function AuthorityKnowledge({ sources = [] }) {
  if (!sources.length) return null;
  return <Accordion allowToggle>
    <AccordionItem border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <AccordionButton px={3.5} py={3} bg="gray.50" _hover={{bg:'gray.100'}}>
        <Box as="span" className="material-icons-outlined" fontSize="17px" color="gray.500" mr={2}>hub</Box>
        <Box flex="1" textAlign="left"><Text fontSize="sm" fontWeight="700">Sources used</Text><Text fontSize="xs" color="gray.500">{sources.length} connected {sources.length === 1 ? 'source' : 'sources'}</Text></Box>
        <Text fontSize="xs" color="gray.500" mr={2}>View</Text><AccordionIcon/>
      </AccordionButton>
      <AccordionPanel px={3.5} py={3}><VStack align="stretch" spacing={2.5}>{sources.map((source,index)=><ReviewSource key={source.source_id || index} source={source}/>)}</VStack></AccordionPanel>
    </AccordionItem>
  </Accordion>;
}

function SpeechBubbleWidget({ title, summary, icon = 'fact_check', children }) {
  return <Accordion allowToggle>
    <AccordionItem border="1px solid" borderColor="whiteAlpha.300" borderRadius="lg" overflow="hidden">
      {({ isExpanded }) => <>
        <AccordionButton px={3} py={2.5} bg="whiteAlpha.100" _hover={{bg:'whiteAlpha.200'}}>
          <Box as="span" className="material-icons-outlined" fontSize="17px" color="gray.300" mr={2}>{icon}</Box>
          <Box flex="1" textAlign="left"><Text fontSize="sm" fontWeight="700" color="white">{title}</Text>{summary&&<Text fontSize="xs" color="gray.300" mt={0.5}>{summary}</Text>}</Box>
          <Text fontSize="xs" color="gray.300" mr={2}>{isExpanded ? 'Hide' : 'View'}</Text><AccordionIcon color="gray.300"/>
        </AccordionButton>
        <AccordionPanel px={3} py={3} bg="white" color="gray.800">{children}</AccordionPanel>
      </>}
    </AccordionItem>
  </Accordion>;
}

function SpeechBubbleSources({ sources = [] }) {
  if (!sources.length) return null;
  return <SpeechBubbleWidget title="Sources used" summary={`${sources.length} connected ${sources.length === 1 ? 'source' : 'sources'}`} icon="hub">
    <VStack align="stretch" spacing={2.5}>{sources.map((source,index) => <ReviewSource key={source.source_id || source.id || index} source={source}/>)}</VStack>
  </SpeechBubbleWidget>;
}

function JobStateWidget({ stateWidget, onResolve, resolving = false, disabled = false }) {
  if (!stateWidget) return null;
  const isProblem = ['failed', 'blocked'].includes(stateWidget.state);
  return <Box mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.300">
    <HStack align="center" spacing={2}>
      <Box w="7px" h="7px" borderRadius="full" bg={isProblem ? 'red.300' : stateWidget.state === 'completed' ? 'green.300' : 'orange.300'} flexShrink={0}/>
      <Text fontSize="sm" fontWeight="700" color="white">{stateWidget.title}</Text>
      {stateWidget.at && <Text fontSize="10px" color="gray.400" ml="auto">{formatDateTime(stateWidget.at)}</Text>}
    </HStack>
    {stateWidget.supporting_text && <Text fontSize="sm" color="gray.200" lineHeight="1.55" mt={2}>{stateWidget.supporting_text}</Text>}
    {stateWidget.primary_action?.type === 'resolve_blocker' && onResolve && <Button mt={3} size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} isLoading={resolving} loadingText="Running job…" isDisabled={disabled} onClick={onResolve}>{stateWidget.primary_action.label}</Button>}
  </Box>;
}

export function MissionResultWork({ workItem, onReply, onResume, onChoose, onResolveState, resolvingState, stateActionDisabled }) {
  const payload = safeObject(workItem.payload);
  const findings = Array.isArray(payload.findings) ? payload.findings : [];
  const sources = Array.isArray(payload.sources) ? payload.sources : [];
  // The story projection is the single authority for whether this historical
  // turn can still accept input. Do not infer actionability from a task result
  // snapshot here: the task result changes after every resumed run.
  const isHistorical = workItem.timeline_turn?.action_status !== 'current';
  const communication = workItem.communication || payload.communication;
  const text = communicationText(communication, workItem.summary);
  const choices = responseChoices(payload.needs_user_response_type, payload.needs_user_options);
  return <PabsResponse communication={communication} fallback={workItem.summary}>
      {(findings.length > 0 || sources.length > 0) && <VStack align="stretch" spacing={2} mt={3}>
        {findings.length > 0 && <SpeechBubbleWidget title="What I found" summary={`${findings.length} finding${findings.length === 1 ? '' : 's'}`}>
          <VStack align="stretch" spacing={2.5}>{findings.map((finding,index)=><HStack key={index} align="flex-start" spacing={2}><Box w="5px" h="5px" mt="7px" borderRadius="full" bg="gray.500" flexShrink={0}/><Text fontSize="sm" lineHeight="1.55">{typeof finding === 'string' ? finding : finding.finding || finding.summary || finding.title}</Text></HStack>)}</VStack>
        </SpeechBubbleWidget>}
        <SpeechBubbleSources sources={sources}/>
      </VStack>}
      {payload.needs_user && <Box mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.300">
        <HStack spacing={2} justify="space-between">
          <Text fontSize="sm" fontWeight="700" color="white">{isHistorical ? 'What I needed from you' : 'What I need from you'}</Text>
          {isHistorical && <Text fontSize="10px" color="gray.400">Historical</Text>}
        </HStack>
        {!communication?.messages?.length && !text.includes(payload.needs_user) && <Text fontSize="sm" color="gray.100" lineHeight="1.6" mt={1}>{payload.needs_user}</Text>}
        {!isHistorical && <HStack mt={3} spacing={2} flexWrap="wrap">
          {choices.length >= 2 && payload.interaction_id && onChoose ? choices.map(choice => <Button key={choice.id} size="sm" whiteSpace="normal" height="auto" minH="32px" py={2} bg="white" color="gray.800" _hover={{bg:'gray.100'}} isLoading={resolvingState} isDisabled={stateActionDisabled} onClick={() => onChoose({ interaction_id: payload.interaction_id, option_id: choice.id })}>{choice.label}</Button>) : <Button size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} onClick={onReply}>Reply in chat</Button>}
          {onResume && choices.length < 2 && <Button size="sm" variant="outline" borderColor="whiteAlpha.600" color="white" _hover={{bg:'whiteAlpha.200'}} isLoading={resolvingState} loadingText="Running job…" isDisabled={stateActionDisabled} onClick={() => onResume()}>Resume job</Button>}
        </HStack>}
      </Box>}
      <JobStateWidget stateWidget={workItem.timeline_turn?.state_widget} onResolve={onResolveState} resolving={resolvingState} disabled={stateActionDisabled}/>
    </PabsResponse>;
}

function AccessibilityAssessmentWork({ workItem, planId, reports }) {
  const payload = safeObject(workItem.payload);
  const assessment = safeObject(payload.assessment);
  const request = safeObject(assessment.evidence_request);
  const findings = Array.isArray(assessment.findings) ? assessment.findings : [];
  const strengths = Array.isArray(assessment.strengths) ? assessment.strengths : [];
  const waiting = payload.state === 'waiting_for_evidence' && Array.isArray(request.items) && request.items.length > 0;
  return <VStack align="stretch" spacing={4}>
    <PabsResponse communication={assessment.communication} fallback={assessment.summary}>
        {(strengths.length > 0 || findings.length > 0) && <Box mt={3}><SpeechBubbleWidget title="What I found" summary={`${strengths.length} working well${findings.length ? ` · ${findings.length} need attention` : ''}`}>
          <VStack align="stretch" spacing={3}>
            {strengths.map((item,index)=><Box key={`strength-${index}`}><Text fontSize="xs" fontWeight="800" color="green.700">WHAT WORKS</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.evidence}</Text></Box>)}
            {findings.map((item,index)=><Box key={item.id||index} pt={index || strengths.length ? 3 : 0} borderTop={index || strengths.length ? '1px solid' : 'none'} borderColor="gray.100"><Text fontSize="xs" fontWeight="800" color={item.certainty === 'demonstrated' ? 'orange.700' : 'gray.500'}>{item.certainty === 'demonstrated' ? 'NEEDS ATTENTION' : 'COULD NOT VERIFY'}</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.finding}</Text></Box>)}
          </VStack>
        </SpeechBubbleWidget></Box>}
        {waiting && request.items?.length > 0 && <Box mt={2}><SpeechBubbleWidget title="What I asked the builder to show" summary={`${request.items.length} evidence request${request.items.length === 1 ? '' : 's'}`} icon="checklist">
          <VStack align="stretch" spacing={3}>{request.items.map((item,index)=><Box key={index}><Text fontSize="sm" fontWeight="700">{item.scenario}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.demonstrate}</Text></Box>)}</VStack>
        </SpeechBubbleWidget></Box>}
        <Box mt={2}><SpeechBubbleSources sources={safeObject(payload.authority_snapshot).sources || []}/></Box>
        <Box mt={2}><SpeechBubbleWidget title="Evidence and assurance details" summary={[formatConfidence(assessment.confidence), payload.accessibility_target].filter(Boolean).join(' · ')} icon="verified_user">
          <Text fontSize="sm" color="gray.700">Target: {payload.accessibility_target}</Text>
          {assessment.limitations?.map((item,index)=><Text key={index} fontSize="sm" color="gray.600" mt={2}>• {item}</Text>)}
        </SpeechBubbleWidget></Box>
    </PabsResponse>
    {reports}
  </VStack>;
}

function DesignImplementationComparisonWork({ workItem, planId }) {
  const payload = safeObject(workItem.payload);
  const comparison = safeObject(payload.comparison);
  const request = safeObject(comparison.evidence_request);
  const preserved = Array.isArray(comparison.preserved_intent) ? comparison.preserved_intent : [];
  const differences = Array.isArray(comparison.differences) ? comparison.differences : [];
  const waiting = payload.state === 'waiting_for_evidence';
  const returnContract = jobResponse(payload).return;
  return <VStack align="stretch" spacing={4}>
    <PabsResponse communication={comparison.communication} fallback={comparison.summary}>
        {(preserved.length > 0 || differences.length > 0) && <Box mt={3}><SpeechBubbleWidget title="What I found" summary={`${preserved.length} preserved · ${differences.length} difference${differences.length === 1 ? '' : 's'}`}>
          <VStack align="stretch" spacing={3}>{preserved.map((item,index)=><Box key={`preserved-${index}`}><Text fontSize="xs" fontWeight="800" color="green.700">INTENT PRESERVED</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.evidence}</Text></Box>)}{differences.map((item,index)=><Box key={item.id||index} pt={index || preserved.length ? 3 : 0} borderTop={index || preserved.length ? '1px solid' : 'none'} borderColor="gray.100"><Text fontSize="xs" fontWeight="800" color={item.classification==='change_required'?'orange.700':item.classification==='could_not_verify'?'gray.500':'blue.700'}>{item.classification==='change_required'?'NEEDS ATTENTION':item.classification==='could_not_verify'?'COULD NOT VERIFY':item.classification==='intentional_adaptation'?'INTENTIONAL ADAPTATION':'ACCEPTABLE VARIATION'}</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.user_consequence}</Text></Box>)}</VStack>
        </SpeechBubbleWidget></Box>}
        {waiting&&request.items?.length > 0&&<Box mt={2}><SpeechBubbleWidget title="What I asked the builder to match" summary={`${request.items.length} evidence request${request.items.length === 1 ? '' : 's'}`} icon="checklist"><VStack align="stretch" spacing={3}>{request.items.map((item,index)=><Box key={index}><Text fontSize="sm" fontWeight="700">{item.state}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.demonstrate}</Text></Box>)}</VStack></SpeechBubbleWidget></Box>}
        <Box mt={2}><SpeechBubbleWidget title="Compared versions and evidence" summary={[formatConfidence(comparison.confidence), payload.design?.version && payload.implementation?.version ? `${payload.design.version} → ${payload.implementation.version}` : null].filter(Boolean).join(' · ')} icon="difference">
          <Text fontSize="sm" color="gray.700">Approved design: {payload.design?.version}</Text><Text fontSize="sm" color="gray.700" mt={1}>Implementation: {payload.implementation?.version}</Text>{comparison.limitations?.map((item,index)=><Text key={index} fontSize="sm" color="gray.600" mt={2}>• {item}</Text>)}
        </SpeechBubbleWidget></Box>
    </PabsResponse>
    {waiting&&returnContract?.tool&&<AiBuilderMcpBanner
      planId={planId}
      subjectLabel={`Design comparison ${payload.comparison_reference}`}
      prompt={`Call ${returnContract.tool} through Pabs MCP with ${JSON.stringify(returnContract.arguments)}. Return the requested evidence: ${JSON.stringify(payload.work_contract?.evidence_required || [])}. ${payload.next_action || ''}`}
    />}
    {false && (preserved.length > 0 || differences.length > 0)&&<Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <Box px={3.5} py={3} bg="gray.50"><Text fontSize="sm" fontWeight="700">What I found</Text></Box>
      {preserved.map((item,index)=><Box key={`preserved-${index}`} px={3.5} py={3} bg="green.50" borderTop="1px solid" borderColor="gray.100"><Text fontSize="xs" fontWeight="800" color="green.700">INTENT PRESERVED</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.evidence}</Text></Box>)}
      {differences.map((item,index)=>{
        const change = item.classification === 'change_required';
        const unverified = item.classification === 'could_not_verify';
        return <Box key={item.id||index} px={3.5} py={3} borderTop="1px solid" borderColor="gray.100"><Text fontSize="xs" fontWeight="800" color={change?'orange.700':unverified?'gray.500':'blue.700'}>{change?'NEEDS ATTENTION':unverified?'COULD NOT VERIFY':item.classification==='intentional_adaptation'?'INTENTIONAL ADAPTATION':'ACCEPTABLE VARIATION'}</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.user_consequence}</Text></Box>;
      })}
    </Box>}
  </VStack>;
}

function DesignSystemUsageWork({ workItem, planId }) {
  const payload = safeObject(workItem.payload);
  const review = safeObject(payload.system_usage_review);
  const request = safeObject(review.evidence_request);
  const compliant = Array.isArray(review.compliant_usage) ? review.compliant_usage : [];
  const issues = Array.isArray(review.issues) ? review.issues : [];
  const proposals = Array.isArray(review.system_gap_proposals) ? review.system_gap_proposals : [];
  const exceptions = Array.isArray(review.exception_candidates) ? review.exception_candidates : [];
  const waiting = payload.state === 'waiting_for_evidence' && Array.isArray(request.items) && request.items.length > 0;
  return <VStack align="stretch" spacing={4}>
    <PabsResponse communication={review.communication} fallback={review.summary}>
        {(compliant.length>0||issues.length>0)&&<Box mt={3}><SpeechBubbleWidget title="What I found" summary={`${compliant.length} working well${issues.length ? ` · ${issues.length} need attention` : ''}`}><VStack align="stretch" spacing={3}>{compliant.map((item,index)=><Box key={`compliant-${index}`}><Text fontSize="xs" fontWeight="800" color="green.700">WORKING AS INTENDED</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.evidence}</Text></Box>)}{issues.map((item,index)=><Box key={item.id||index} pt={index || compliant.length ? 3 : 0} borderTop={index || compliant.length ? '1px solid' : 'none'} borderColor="gray.100"><Text fontSize="xs" fontWeight="800" color={item.certainty==='demonstrated'?'orange.700':'gray.500'}>{item.certainty==='demonstrated'?'NEEDS ATTENTION':'COULD NOT VERIFY'}</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.finding}</Text></Box>)}</VStack></SpeechBubbleWidget></Box>}{(proposals.length>0||exceptions.length>0)&&<Box mt={2}><SpeechBubbleWidget title="What this may need next" summary={`${exceptions.length} exception candidate${exceptions.length===1?'':'s'}${proposals.length?` · ${proposals.length} system proposal${proposals.length===1?'':'s'}`:''}`} icon="route"><VStack align="stretch" spacing={3}>{exceptions.map((item,index)=><Box key={`exception-${index}`}><Badge colorScheme="orange">Exception candidate</Badge><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700">{item.reason}</Text></Box>)}{proposals.map((item,index)=><Box key={`proposal-${index}`}><Badge colorScheme="blue">System gap proposal</Badge><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700">{item.evidence}</Text></Box>)}</VStack></SpeechBubbleWidget></Box>}{waiting&&<Box mt={2}><SpeechBubbleWidget title="What I asked the builder to show" summary={`${request.items.length} evidence request${request.items.length===1?'':'s'}`} icon="checklist"><VStack align="stretch" spacing={3}>{request.items.map((item,index)=><Box key={index}><Text fontSize="sm" fontWeight="700">{item.area}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.demonstrate}</Text></Box>)}</VStack></SpeechBubbleWidget></Box>}<Box mt={2}><SpeechBubbleWidget title="Usage evidence and rules" summary={formatConfidence(review.confidence)} icon="policy"><Text fontSize="sm" color="gray.700">Artifact version: {payload.artifact?.version}</Text>{review.limitations?.map((item,index)=><Text key={index} fontSize="sm" color="gray.600" mt={2}>• {item}</Text>)}</SpeechBubbleWidget></Box></PabsResponse>
    {waiting&&(
      <AiBuilderMcpBanner
        planId={planId}
        subjectLabel={`Design-system review ${payload.usage_review_reference}`}
        prompt={`Continue this design-system review with Pabs through MCP. Gather only the requested usage evidence for usage_review_reference "${payload.usage_review_reference}", then call review_design_system_usage again with that reference, the same artifact_version, and the new evidence_bundle.`}
      />
    )}
    {false && (compliant.length>0||issues.length>0)&&<Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden"><Box px={3.5} py={3} bg="gray.50"><Text fontSize="sm" fontWeight="700">What I found</Text></Box>{compliant.map((item,index)=><Box key={`compliant-${index}`} px={3.5} py={3} bg="green.50" borderTop="1px solid" borderColor="gray.100"><Text fontSize="xs" fontWeight="800" color="green.700">WORKING AS INTENDED</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.evidence}</Text></Box>)}{issues.map((item,index)=><Box key={item.id||index} px={3.5} py={3} borderTop="1px solid" borderColor="gray.100"><Text fontSize="xs" fontWeight="800" color={item.certainty==='demonstrated'?'orange.700':'gray.500'}>{item.certainty==='demonstrated'?'NEEDS ATTENTION':'COULD NOT VERIFY'}</Text><Text fontSize="sm" fontWeight="700" mt={1}>{item.title}</Text><Text fontSize="sm" color="gray.700" mt={1}>{item.finding}</Text></Box>)}</Box>}
  </VStack>;
}

/**
 * Shared narrative surface for Design Authority work. New design playbooks should
 * project into the same sequence: what was examined, what Pabs concluded, why,
 * knowledge used, human decision, optional calibration, and the next handoff.
 */
function DesignAuthorityReviewWork({ workItem, planId, taskId, onRefresh }) {
  const toast = useToast();
  const payload = safeObject(workItem.payload);
  const review = safeObject(payload.review);
  const awaitingBuilderEvidence = payload.state === 'waiting_for_evidence' || review.evidence_status === 'supplement_required';
  const builderEvidenceRequest = safeObject(review.evidence_request);
  const needsHumanEvidenceIntervention = builderEvidenceRequest.human_intervention_required === true;
  const snapshot = safeObject(payload.authority_snapshot);
  const findings = Array.isArray(review.findings) ? review.findings : [];
  const reviewFocus = Array.isArray(review.review_focus) && review.review_focus.length
    ? review.review_focus
    : [
      'the journey makes the next decision clear',
      'each job state gives people the right action and explanation',
      'the interaction is accessible and works beyond the happy path',
      'the experience follows the product’s design principles',
      'the decision is recorded and leads to the correct next step',
    ];
  const sources = Array.isArray(snapshot.sources) ? snapshot.sources : [];
  const strengths = Array.isArray(review.strengths) ? review.strengths : [];
  const brandAssessment = safeObject(review.brand_assessment);
  const displayStrengths = brandAssessment.status === 'aligned'
    ? [...strengths, { title: 'Brand implementation is aligned', summary: brandAssessment.summary, evidence: brandAssessment.evidence }]
    : strengths;
  const brandFinding = ['needs_change', 'could_not_verify'].includes(brandAssessment.status)
    ? {
      type: brandAssessment.status === 'needs_change' ? 'needs_attention' : 'could_not_verify',
      severity: brandAssessment.status === 'needs_change' ? 'major' : 'minor',
      dimension: 'brand',
      title: brandAssessment.status === 'needs_change' ? 'Brand implementation needs attention' : 'Complete brand implementation',
      finding: brandAssessment.summary,
      evidence: brandAssessment.evidence,
    }
    : null;
  const displayFindings = brandFinding && !findings.some(finding => finding.dimension === 'brand')
    ? [...findings, brandFinding]
    : findings;
  const [calibration, setCalibration] = useState(null);
  const [loadingDecision, setLoadingDecision] = useState(true);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [decision, setDecision] = useState('');
  const [reason, setReason] = useState('');
  const [falsePositiveIds, setFalsePositiveIds] = useState([]);
  const [missedFinding, setMissedFinding] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [strengthsExpanded, setStrengthsExpanded] = useState(false);
  const [continuation, setContinuation] = useState(() => safeObject(payload.continuation));
  const [saving, setSaving] = useState(false);

  const loadCalibration = useCallback(async () => {
    if (!planId || !taskId) return;
    setLoadingDecision(true);
    try {
      const response = await fetchWithAuth(`/api/design-authority/${planId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not load the design decision');
      const item = (data.design_activity || []).find(entry => Number(entry.task_id) === Number(taskId));
      const existing = item?.calibration || null;
      setCalibration(existing);
      setDecision(existing?.human_disposition || '');
      setReason(existing?.override_reason || '');
      setFalsePositiveIds((existing?.false_positive_findings || []).map(entry => String(entry.id)));
      setMissedFinding(existing?.missed_findings?.[0]?.finding || '');
      if (item?.feedback) {
        setFeedbackRating(item.feedback.rating);
        setFalsePositiveIds((item.feedback.false_positive_findings || []).map(entry => String(entry.id)));
        setMissedFinding(item.feedback.missed_findings?.[0]?.finding || '');
      }
      setContinuation(safeObject(item?.continuation));
    } catch (error) {
      console.warn('Could not load Design Review decision:', error.message);
    } finally {
      setLoadingDecision(false);
    }
  }, [planId, taskId, payload.review_reference]);

  useEffect(() => { loadCalibration(); }, [loadCalibration]);

  if (awaitingBuilderEvidence) {
    const requestedItems = Array.isArray(builderEvidenceRequest.items) ? builderEvidenceRequest.items : [];
    const rawBuilderName = payload.builder_client || '';
    const builderName = !rawBuilderName || /^(?:mcp client|mcp builder)$/i.test(rawBuilderName)
      ? 'the AI tool'
      : rawBuilderName;
    return <VStack align="stretch" spacing={5}>
      <HStack spacing={2} align="baseline" flexWrap="wrap">
        <Text fontSize="sm" fontWeight="700" color="gray.900">{payload.work_name || artifactName(payload.artifact_reference)}</Text>
        <Text fontSize="xs" color="gray.500">Review in progress</Text>
      </HStack>
      <PabsResponse communication={review.communication} fallback={[
        needsHumanEvidenceIntervention ? (builderEvidenceRequest.human_intervention_reason || 'The builder needs access or authority that only you can provide.') : `I need more evidence to finish this review. I asked ${builderName} to return it.`,
        needsHumanEvidenceIntervention ? null : `When ${builderName} responds, I’ll continue this review. You don’t need to provide the evidence.`,
      ].filter(Boolean).join('\n\n')}/>
      {requestedItems.length > 0 && <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
        <Box px={3.5} py={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.200">
          <Text fontSize="sm" fontWeight="700" color="gray.800">What I asked {builderName} to check</Text>
        </Box>
        <VStack align="stretch" spacing={0}>{requestedItems.map((item,index)=><Box key={index} px={3.5} py={3} borderTop={index?'1px solid':'none'} borderColor="gray.100">
          <Text fontSize="sm" fontWeight="700" color="gray.800">{item.scenario}</Text>
          <Text fontSize="sm" color="gray.700" lineHeight="1.55" mt={0.5}>{item.demonstrate}</Text>
        </Box>)}</VStack>
      </Box>}
    </VStack>;
  }

  const saveDecision = async ({ includeFeedback = false, disposition = decision } = {}) => {
    if (!disposition) {
      toast({ status: 'warning', title: 'Choose what should happen to this design' });
      return;
    }
    if (disposition !== review.verdict && !reason.trim()) {
      toast({ status: 'warning', title: 'Briefly explain why you see it differently' });
      return;
    }
    setSaving(true);
    try {
      const response = await fetchWithAuth(`/api/design-authority/${planId}/reviews/${encodeURIComponent(payload.review_reference)}/disposition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          human_disposition: disposition,
          override_reason: reason,
          false_positive_findings: includeFeedback ? falsePositiveIds.map(id => {
            const finding = displayFindings.find(entry => String(entry.id || entry.finding) === id);
            return { id, finding: finding?.finding || 'Marked as not valid', severity: finding?.severity || null };
          }) : [],
          missed_findings: includeFeedback && missedFinding.trim() ? [{ finding: missedFinding.trim(), severity: 'major' }] : [],
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not save the design decision');
      setContinuation(result.continuation || null);
      setDecisionOpen(false);
      setFeedbackOpen(false);
      await loadCalibration();
      await onRefresh?.();
      toast({ status: 'success', title: includeFeedback ? 'Feedback saved' : 'Design decision recorded' });
    } catch (error) {
      toast({ status: 'error', title: error.message });
    } finally {
      setSaving(false);
    }
  };

  const saveFeedback = async rating => {
    setSaving(true);
    try {
      const response = await fetchWithAuth(`/api/design-authority/${planId}/reviews/${encodeURIComponent(payload.review_reference)}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          false_positive_findings: falsePositiveIds.map(id => {
            const finding = displayFindings.find(entry => String(entry.id || entry.finding) === id);
            return { id, finding: finding?.finding || 'Marked as not valid', severity: finding?.severity || null };
          }),
          missed_findings: missedFinding.trim() ? [{ finding: missedFinding.trim(), severity: 'major' }] : [],
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not save review feedback');
      setFeedbackRating(rating);
      setFeedbackOpen(false);
      toast({ status: 'success', title: 'Review feedback saved' });
    } catch (error) {
      toast({ status: 'error', title: error.message });
    } finally {
      setSaving(false);
    }
  };

  const recommendation = DESIGN_REVIEW_DECISIONS[review.verdict] || DESIGN_REVIEW_DECISIONS.revise;
  const humanDecision = DESIGN_REVIEW_DECISIONS[calibration?.human_disposition];
  const findingId = (finding, index) => String(finding.id || finding.finding || index);
  const toggleFalsePositive = id => setFalsePositiveIds(current => (
    current.includes(id) ? current.filter(value => value !== id) : [...current, id]
  ));
  const list = (heading, values) => Array.isArray(values) && values.length ? <Box>
    <Text fontSize="sm" fontWeight="700" color="gray.800">{heading}</Text>
    <VStack align="stretch" spacing={2} mt={2}>{values.map((value, index) => <Text key={index} fontSize="sm" color="gray.700" lineHeight="1.6">{typeof value === 'string' ? value : value.action || value.finding}</Text>)}</VStack>
  </Box> : null;
  const findingType = finding => finding.type === 'change_required' && finding.demonstrated_failure !== true
    ? 'needs_attention'
    : finding.type || (
    finding.dimension === 'evidence' || /cannot|could not|unable|not verif/i.test(finding.finding || '')
      ? 'could_not_verify'
      : finding.severity === 'minor' ? 'minor_issue' : 'change_required'
  );
  const findingLabel = finding => ({
    needs_attention: 'Needs attention',
    change_required: 'Change required',
    minor_issue: 'Minor issue',
    could_not_verify: 'Could not verify',
  })[findingType(finding)] || 'What I found';
  const positiveSummary = review.positive_summary || (displayStrengths.length
    ? displayStrengths.map(strength => strength.title || strength.summary).filter(Boolean).slice(0, 3).join(', ')
    : null);
  const lifecycleNarrative = safeObject(review.lifecycle_narrative);
  const countSummary = designReviewCountSummary(review);
  const reviewEvidenceDetails = <VStack align="stretch" spacing={5}>
    {reviewFocus.length > 0 && list('What I checked', reviewFocus)}
    {review.remediation_verification?.length > 0 && <Box>
      <Text fontSize="sm" fontWeight="700" color="gray.800">Corrections checked</Text>
      <VStack align="stretch" spacing={0} mt={2} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
        {review.remediation_verification.map((item, index) => {
          const resolved = item.status === 'resolved';
          const label = ({resolved:'Resolved',partially_resolved:'Partly resolved',unresolved:'Still unresolved',unverifiable:'Could not verify'})[item.status] || 'Could not verify';
          return <Box key={item.action_id || index} px={3} py={2.5} borderTop={index?'1px solid':'none'} borderColor="gray.100"><HStack align="flex-start" spacing={2}><Box as="span" className="material-icons-outlined" fontSize="16px" color={resolved?'green.500':item.status==='partially_resolved'?'orange.500':'red.500'} mt="2px">{resolved?'check_circle':'error_outline'}</Box><Box minW={0}><Text fontSize="xs" fontWeight="700" color="gray.700">{label}</Text><Text fontSize="sm" color="gray.700" lineHeight="1.55" mt={0.5}>{item.assessment || item.action_id}</Text>{item.evidence&&<Text fontSize="xs" color="gray.500" mt={1}>Evidence: {item.evidence}</Text>}</Box></HStack></Box>;
        })}
      </VStack>
    </Box>}
    {list('Evidence still needed', review.evidence_required)}
    {findings.some(finding=>finding.evidence) && <Box><Text fontSize="sm" fontWeight="700" color="gray.800">Evidence behind the findings</Text><VStack align="stretch" spacing={3} mt={2}>{findings.filter(finding=>finding.evidence).map((finding,index)=><Box key={index}><Text fontSize="sm" color="gray.700" lineHeight="1.55">{finding.finding}</Text><Text fontSize="xs" color="gray.500" lineHeight="1.55" mt={1}>{finding.evidence}</Text></Box>)}</VStack></Box>}
    {review.implementation_reality?.assessment && <Box><Text fontSize="sm" fontWeight="700" color="gray.800">Implementation assessment</Text><Text fontSize="sm" color="gray.700" mt={2} lineHeight="1.6">{review.implementation_reality.assessment}</Text></Box>}
    {list('What needs to change', review.remediation)}
  </VStack>;

  return <VStack align="stretch" spacing={5}>
    <Box>
      <PabsResponse communication={review.communication} fallback={[lifecycleNarrative.situation, lifecycleNarrative.why_now || review.decision_summary || recommendation.leadSentence, positiveSummary, `My call: ${recommendation.short}`, review.next_step].filter(Boolean).join('\n\n')}>
          <VStack align="stretch" spacing={2} mt={3}>
            {displayFindings.length > 0 && <SpeechBubbleWidget title="What I found" summary={countSummary}>
              <VStack align="stretch" spacing={3}>
                {displayStrengths.map((strength,index)=><Box key={strength.title||index}><Text fontSize="xs" fontWeight="700" color="green.700" textTransform="uppercase">What works</Text><Text fontSize="sm" fontWeight="700" mt={1}>{strength.title || strength.summary}</Text>{strength.summary&&strength.title&&<Text fontSize="sm" color="gray.700" mt={1}>{strength.summary}</Text>}</Box>)}
                {displayFindings.map((finding,index)=>{ const id=findingId(finding,index); const selected=falsePositiveIds.includes(id); return <Box key={id} pt={index || displayStrengths.length ? 3 : 0} borderTop={index || displayStrengths.length ? '1px solid' : 'none'} borderColor="gray.100"><HStack align="flex-start"><Box flex={1}><Text fontSize="xs" fontWeight="700" color={['needs_attention','change_required'].includes(findingType(finding))?'orange.700':findingType(finding)==='could_not_verify'?'gray.600':'gray.500'} textTransform="uppercase">{findingLabel(finding)}</Text><Text fontSize="sm" fontWeight={finding.title?'700':'400'} mt={1}>{finding.title || finding.finding}</Text>{finding.title&&<Text fontSize="sm" color="gray.700" mt={1}>{finding.finding}</Text>}</Box>{feedbackOpen&&<Checkbox isChecked={selected} onChange={()=>toggleFalsePositive(id)} aria-label={`Mark finding as incorrect: ${finding.finding}`}/>}</HStack></Box>;})}
                <HStack pt={2} borderTop="1px solid" borderColor="gray.100" justify="space-between"><Text fontSize="xs" color="gray.500">Was this review useful?</Text><HStack spacing={0}><Tooltip label="These findings were useful" hasArrow><IconButton size="xs" variant={feedbackRating==='useful'?'solid':'ghost'} aria-label="These findings were useful" icon={<Box as="span" className="material-icons-outlined" fontSize="16px">thumb_up</Box>} onClick={()=>saveFeedback('useful')}/></Tooltip><Tooltip label="Something is wrong or missing" hasArrow><IconButton size="xs" variant={feedbackRating==='needs_improvement'?'solid':'ghost'} aria-label="Something is wrong or missing" icon={<Box as="span" className="material-icons-outlined" fontSize="16px">thumb_down</Box>} onClick={()=>setFeedbackOpen(true)}/></Tooltip></HStack></HStack>
                {feedbackOpen&&<VStack align="stretch" spacing={2}><Textarea size="sm" value={missedFinding} onChange={event=>setMissedFinding(event.target.value)} placeholder="Add an issue I should have found (optional)"/><HStack justify="flex-end"><Button size="xs" variant="ghost" onClick={()=>setFeedbackOpen(false)}>Cancel</Button><Button size="xs" isLoading={saving} onClick={()=>saveFeedback('needs_improvement')}>Save feedback</Button></HStack></VStack>}
              </VStack>
            </SpeechBubbleWidget>}
            <SpeechBubbleSources sources={sources}/>
            <SpeechBubbleWidget title="Review evidence and implementation details" summary={formatConfidence(review.confidence)} icon="fact_check">
              {reviewEvidenceDetails}
            </SpeechBubbleWidget>
          </VStack>

          {loadingDecision ? <Spinner size="sm" mt={3}/> : !calibration && <Box mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.300">
            {!review.communication?.messages?.length && <Text fontWeight="600" color="white">What do you want the team to do?</Text>}
            {!review.communication?.messages?.length && lifecycleNarrative.decision_needed && <Text color="gray.300" mt={1.5}>{lifecycleNarrative.decision_needed}</Text>}
            {!review.communication?.messages?.length && (lifecycleNarrative.if_agreed || lifecycleNarrative.if_different_direction) && <Text color="gray.300" mt={1.5}>{lifecycleNarrative.if_agreed && `If you agree: ${lifecycleNarrative.if_agreed}`} {lifecycleNarrative.if_different_direction && `If you choose another direction: ${lifecycleNarrative.if_different_direction}`}</Text>}
            <HStack spacing={2} mt={2} flexWrap="wrap">
              <Button size="sm" minH="40px" px={4} bg="white" color="gray.800" _hover={{bg:'gray.100'}} isLoading={saving} loadingText="Recording decision…" onClick={()=>saveDecision({disposition:review.verdict})}>Agree with you</Button>
              <Button size="sm" minH="40px" px={3} variant="ghost" color="white" _hover={{bg:'whiteAlpha.200'}} isDisabled={saving} onClick={()=>setDecisionOpen(true)}>Choose another direction</Button>
            </HStack>
          </Box>}
      </PabsResponse>

      {calibration && <VStack align="stretch" spacing={3} mt={3}>
        <Box alignSelf="flex-end" maxW="calc(100% - 42px)">
          <Text fontSize="10px" color="gray.400" textAlign="right" mb={1.5}>
            Decided by you{calibration.created_at ? ` · ${formatDateTime(calibration.created_at)}` : ''}
          </Text>
          <Box bg="blue.50" borderRadius="16px 0px 16px 16px" px={3.5} py={3} fontSize="sm" lineHeight="1.6">
            <Text fontWeight="600" color="gray.800">I decided to {humanDecision?.firstPerson || humanizeMentalModelValue(calibration.human_disposition)}</Text>
            {calibration.override_reason && <Text color="gray.600" mt={1.5}>{calibration.override_reason}</Text>}
            <Button size="sm" variant="link" fontWeight="600" color="gray.600" mt={2} onClick={()=>setDecisionOpen(open=>!open)}>Change decision</Button>
            <Collapse in={decisionOpen} animateOpacity><VStack align="stretch" spacing={3} mt={3} pt={3} borderTop="1px solid" borderColor="blue.100">
              <Text fontSize="sm" fontWeight="700">Choose the direction</Text>
              <VStack align="stretch" spacing={1}>{Object.entries(DESIGN_REVIEW_DECISIONS).map(([id, option]) => <Flex key={id} as="button" type="button" onClick={()=>setDecision(id)} textAlign="left" align="flex-start" gap={3} px={2} py={2} borderRadius="md" bg={decision===id?'white':'transparent'} _hover={{bg:'whiteAlpha.700'}}>
                <Flex mt="2px" w="16px" h="16px" borderRadius="full" border="2px solid" borderColor={decision===id?'blue.500':'gray.300'} align="center" justify="center" flexShrink={0}>{decision===id&&<Box w="7px" h="7px" borderRadius="full" bg="blue.500"/>}</Flex>
                <Box><Text fontSize="sm" fontWeight="700" color="gray.800">{option.short}</Text><Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="1.45">{option.help}</Text></Box>
              </Flex>)}</VStack>
              {decision && decision !== review.verdict && <Box><Text fontSize="sm" fontWeight="600">Why do you see it differently?</Text><Textarea className="editable-field-textarea" mt={2} minH="96px" borderRadius="lg" resize="vertical" value={reason} onChange={event=>setReason(event.target.value)} placeholder="A short reason keeps the decision useful later." bg="white"/></Box>}
              <HStack justify="flex-end"><Button size="sm" variant="ghost" onClick={()=>setDecisionOpen(false)}>Cancel</Button><Button size="sm" colorScheme="blue" isLoading={saving} onClick={()=>saveDecision()}>Record decision</Button></HStack>
            </VStack></Collapse>
          </Box>
        </Box>
        <HStack align="flex-start" spacing={2.5}>
          <Avatar name="Pabs" size="xs" bg="gray.800" color="white" flexShrink={0} mt={1}/>
          <Box bg="gray.800" borderRadius="0px 14px 14px 14px" px={3.5} py={3} flex={1} fontSize="sm" lineHeight="1.6">
            <Text color="gray.100" whiteSpace="pre-wrap">{jobResponse({ continuation }).message || 'Decision recorded.'}</Text>
            {continuation?.task_id && <Button mt={3} size="sm" minH="38px" bg="white" color="gray.800" borderColor="white" _hover={{bg:'gray.100'}} rightIcon={<ChevronRightIcon/>} onClick={()=>window.dispatchEvent(new CustomEvent('pabs:openJobs',{detail:{taskId:continuation.task_id}}))}>{jobResponse({ continuation }).action?.label}</Button>}
            {Array.isArray(continuation?.spawned_work) && continuation.spawned_work.some(work=>work.kind==='product_requirement_candidate') && <Box mt={3} pt={3} borderTop="1px solid" borderColor="whiteAlpha.300">
              <Text fontSize="xs" color="gray.200" lineHeight="1.5">I also preserved {continuation.spawned_work.filter(work=>work.kind==='product_requirement_candidate').length} possible product requirement for a separate decision. It is not approved build work.</Text>
              <VStack align="stretch" spacing={1} mt={2}>{continuation.spawned_work.filter(work=>work.kind==='product_requirement_candidate').map(work=><Button key={work.task_id} size="sm" minH="38px" justifyContent="space-between" variant="outline" color="white" borderColor="whiteAlpha.500" _hover={{bg:'whiteAlpha.200'}} rightIcon={<ChevronRightIcon/>} onClick={()=>window.dispatchEvent(new CustomEvent('pabs:openJobs',{detail:{taskId:work.task_id}}))}>{work.title || 'Review product proposal'}</Button>)}</VStack>
            </Box>}
          </Box>
        </HStack>
      </VStack>}

      {!calibration && <Collapse in={decisionOpen} animateOpacity><VStack align="stretch" spacing={3} mt={4} pt={4} borderTop="1px solid" borderColor="gray.200">
        <Text fontSize="sm" fontWeight="700">Choose the direction</Text>
        <VStack align="stretch" spacing={1}>{Object.entries(DESIGN_REVIEW_DECISIONS).map(([id, option]) => <Flex key={id} as="button" type="button" onClick={()=>setDecision(id)} textAlign="left" align="flex-start" gap={3} px={2} py={2} borderRadius="md" bg={decision===id?'gray.100':'transparent'} _hover={{bg:'gray.50'}}>
          <Flex mt="2px" w="16px" h="16px" borderRadius="full" border="2px solid" borderColor={decision===id?'blue.500':'gray.300'} align="center" justify="center" flexShrink={0}>{decision===id&&<Box w="7px" h="7px" borderRadius="full" bg="blue.500"/>}</Flex>
          <Box><Text fontSize="sm" fontWeight="700" color="gray.800">{option.short}</Text><Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="1.45">{option.help}</Text></Box>
        </Flex>)}</VStack>
        {decision && decision !== review.verdict && <Box><Text fontSize="sm" fontWeight="600">Why do you see it differently?</Text><Textarea className="editable-field-textarea" mt={2} minH="96px" borderRadius="lg" resize="vertical" value={reason} onChange={event=>setReason(event.target.value)} placeholder="A short reason keeps the decision useful later." bg="white"/></Box>}
        <HStack justify="flex-end"><Button size="sm" variant="ghost" onClick={()=>setDecisionOpen(false)}>Cancel</Button><Button size="sm" colorScheme="blue" isLoading={saving} onClick={()=>saveDecision()}>Record decision</Button></HStack>
      </VStack></Collapse>}
    </Box>

    {false && (displayFindings.length > 0 || sources.length > 0) && <VStack align="stretch" spacing={3} ml="42px" mt="-20px" bg="gray.800" px={3} pb={3} borderRadius="0 0 16px 16px">
    {displayFindings.length > 0 && <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <HStack px={3.5} py={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2}>
        <Box as="span" className="material-icons-outlined" fontSize="17px" color="gray.500">fact_check</Box>
        <Box flex={1}>
          <Text fontSize="sm" fontWeight="700" color="gray.800">What I found</Text>
          {countSummary && <Text fontSize="xs" color="gray.500" mt={0.5}>{countSummary}</Text>}
          {feedbackOpen && <Text fontSize="xs" color="gray.500" mt={0.5}>Select anything I got wrong, or add something I missed.</Text>}
        </Box>
        <HStack spacing={0}>
          <Tooltip label="These findings were useful" hasArrow><IconButton size="xs" variant={feedbackRating==='useful'?'solid':'ghost'} aria-label="These findings were useful" icon={<Box as="span" className="material-icons-outlined" fontSize="16px">thumb_up</Box>} onClick={()=>saveFeedback('useful')}/></Tooltip>
          <Tooltip label="Something is wrong or missing" hasArrow><IconButton size="xs" variant={feedbackRating==='needs_improvement'?'solid':'ghost'} aria-label="Something is wrong or missing" icon={<Box as="span" className="material-icons-outlined" fontSize="16px">thumb_down</Box>} onClick={()=>setFeedbackOpen(true)}/></Tooltip>
        </HStack>
      </HStack>
      {displayStrengths.length > 0 && <Box bg="green.50">
        <Box px={3.5} pt={3} pb={strengthsExpanded ? 3 : 1} position="relative" maxH={strengthsExpanded ? 'none' : '230px'} overflow="hidden">
          <Text fontSize="xs" fontWeight="700" color="green.700" textTransform="uppercase">What works</Text>
          <VStack align="stretch" spacing={2} mt={2}>{displayStrengths.map((strength,index)=><Box key={strength.title||index}><Text fontSize="sm" fontWeight="700" color="gray.800">{strength.title}</Text>{strength.summary&&<Text fontSize="sm" color="gray.700" lineHeight="1.55" mt={0.5}>{strength.summary}</Text>}</Box>)}</VStack>
          {!strengthsExpanded && displayStrengths.length > 2 && <Box position="absolute" left={0} right={0} bottom={0} h="72px" bgGradient="linear(to-b, transparent, green.50)" pointerEvents="none"/>}
        </Box>
        {displayStrengths.length > 2 && <Button w="100%" borderRadius={0} variant="ghost" size="sm" color="green.800" onClick={()=>setStrengthsExpanded(value=>!value)}>{strengthsExpanded ? 'Show less' : `View all ${displayStrengths.length} things that work`}</Button>}
      </Box>}
      <VStack align="stretch" spacing={0}>{displayFindings.map((finding, index) => {
        const id = findingId(finding, index);
        const selected = falsePositiveIds.includes(id);
        return <Box key={id} as={feedbackOpen ? 'button' : 'div'} type={feedbackOpen ? 'button' : undefined} onClick={feedbackOpen ? ()=>toggleFalsePositive(id) : undefined} textAlign="left" width="100%" px={3.5} py={3} borderTop={index?'1px solid':'none'} borderColor="gray.100" bg={selected && feedbackOpen ? 'red.50' : 'white'} _hover={feedbackOpen ? {bg:selected?'red.50':'gray.50'} : undefined}>
        <HStack align="flex-start" spacing={2.5}>
          {feedbackOpen && <Checkbox mt={0.5} isChecked={selected} pointerEvents="none" aria-label={`Mark finding as incorrect: ${finding.finding}`}/>}
          <Box minW={0}><Text fontSize="xs" fontWeight="700" color={['needs_attention','change_required'].includes(findingType(finding))?'orange.700':findingType(finding)==='could_not_verify'?'gray.600':'gray.500'} textTransform="uppercase">{findingLabel(finding)}</Text><Text fontSize="sm" fontWeight={finding.title?'700':'400'} color="gray.800" lineHeight="1.5" mt={0.5}>{finding.title || finding.finding}</Text>{finding.title&&<Text fontSize="sm" color="gray.700" lineHeight="1.55" mt={0.5}>{finding.finding}</Text>}</Box>
        </HStack>
      </Box>})}</VStack>
      <Collapse in={feedbackOpen} animateOpacity>
        <VStack align="stretch" spacing={3} px={3.5} py={3.5} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="gray.800">Did I miss anything important?</Text>
            <Textarea className="editable-field-textarea" mt={2} size="sm" minH="96px" borderRadius="lg" resize="vertical" value={missedFinding} onChange={event=>setMissedFinding(event.target.value)} placeholder="Add an issue I should have found (optional)" bg="white"/>
          </Box>
          <HStack justify="flex-end">
            <Button size="sm" variant="ghost" onClick={()=>setFeedbackOpen(false)}>Cancel</Button>
            <Button size="sm" colorScheme="blue" isLoading={saving} onClick={()=>saveFeedback('needs_improvement')}>Save feedback</Button>
          </HStack>
        </VStack>
      </Collapse>
    </Box>}
    <AuthorityKnowledge sources={sources}/>
    </VStack>}
  </VStack>;
}

function humanizeDataKey(value = '') {
  return String(value)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}

function StructuredChangeValue({ value, depth = 0 }) {
  if (Array.isArray(value)) {
    const visibleEntries = value.slice(0, 5);
    return (
      <VStack align="stretch" spacing={2.5}>
        {visibleEntries.map((entry, index) => (
          <Box key={entry?.id || index} p={2.5} bg="gray.50" borderRadius="md">
            <StructuredChangeValue value={entry} depth={depth + 1} />
          </Box>
        ))}
        {value.length > visibleEntries.length && (
          <Text fontSize="xs" color="gray.500">
            + {value.length - visibleEntries.length} more
          </Text>
        )}
      </VStack>
    );
  }

  if (value && typeof value === 'object') {
    if (depth >= 3) {
      return (
        <Text fontSize="xs" color="gray.600">
          {Array.isArray(value) ? `${value.length} items` : `${Object.keys(value).length} fields`}
        </Text>
      );
    }
    const visibleEntries = Object.entries(value)
      .filter(([, entryValue]) => entryValue != null && entryValue !== '')
      .slice(0, 8);
    return (
      <VStack align="stretch" spacing={1.5}>
        {visibleEntries.map(([key, entryValue]) => (
            <Box key={key}>
              <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700">
                {humanizeDataKey(key)}
              </Text>
              {typeof entryValue === 'object'
                ? <Box mt={1}><StructuredChangeValue value={entryValue} depth={depth + 1} /></Box>
                : <Text fontSize="xs" color="gray.700" mt={0.5} whiteSpace="pre-wrap">{String(entryValue)}</Text>}
            </Box>
          ))}
      </VStack>
    );
  }

  return (
    <Text fontSize="sm" color="gray.700" lineHeight="1.6" whiteSpace="pre-wrap">
      {value == null || value === '' ? 'No value recorded.' : String(value)}
    </Text>
  );
}

function isProductRequirementItem(item = {}) {
  return item.capability === 'product_requirement_issue' || item.task_type === 'product_requirement_issue';
}

function productRequirementStatusLabel(item = {}, status) {
  return linkedWorkStatus({ ...item, status });
}

function productRequirementRouteLabel(routeTarget) {
  const route = String(routeTarget || '').trim();
  if (route === 'human') return 'Review / route';
  if (route === 'human_review') return 'Human review';
  if (route === 'human_implementation') return 'Human implementation';
  if (route === 'vibecode' || route === 'agent') return 'AI builder';
  return humanizeMentalModelValue(route || 'handoff');
}

// Whether this requirement is in the "ready for an AI build tool" state — same
// condition the inner routing badge uses, since the MCP/vibe-coding messaging
// below only applies to vibecode/agent routing, not github_issue/jira/linear.
function isReadyForAiBuilder(item = {}, taskParameters = {}) {
  return (
    isProductRequirementItem(item) &&
    item.status === 'waiting_for_evidence' &&
    (taskParameters.routing_target === 'vibecode' || taskParameters.routing_target === 'agent')
  );
}

function AiBuilderMcpBanner({ planId, requirementId, prompt: promptOverride = null, subjectLabel = null, embedded = false }) {
  const [dismissed, setDismissed] = useState(false);
  const [mcpConnected, setMcpConnected] = useState(true); // default true: don't show a CTA until we know for sure

  useEffect(() => {
    let cancelled = false;
    if (!planId) return undefined;
    fetchWithAuth(`/api/plans/${planId}/related-data`)
      .then((res) => (res?.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        setMcpConnected((data.relatedData?.mcpTokens || 0) > 0);
      })
      .catch(() => {
        // Non-critical setup guidance; leave the CTA hidden on failure.
      });
    return () => { cancelled = true; };
  }, [planId]);

  if (dismissed) return null;

  // Mirrors the lifecycle Pabs already documents for this capability
  // (src/tools/action/createProductRequirementIssue.js: discover/implement/report),
  // so the prompt calls the same MCP tools a builder would actually use.
  const prompt = promptOverride || (requirementId
    ? `Connect to Pabs via MCP. Call get_product_requirements to find Requirement #${requirementId}, then implement it. When done, call complete_product_requirement with your delivery_summary and evidence.`
    : `Connect to Pabs via MCP and call get_product_requirements to find the open Build Requirement for this plan. Implement it, then call complete_product_requirement with your delivery_summary and evidence.`);


  return (
    <Flex
      align="flex-start"
      gap={2}
      px={embedded ? 0 : 3}
      py={embedded ? 0 : 2}
      mb={embedded ? 0 : 2}
      mt={embedded ? 3 : 0}
      bg={embedded ? "transparent" : "blue.50"}
      border={embedded ? "0" : "1px solid"}
      borderColor={embedded ? "transparent" : "blue.200"}
      borderRadius={embedded ? 0 : "xl"}
    >
      {!embedded && <Box as="span" className="material-icons-outlined" fontSize="16px" color="blue.500" mt="1px">
        smart_toy
      </Box>}
      <VStack align="stretch" spacing={2} flex={1} minW={0}>
        <Text fontSize={embedded ? "sm" : "xs"} color={embedded ? "gray.100" : "blue.900"} lineHeight="1.5">
          {subjectLabel || (requirementId ? `Requirement #${requirementId}` : "This requirement")} is ready for an AI build tool. {mcpConnected
            ? "MCP is connected. Copy this prompt into your vibe-coding tool or dev team:"
            : "Connect Pabs via MCP, then copy this prompt into your vibe-coding tool or dev team:"}
        </Text>
        <div className="pabs-install pabs-install-compact" data-appearance={embedded ? "dark" : undefined}>
          <CopyableCommand value={prompt} label="Copy prompt" />
        </div>
        {!mcpConnected && (
          <HStack spacing={2}>
            <Button
              size="xs"
              colorScheme="blue"
              variant="solid"
              onClick={() => window.dispatchEvent(new CustomEvent("pabs:openMcpModal"))}
            >
              Connect MCP
            </Button>
          </HStack>
        )}
      </VStack>
      {!embedded && <IconButton
        size="xs"
        flexShrink={0}
        variant="ghost"
        colorScheme="blue"
        aria-label="Dismiss"
        icon={<Box as="span" className="material-icons-outlined" fontSize="14px">close</Box>}
        onClick={() => setDismissed(true)}
      />}
    </Flex>
  );
}

function productRequirementDraftFromTask(task = {}) {
  const parameters = safeObject(task.parameters);
  return {
    requirement: parameters.requirement || task.reasoning || '',
    strategic_rationale: parameters.strategic_rationale || '',
    expected_outcome: parameters.expected_outcome || '',
    acceptance_criteria: Array.isArray(parameters.acceptance_criteria)
      ? parameters.acceptance_criteria.join('\n')
      : '',
    evidence_required: parameters.evidence_required || '',
  };
}

function humanizeMentalModelValue(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
}

function operatingActionLines(value) {
  const lines = String(value || '')
    .split(/(?<=[.!?])\s+|;\s+/)
    .map(line => line.trim().replace(/[.;]\s*$/, ''))
    .filter(Boolean);

  const actions = lines.filter(line => (
    !/^run\s+(daily|weekly|monthly|once|one time|every\b.*)$/i.test(line)
  ));

  return actions.length > 0 ? actions : lines;
}

function suggestedJobIcon(item = {}) {
  if (isDecisionCaptureTask(item)) return 'rule';
  const purpose = suggestedJobPurpose(item);
  if (purpose === 'Direction') return 'track_changes';
  if (purpose === 'Discovery') return 'travel_explore';
  if (purpose === 'Decisions') return 'rule';
  if (purpose === 'Delivery') return 'difference';
  if (purpose === 'Learning') return 'psychology';
  if (purpose === 'Alignment') return 'campaign';
  if (item.capability === 'agent_mission') return 'travel_explore';
  if (item.capability === 'github_change_request' || item.task_type === 'github_change_request') return 'code';
  if (item.capability === 'product_requirement_issue' || item.task_type === 'product_requirement_issue') return 'assignment';
  if (item.capability === 'generate_pulse' || item.task_type === 'generate_pulse') return 'monitor_heart';
  if (item.capability === 'generate_document') return 'description';
  if (item.capability === 'engagement_email') return 'mark_email_unread';
  if (item.capability === 'send_user_email') return 'outgoing_mail';
  if (item.capability === 'connector_write') return 'hub';
  if (String(item.capability || '').startsWith('update_')) return 'edit_note';
  return 'auto_awesome';
}

function jobDisplayLabel(item = {}) {
  if(isClientActivity(item)) return clientActivityLabel(item);
  const candidate = safeObject(item.result).decision_candidate;
  const decisionTitle = candidate?.recommendation?.title;
  if (decisionTitle) return String(decisionTitle).replace(/^Mission:\s*/i, '');
  if (item.capability === 'review_design_promotion') {
    const parameters = safeObject(item.parameters);
    const result = safeObject(item.result);
    return parameters.work_name || result.work_name || 'Design Review';
  }
  return (TASK_TYPE_LABEL[item.task_type] || item.task_type || 'Job').replace(/^Mission:\s*/i, '');
}

function jobCardDescription(item = {}) {
  const parameters = safeObject(item.parameters);
  const result = safeObject(item.result);
  if (item.capability === 'external_blocker_work') return result.closed_reason || result.summary || (result.state === 'ready_for_ai_tool' ? 'Ready for your AI tool to pick up.' : 'Follow the work returned by your AI tool.');
  if (item.capability === 'review_design_promotion') {
    const name = parameters.work_name || result.work_name || 'the submitted experience';
    return `Review ${name} before the team continues.`;
  }
  if (item.capability === 'audit_user_journey') {
    const name = parameters.journey_name || 'the submitted journey';
    return `Reviewing ${name} against the intended experience.`;
  }
  if (item.capability === 'generate_design_remediation_plan') {
    return result.remediation?.summary || 'A focused brief for resolving the design review findings.';
  }
  if (item.capability === 'generate_design_exception_review') {
    return result.assessment?.rationale || `Review the requested exception to ${parameters.standard_reference || 'the applicable design standard'}.`;
  }
  if (item.capability === 'generate_accessibility_evidence_assessment') {
    return result.assessment?.summary || `Review accessibility evidence for ${parameters.work_name || 'the submitted experience'}.`;
  }
  if (item.capability === 'generate_design_implementation_comparison') {
    return result.comparison?.summary || `Compare the approved design and implementation for ${parameters.work_name || 'this experience'}.`;
  }
  if (item.capability === 'generate_design_system_usage_review') {
    return result.system_usage_review?.summary || `Review design-system usage for ${parameters.work_name || 'this implementation'}.`;
  }
  const candidate = result.decision_candidate;
  if (candidate) return decisionSummaryLine(candidate, safeObject(item.result), parameters);
  if (item.capability === 'product_requirement_issue') {
    return parameters.requirement || parameters.problem || item.reasoning || TASK_TYPE_DESCRIPTION[item.task_type];
  }
  return item.mission_objective
    || parameters.objective
    || parameters.mission_objective
    || parameters.mission?.objective
    || parameters.goal
    || TASK_TYPE_DESCRIPTION[item.task_type]
    || item.reasoning
    || 'Run this job for the strategy.';
}

function cleanDecisionPrompt(value = '') {
  return String(value || '')
    .replace(/^\s*check\s+with\s+pabs\s*:\s*/i, '')
    .trim();
}

// The user's own words, verbatim — what they told their builder that Pabs caught.
function decisionUserStatement(result = {}, parameters = {}) {
  return cleanDecisionPrompt(result.original_question || parameters.original_question) || null;
}

function decisionSourceLabel(result = {}, parameters = {}) {
  const source = safeObject(result.source);
  const type = source.type || parameters.source;
  const client = source.client;
  if (client && client !== 'MCP client') return client;
  if (type === 'mcp') return 'AI builder';
  if (type === 'chat') return 'Pabs chat';
  if (type === 'api') return 'API source';
  return 'Source';
}

function splitLeadSentence(value = '') {
  const text = String(value || '').trim();
  if (!text) return { lead: '', rest: '' };
  const match = text.match(/^(.+?[.!?])(\s+[\s\S]*)?$/);
  if (!match) return { lead: text, rest: '' };
  return {
    lead: match[1],
    rest: String(match[2] || '').trim(),
  };
}

// A clean one-line summary of the decision, for cards and the list. Never the raw prompt if we can avoid it.
function decisionSummaryLine(candidate = {}, result = {}, parameters = {}) {
  return candidate.context?.user_intent
    || candidate.recommendation?.title
    || decisionUserStatement(result, parameters)
    || 'A decision Pabs caught before it turned into work.';
}

// Pabs' point of view: the call and the one-line reason. This is the hero of the read.
function decisionCallReason(candidate = {}) {
  const rec = candidate.recommendation || {};
  return rec.call_reason || rec.description || null;
}

// The tension: what the user gives up by making this call.
function decisionTradeoff(candidate = {}) {
  return candidate.recommendation?.tradeoff || null;
}

// One human sentence tying the call to the named strategy elements it moves.
function decisionImpactLine(candidate = {}) {
  const impacts = Array.isArray(candidate.recommendation?.strategy_impact)
    ? candidate.recommendation.strategy_impact.filter(i => i && i.element)
    : [];
  if (!impacts.length) return null;
  const parts = impacts.slice(0, 3).map((i) => {
    const verb = i.direction === 'advances' ? 'moves' : i.direction === 'threatens' ? 'puts pressure on' : 'touches';
    return `${verb} ${i.element}`;
  });
  const joined = parts.length === 1
    ? parts[0]
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return `It ${joined}.`;
}

// Confidence as a human phrase, not a bare number.
function decisionConfidenceWord(candidate = {}) {
  const c = candidate.reasoning?.confidence;
  if (typeof c !== 'number') return null;
  if (c >= 0.75) return "I'm confident on this";
  if (c >= 0.5) return "I'm fairly confident";
  if (c >= 0.3) return "I'm not fully sure";
  return "I'm genuinely unsure here";
}

function decisionWatchLine(candidate = {}) {
  const risk = Array.isArray(candidate.reasoning?.risks) ? candidate.reasoning.risks[0] : null;
  return candidate.drift_signal || risk || null;
}

function suggestedJobWorksWith(item = {}) {
  const scope = safeObject(item.mission_scope);
  const authority = safeObject(scope.authority || item.mission_authority);
  const sources = Array.isArray(scope.sources_to_check) ? scope.sources_to_check : [];
  const values = [...sources];

  if (scope.uses_live_search) values.push('web_search');
  if (authority.email_plan_owner === 'automatic' || authority.email_plan_owner === 'ask') values.push('email');

  const labelMap = {
    active_routines: 'Routines',
    competitor_analysis: 'Competitors',
    github: 'GitHub',
    granola: 'Granola',
    learnings: 'Learnings',
    mcp_activity: 'MCP',
    notion: 'Notion',
    web_search: 'Web search',
    email: 'Email',
  };

  const labels = [...new Set(values.map(value => labelMap[value] || humanizeMentalModelValue(value)).filter(Boolean))];
  return labels.slice(0, 4);
}

function suggestedJobPurpose(item = {}) {
  if (isDecisionCaptureTask(item)) return 'Decisions';
  const params = safeObject(item.parameters);
  const scope = safeObject(item.mission_scope);
  const explicitPurpose = params.pm_purpose || scope.pm_purpose;
  const validPurposes = ['Direction', 'Discovery', 'Decisions', 'Delivery', 'Learning', 'Alignment'];
  if (validPurposes.includes(explicitPurpose)) return explicitPurpose;

  const text = [
    params.pm_purpose,
    scope.pm_purpose,
    item.reasoning,
    item.task_type,
    item.capability,
  ].filter(Boolean).join(' ').toLowerCase();

  if (text.includes('decision') || text.includes('prioriti') || text.includes('trade-off')) return 'Decisions';
  if (text.includes('handoff') || text.includes('alignment') || text.includes('stakeholder')) return 'Alignment';
  if (item.capability === 'product_requirement_issue' || text.includes('requirement')) return 'Delivery';
  if (text.includes('build') || text.includes('delivery') || text.includes('implementation') || text.includes('github')) return 'Delivery';
  if (text.includes('learning') || text.includes('evidence') || text.includes('measure') || text.includes('feedback')) return 'Learning';
  if (text.includes('research') || text.includes('discover') || text.includes('market') || text.includes('customer')) return 'Discovery';
  return 'Direction';
}

function jobCueTag(item = {}) {
  return suggestedJobPurpose(item);
}

function suggestedJobPurposeReason(purpose) {
  return {
    Direction: 'Keeps the strategy pointed at the right outcome',
    Discovery: 'Reduces uncertainty before commitment',
    Decisions: 'Helps resolve the next product call',
    Delivery: 'Checks execution against the strategy',
    Learning: 'Turns evidence into strategy movement',
    Alignment: 'Creates shared context for the next move',
  }[purpose] || 'Explains why Pabs suggests this job';
}

function ConfirmActionDialog({
  isOpen,
  leastDestructiveRef,
  title,
  body,
  confirmLabel,
  confirmColorScheme = 'red',
  isLoading = false,
  onClose,
  onConfirm,
}) {
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={leastDestructiveRef}
      onClose={onClose}
      isCentered
    >
      <AlertDialogOverlay bg="blackAlpha.300">
        <AlertDialogContent borderRadius="lg" maxW="380px">
          <AlertDialogHeader fontSize="md" fontWeight="700" pb={2}>
            {title}
          </AlertDialogHeader>
          <AlertDialogBody fontSize="sm" color="gray.600" lineHeight="1.6">
            {body}
          </AlertDialogBody>
          <AlertDialogFooter pt={4}>
            <Button ref={leastDestructiveRef} size="sm" variant="ghost" onClick={onClose} isDisabled={isLoading}>
              Keep it
            </Button>
            <Button
              size="sm"
              colorScheme={confirmColorScheme}
              ml={3}
              isLoading={isLoading}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

function destructiveActionCopy(action, item = {}) {
  const isRoutine = !!taskCadence(item) || item.record_type === 'routine';
  if (action === 'delete') {
    return {
      title: 'Delete job?',
      body: 'This removes it from the Jobs list. Completed history may no longer be available from this view.',
      confirmLabel: 'Delete',
    };
  }
  return {
    title: isRoutine ? 'Cancel routine?' : 'Cancel job?',
    body: isRoutine
      ? 'This stops future runs and removes queued work. Past completed runs stay in the history.'
      : 'This cancels the queued job. Past completed work stays in the history.',
    confirmLabel: isRoutine ? 'Cancel routine' : 'Cancel job',
  };
}

// ---------------------------------------------------------------------------
// Task detail side panel
// ---------------------------------------------------------------------------
export function buildJobTimelineItems(presentation, openingRequest, jobId) {
  const work = presentation?.work || [];
  const turns = presentation?.story?.turns;
  if (!Array.isArray(turns) || !turns.length) {
    return [
      ...(openingRequest?.message ? [{ id: `request:${jobId}`, kind: 'timeline_message', timeline_turn: { ...openingRequest, actor_label: openingRequest.requested_by } }] : []),
      ...work,
    ];
  }
  const workById = new Map(work.map(entry => [entry.id, entry]));
  const referencedWorkIds = new Set();
  // Date outcomes independently from the response they describe. Legacy jobs
  // can record failure before their run finishes; nesting that status below the
  // later response would display its timestamp out of order.
  const events = turns.flatMap(turn => [
    { ...turn, state_widget: undefined },
    ...(turn.state_widget ? [{
      ...turn.state_widget,
      id: turn.state_widget.id || `outcome:${turn.id}`,
      at: turn.state_widget.at || turn.at,
      kind: 'state_widget', work_ids: [],
    }] : []),
  ]).sort((a, b) => (new Date(a.at || 0).getTime() || 0) - (new Date(b.at || 0).getTime() || 0));
  return events.flatMap(turn => {
    const ownedWork = (turn.work_ids || []).map((id, index) => {
      referencedWorkIds.add(id);
      const entry = workById.get(id);
      return entry ? { ...entry, timeline_turn: turn, timeline_work_index: index } : null;
    }).filter(Boolean);
    return ownedWork.length ? ownedWork : [{
      id: turn.id,
      kind: turn.kind === 'state_widget' ? 'timeline_state_widget' : turn.kind === 'state' ? 'timeline_state' : 'timeline_message',
      timeline_turn: turn,
    }];
  }).concat(work.filter(entry => !referencedWorkIds.has(entry.id)));
}

export function TaskDetailPanel({
  item,
  onClose,
  onOpenLinked,
  toolMeta,
  workflowMeta,
  onRefresh,
  onDelete,
  isRoutineView = false,
  activationCredit,
  onCloseDrawer,
  relatedRecords = [],
}) {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(item?.record_type === 'task');
  const detailSelectionRef = useRef(null);
  // Resuming/pausing a mission re-renders the panel with a different primary
  // action (e.g. the clicked "Continue" button unmounts once the mission
  // moves on) — the drawer's focus trap then relocates focus to whatever
  // tabbable element is left, and the browser scrolls that into view. Left
  // alone, that lands on the Notifications toggle near the bottom of a long
  // job. Restoring the scroll position after the update keeps the user where
  // they were instead of at the mercy of the focus trap's target.
  const { scrollRef: detailScrollRef, conversationRef, latestRef } = useJobConversationScroll(
    `${item?.record_type}:${item?.id}`,
    !detailLoading && (item?.record_type !== 'task' || !detail || Number(detail?.task?.id) === Number(item?.id)),
  );
  const [fullDetailsOpen, setFullDetailsOpen] = useState(false);
  const [actionUpdating, setActionUpdating] = useState(false);
  // actionUpdating alone can't tell two buttons apart, so clicking Continue
  // also lit up the unrelated Pause button's spinner. pendingAction tracks
  // which specific action is actually running, so only that one shows a
  // spinner — the rest just disable.
  const [pendingAction, setPendingAction] = useState(null);
  const [cadenceUpdating, setCadenceUpdating] = useState(false);
  // A routine that's already scheduled (active/paused) has a real cadence to
  // summarize, so it opens collapsed behind "Change schedule" — only a never-
  // configured ('unscheduled') routine opens straight into the editor, since
  // there's nothing yet to summarize and setup is the only thing to do.
  // This panel is never remounted when the selected item changes (no `key`
  // on <TaskDetailPanel>), so a plain useState initializer would only ever
  // evaluate for whichever item was open first — synced via effect instead.
  const [showScheduleEditor, setShowScheduleEditor] = useState(item?.status === 'unscheduled');
  useEffect(() => {
    setShowScheduleEditor(item?.status === 'unscheduled');
  }, [item?.id, item?.record_type, item?.status]);
  const [deleting, setDeleting] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [emailEnabled, setEmailEnabled] = useState(!!item?.pulse_email_enabled);
  const [githubReviewMode, setGithubReviewMode] = useState(null);
  const [githubReviewFeedback, setGithubReviewFeedback] = useState('');
  const [githubPendingDecision, setGithubPendingDecision] = useState(null);
  const [decisionCapturePendingDecision, setDecisionCapturePendingDecision] = useState(null);
  const [decisionRejectOpen, setDecisionRejectOpen] = useState(false);
  const [decisionRejectText, setDecisionRejectText] = useState('');
  const [productRequirementDraft, setProductRequirementDraft] = useState({});
  const [productRequirementPendingDecision, setProductRequirementPendingDecision] = useState(null);
  const [productRequirementEditMode, setProductRequirementEditMode] = useState(false);
  const leastDestructiveRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    const authority = safeObject(item?.mission_authority);
    if (item?.capability === 'agent_mission' && authority.email_plan_owner) {
      setEmailEnabled(authority.email_plan_owner === 'automatic');
      return;
    }
    setEmailEnabled(!!item?.pulse_email_enabled);
  }, [item?.pulse_email_enabled, item?.mission_authority, item?.capability]);

  useEffect(() => {
    if (!item) return;
    let active = true;
    const selectionKey = `${item.record_type}:${item.id}`;
    const changedSelection = detailSelectionRef.current !== selectionKey;
    if (changedSelection) {
      detailSelectionRef.current = selectionKey;
      // List polling supplies a new object for the same job. Keep its mounted
      // conversation and scroll position while fetching fresher detail; only a
      // different selection should replace the history with a loading state.
      setDetail(null);
      setDetailLoading(item.record_type === 'task');
      setFullDetailsOpen(false);
      setConfirmAction(null);
      setGithubReviewMode(null);
      setGithubReviewFeedback('');
      setGithubPendingDecision(null);
      setDecisionCapturePendingDecision(null);
      setDecisionRejectOpen(false);
      setDecisionRejectText('');
      setProductRequirementDraft({});
      setProductRequirementPendingDecision(null);
      setProductRequirementEditMode(false);
    }
    // Non-task records own their presentation; changing selection cleared any
    // previous task lifecycle above.
    if (item.record_type !== 'task') return () => { active = false; };
    if (changedSelection && item.capability === 'product_requirement_issue') {
      setProductRequirementDraft(productRequirementDraftFromTask(item));
    }
    fetchWithAuth(`/api/agent/tasks/${item.id}/detail`)
      .then(r => r.json())
      .then(d => {
        if (!active) return;
        setDetail(d);
        if (d?.task?.capability === 'product_requirement_issue') {
          setProductRequirementDraft(productRequirementDraftFromTask(d.task));
        }
        setDetailLoading(false);
      })
      .catch(() => {
        if (active) setDetailLoading(false);
      });
    return () => { active = false; };
  }, [item]);

  // The selected job may start through plan chat while the list still holds a
  // Planned snapshot. Refresh its authoritative detail independently of the
  // list's running-only poll, and preserve scroll/editor state on refresh.
  useEffect(() => {
    if (item?.record_type !== 'task') return;
    let active = true;
    let fetching = false;
    let lastStatus = item.status;
    const refresh = async () => {
      if (!active || fetching) return;
      fetching = true;
      try {
        const response = await fetchWithAuth(`/api/agent/tasks/${item.id}/detail`);
        if (!response.ok) return;
        const nextDetail = await response.json();
        if (active) {
          setDetail(nextDetail);
          if (nextDetail.task?.status && nextDetail.task.status !== lastStatus) {
            lastStatus = nextDetail.task.status;
            onRefresh?.();
          }
        }
      } catch {} finally { fetching = false; }
    };
    const timer = window.setInterval(refresh, 5000);
    window.addEventListener('pabs:agentTasksUpdated', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('pabs:agentTasksUpdated', refresh);
    };
  }, [item?.id, item?.record_type, onRefresh]);

  const performTaskAction = async (action, instruction = null) => {
    if (!item || actionUpdating) return null;
    const missionId = detail?.mission?.id;
    if (action === 'activate') {
      setActionUpdating(true);
      try {
        const res = await fetchWithAuth(`/api/agent/tasks/${item.id}/activate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (res.ok) {
          toast({ title: 'Job activated', status: 'success', duration: 2000, isClosable: true });
          if (onRefresh) await onRefresh();
          return { activated: true };
        } else {
          const payload = await res.json().catch(() => ({}));
          if (isCreditLimitError(payload.error)) {
            return { needsCredits: true, neededCredits: parseNeededCredits(payload.error), error: payload.error };
          }
          toast({ title: payload.error || 'Activation failed', status: 'error', duration: 3500, isClosable: true });
        }
      } catch {
        toast({ title: 'Activation failed', status: 'error', duration: 3500, isClosable: true });
      } finally {
        setActionUpdating(false);
      }
      return null;
    }

    const isMissionAction = ['pause', 'resume', 'acknowledge', 'cancel-mission'].includes(action);
    if (isMissionAction && !missionId) return;

    // The clicked button is about to become disabled (isLoading), which makes
    // it unfocusable — the drawer's focus trap then relocates focus to
    // whatever's left, and the browser scrolls that into view. Move focus to
    // the (stable, already-in-view) scroll container ourselves first, with
    // preventScroll so this move itself doesn't cause a jump, so the trap
    // never has to relocate anything. The scrollTop restore below is a
    // backup for the second re-render once the request actually resolves.
    detailScrollRef.current?.focus?.({ preventScroll: true });
    const preservedScrollTop = detailScrollRef.current?.scrollTop;
    const restoreScrollPosition = () => {
      if (preservedScrollTop == null) return;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (detailScrollRef.current) detailScrollRef.current.scrollTop = preservedScrollTop;
      }));
    };

    const endpoint = action === 'pause'
      ? `/api/agent/missions/${missionId}/pause`
      : action === 'resume'
        ? `/api/agent/missions/${missionId}/resume`
        : action === 'acknowledge'
          ? `/api/agent/missions/${missionId}/acknowledge`
          : action === 'cancel-mission'
            ? `/api/agent/missions/${missionId}/cancel`
            : `/api/agent/tasks/${item.id}/cancel`;

    setActionUpdating(true);
    setPendingAction(action);
    try {
      const res = await fetchWithAuth(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'resume'
          ? {
              next_run_at: new Date().toISOString(),
              execute_now: true,
              // A specific answer (e.g. a confirm-choice option) goes straight
              // to the mission as its continuation — deterministic backend
              // code, not the general chat's free-form tool selection, which
              // has to guess the right tool out of everything Pabs can do and
              // can pick the wrong one (see the approve_learning incident this
              // replaces: a plain chat reply approving a mission's proposed
              // learning was misrouted to an unrelated tool and never reached
              // the mission at all).
              ...(instruction && typeof instruction === 'object' ? instruction : instruction ? { user_instruction: instruction } : {}),
            }
          : {}),
      });
      if (res.ok) {
        const responseData = await res.json().catch(() => ({}));
        window.dispatchEvent(new CustomEvent('pabs:agentTasksUpdated', { detail: { planId: item.plan_id || item._plan_id } }));
        const actionLabel = {
          pause: 'Mission paused',
          resume: 'Mission resumed',
          acknowledge: 'Acknowledged — no re-run needed',
          'cancel-mission': 'Mission cancelled',
          'cancel-task': 'Task cancelled',
        }[action];
        toast({ title: actionLabel, status: 'success', duration: 2000, isClosable: true });
        if (responseData.mission) {
          setDetail(prev => ({ ...prev, mission: responseData.mission }));
        }
        await onRefresh?.();
        if (action === 'cancel-mission' || action === 'cancel-task') {
          onClose();
        } else {
          restoreScrollPosition();
        }
      } else {
        const payload = await res.json().catch(() => ({}));
        toast({ title: payload.error || 'Could not update this work', status: 'error', duration: 3000, isClosable: true });
        restoreScrollPosition();
      }
    } catch {
      toast({ title: 'Could not update this work', status: 'error', duration: 3000, isClosable: true });
      restoreScrollPosition();
    }
    setActionUpdating(false);
    setPendingAction(null);
  };

  const handleTaskAction = async (action, instruction = null) => {
    if (action === 'cancel-mission' || action === 'cancel-task') {
      setConfirmAction({
        ...destructiveActionCopy('cancel', item),
        onConfirm: () => performTaskAction(action),
      });
      return null;
    }
    return performTaskAction(action, instruction);
  };

  const handleGithubChangeDecision = async (decision, feedback = '') => {
    if (!item?.id || actionUpdating || githubPendingDecision) return;
    setGithubPendingDecision(decision);
    setActionUpdating(true);
    try {
      const response = await fetchWithAuth(`/api/agent/tasks/${item.id}/github-change-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, feedback }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        const error = new Error(payload.error || 'Could not update GitHub change request');
        error.status = response.status;
        throw error;
      }
      setDetail(previous => previous ? {
        ...previous,
        task: payload.task || previous.task,
      } : previous);
      window.dispatchEvent(new CustomEvent('pabs:githubChangeDecision', {
        detail: {
          taskId: payload.task?.id || item.id,
          status: payload.task?.status || payload.result?.state,
          result: payload.result || payload.task?.result || {},
        },
      }));
      window.dispatchEvent(new CustomEvent('pabs:agentTasksUpdated'));
      dispatchGithubDecisionReply(decision, {
        status: payload.task?.status || payload.result?.state,
        result: payload.result || payload.task?.result || {},
      }, payload.assistant_message);
      setGithubReviewMode(null);
      setGithubReviewFeedback('');
      toast({
        title: decision === 'approve'
          ? 'GitHub change approved'
          : decision === 'request_changes'
            ? 'Changes requested'
            : 'GitHub change rejected',
        status: decision === 'reject' ? 'info' : 'success',
        duration: 2400,
        isClosable: true,
      });
      await onRefresh?.();
    } catch (error) {
      if (error.status === 402 || isCreditBlockedError(error.message)) {
        dispatchCreditBlockedReply();
        toast({
          title: 'Task credits needed',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        setGithubReviewMode(null);
        setGithubReviewFeedback('');
        return;
      }
      toast({
        title: error.message || 'Could not update GitHub change request',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setGithubPendingDecision(null);
      setActionUpdating(false);
    }
  };

  const handleDecisionCaptureDecision = async (decision, payload = {}) => {
    if (!item?.id || actionUpdating || decisionCapturePendingDecision) return;
    setDecisionCapturePendingDecision(decision);
    setActionUpdating(true);
    try {
      const response = await fetchWithAuth(`/api/agent/tasks/${item.id}/decision-capture-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision,
          revised_intent: payload.revised_intent,
          commit_note: payload.commit_note,
        }),
      });
      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responsePayload.error || 'Could not update decision job');
      }
      setDetail(previous => previous ? {
        ...previous,
        task: responsePayload.task || previous.task,
      } : previous);
      if (responsePayload.task) {
        setProductRequirementDraft(productRequirementDraftFromTask(responsePayload.task));
      }
      window.dispatchEvent(new CustomEvent('pabs:agentTasksUpdated'));
      dispatchDecisionCaptureReply(responsePayload.assistant_message);
      toast({
        title: decision === 'commit'
          ? 'Committed'
          : decision === 'reject'
            ? 'Rejected'
            : decision === 'defer'
              ? 'Left open'
              : decision === 'revise'
                ? 'Updated'
                : 'Closed',
        status: (decision === 'reject' || decision === 'defer') ? 'info' : 'success',
        duration: 2400,
        isClosable: true,
      });
      await onRefresh?.();
      if (decision !== 'revise' && decision !== 'defer') {
        onClose();
      }
    } catch (error) {
      toast({
        title: decision === 'commit'
          ? 'Could not commit this decision'
          : decision === 'reject'
            ? 'Could not reject this decision'
            : 'Could not update this decision',
        description: error.message && !/parameter|sql|syntax|postgres|deduced/i.test(error.message)
          ? error.message
          : 'Pabs could not save the action. Try again in a moment.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setDecisionCapturePendingDecision(null);
      setActionUpdating(false);
    }
  };

  const handleProductRequirementReview = async (decision) => {
    if (!item?.id || actionUpdating || productRequirementPendingDecision) return;
    setProductRequirementPendingDecision(decision);
    setActionUpdating(true);
    try {
      const response = await fetchWithAuth(`/api/agent/tasks/${item.id}/product-requirement-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision,
          ...productRequirementDraft,
        }),
      });
      const responsePayload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responsePayload.error || 'Could not update product requirement');
      }
      setDetail(previous => previous ? {
        ...previous,
        task: responsePayload.task || previous.task,
      } : previous);
      if (responsePayload.task) {
        setProductRequirementDraft(productRequirementDraftFromTask(responsePayload.task));
      }
      window.dispatchEvent(new CustomEvent('pabs:agentTasksUpdated'));
      if (responsePayload.assistant_message) {
        window.dispatchEvent(new CustomEvent('pabs:assistantMessage', {
          detail: {
            source: 'product_requirement_review',
            text: responsePayload.assistant_message,
          },
        }));
      }
      toast({
        title: decision === 'approve_handoff' ? 'Ready for handoff' : 'Requirement updated',
        status: 'success',
        duration: 2400,
        isClosable: true,
      });
      setProductRequirementEditMode(false);
      await onRefresh?.();
    } catch (error) {
      toast({
        title: 'Could not update product requirement',
        description: error.message,
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
    } finally {
      setProductRequirementPendingDecision(null);
      setActionUpdating(false);
    }
  };

  const performDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetchWithAuth(`/api/agent/tasks/${item.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast({ title: 'Task deleted', status: 'success', duration: 2000, isClosable: true });
        onClose();
        onDelete?.(item.id);
      } else {
        toast({ title: 'Failed to delete task', status: 'error', duration: 3000, isClosable: true });
      }
    } catch { toast({ title: 'Error deleting task', status: 'error', duration: 3000, isClosable: true }); }
    setDeleting(false);
  };

  const handleDelete = () => {
    setConfirmAction({
      ...destructiveActionCopy('delete', item),
      onConfirm: performDelete,
    });
  };

  const handleCadenceChange = async (frequency) => {
    const missionId = detail?.mission?.id;
    const isPulseRoutine = item.task_type === 'generate_pulse' && item.pulse_frequency;
    if ((!missionId && !isPulseRoutine) || cadenceUpdating) return;
    setCadenceUpdating(true);
    try {
      const response = missionId
        ? await fetchWithAuth(`/api/agent/missions/${missionId}/cadence`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ frequency }),
          })
        : frequency === 'paused'
          ? await fetchWithAuth(`/api/pulse/${item.plan_id}/schedule/pause`, { method: 'POST' })
          : await fetchWithAuth(`/api/pulse/${item.plan_id}/schedule`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                frequency,
                day_of_week: item.pulse_day_of_week ?? 1,
                hour: item.pulse_hour ?? 9,
                timezone: item.pulse_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                tone: item.pulse_tone || 'direct',
                email: !!item.pulse_email_enabled,
              }),
            });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Could not update cadence');
      }
      const payload = await response.json();
      if (payload.mission) {
        setDetail(previous => ({ ...previous, mission: payload.mission }));
      }
      await onRefresh?.();
      if (item.plan_id) {
        window.dispatchEvent(new CustomEvent('pabs:pulseScheduleUpdated', { detail: { planId: item.plan_id } }));
      }
      toast({
        title: `Cadence changed to ${cadenceLabel(frequency)}`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: error.message || 'Could not update cadence',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    setCadenceUpdating(false);
  };

  const handleEmailToggle = async (enabled) => {
    setEmailEnabled(enabled);
    try {
      const missionId = detail?.mission?.id || item?.mission_id;
      const res = missionId
        ? await fetchWithAuth(`/api/agent/missions/${missionId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              authority: {
                ...(detail?.mission?.authority || safeObject(item?.mission_authority)),
                email_plan_owner: enabled ? 'automatic' : 'never',
              },
            }),
          })
        : await fetchWithAuth(`/api/pulse/${item.plan_id}/email`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled }),
          });
      if (!res?.ok) {
        setEmailEnabled(!enabled);
        return;
      }
      const payload = await res.json().catch(() => ({}));
      if (payload.mission) {
        setDetail(previous => ({ ...previous, mission: payload.mission }));
      }
      await onRefresh?.();
      toast({
        title: enabled ? 'Email summaries enabled' : 'Email summaries disabled',
        status: 'success',
        duration: 1800,
        isClosable: true,
      });
    } catch {
      setEmailEnabled(!enabled);
      toast({
        title: 'Could not update email setting',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (!item) return null;

  const isTask  = item.record_type === 'task';
  const isRoutineRecord = item.record_type === 'routine';
  const isWorkItem = isTask || isRoutineRecord;
  const isPulse = item.record_type === 'pulse';
  const isSuggestedWork = isTask && item.status === 'suggested';

  const label  = isWorkItem ? jobDisplayLabel(item) : 'Strategy pulse';
  const mission = detail?.mission || null;
  const lifecycle = detail?.lifecycle || null;
  const isRoutineContainer = isRoutineRecord || (
    isRoutineView
    && (item.mission_id || item.capability === 'agent_mission' || Boolean(taskCadence(item)))
  );
  const presentation = isRoutineContainer
    ? buildRoutinePresentation(item, mission, label, projectRoutineLinkedWork(item, relatedRecords))
    : isPulse ? {
    version: 1,
    identity: { id: item.id, title: label, kind: 'pulse' },
    now: {
      state: 'completed',
      reason: null,
      raw_status: 'completed',
      display_mode: 'omit',
      title: 'Strategy pulse completed',
      message: item.is_nudge ? 'No material strategy change was found.' : 'Pabs completed this strategy review.',
      related_work_ids: [`pulse:${item.id}`],
      primary_action: null,
      secondary_actions: [],
    },
    work: [{
      id: `pulse:${item.id}`,
      kind: 'pulse',
      title: item.subject || 'Strategy pulse',
      summary: null,
      status: 'final',
      preview: item.content || null,
      outcome: item.is_nudge
        ? { type: 'no_change_required', label: 'No plan change required' }
        : { type: 'review_complete', label: 'Strategy review complete' },
      primary_action: null,
    }],
    activity: item.created_at ? [{ id: `pulse-created:${item.id}`, type: 'completed', label: 'Strategy pulse created', at: item.created_at }] : [],
    details: {
      objective: 'Review the strategy for material changes and recommend the next move.',
      schedule: {},
      routine: false,
      notifications: { configurable: false },
      sources: [],
    },
  } : lifecycle?.presentation || null;
  const showNotificationsProminently = Boolean(
    presentation?.details?.notifications?.configurable
    && !['completed', 'failed', 'cancelled'].includes(presentation?.now?.state)
  );
  const nowDisplayMode = presentation?.now?.display_mode || (
    ['completed', 'cancelled'].includes(presentation?.now?.state)
      ? 'omit'
      : ['needs_action', 'blocked', 'failed'].includes(presentation?.now?.state)
        ? 'callout'
        : 'summary'
  );
  const openingRequest = presentation?.details?.request || null;
  const workOwnsInteraction = presentation?.work?.some(work => [
    'mission_result',
    'design_authority_review',
    'design_remediation',
    'design_exception',
    'accessibility_assessment',
    'design_implementation_comparison',
    'design_system_usage_review',
  ].includes(work.kind));
  const openingRequestIsPabs = openingRequest?.actor === 'pabs'
    || String(openingRequest?.requested_by || '').trim().toLowerCase() === 'pabs';
  const storyOwnsNowState = Boolean(presentation?.story?.turns?.some(turn => turn.kind === 'state'));
  const timelineItems = buildJobTimelineItems(presentation, openingRequest, item?.id);
  const withPabsTurnMeta = (workItem, content) => (
    <Box key={workItem.id}>
      {(workItem.timeline_work_index == null || workItem.timeline_work_index === 0) && <Text fontSize="10px" color="gray.400" ml="42px" mb={1.5}>
          Pabs{(workItem.timeline_turn?.at || workItem.created_at) ? ` · ${formatDateTime(workItem.timeline_turn?.at || workItem.created_at)}` : ''}
        </Text>}
      {content}
      {workItem.kind !== 'mission_result'
        && workItem.timeline_turn?.state_widget
        && workItem.timeline_work_index === (workItem.timeline_turn.work_ids?.length || 1) - 1
        && <Box ml="42px" bg="gray.800" color="white" borderRadius="lg" px={4} pb={3} mt={2}>
          <JobStateWidget stateWidget={workItem.timeline_turn.state_widget} onResolve={canResolveBlocker ? () => handleTaskAction('resume') : null} resolving={pendingAction === 'resume'} disabled={actionUpdating}/>
        </Box>}
    </Box>
  );
  const normalizeStoryText = value => String(value || '').trim().replace(/\s+/g, ' ');
  const requestReplacesNowSummary = openingRequestIsPabs
    && ['proposal', 'scheduled'].includes(presentation?.now?.state)
    && normalizeStoryText(openingRequest?.message) === normalizeStoryText(presentation?.now?.message);
  const effectiveStatus = isRoutineView
    ? routineDisplayStatus({ ...item, mission_status: mission?.status || item.mission_status })
    : effectiveTaskStatus({
        ...item,
        ...detail?.task,
        status: lifecycle?.canonical_status || detail?.task?.status || item.status,
        mission_status: lifecycle?.canonical_status || mission?.status || item.mission_status,
      });
  const taskResult = safeObject(detail?.task?.result || item.result);
  const status = isWorkItem ? lifecycleStatusLabel({ ...item, status: effectiveStatus, result: taskResult }) : 'Done';
  const color  = isWorkItem ? (STATUS_COLOR[effectiveStatus] || 'gray') : 'green';
  // Latest state date of the job, shown consistently below the title in the detail view
  // for every job type/state (open, completed, cancelled, rejected, pulse, decision, etc.).
  // completed_at for terminal states, otherwise updated_at (bumped on each state change),
  // falling back to created_at. Prefer the freshly loaded detail over the list item.
  const detailStateDate = isSuggestedWork
    ? (detail?.task?.created_at || item.created_at || detail?.task?.updated_at || item.updated_at || null)
    : (detail?.task?.completed_at || item.completed_at
      || detail?.task?.updated_at || item.updated_at
      || detail?.task?.created_at || item.created_at
      || null);
  const detailStateDatePrefix = isSuggestedWork ? 'Suggested ' : '';
  const lifecycleOutputs = Array.isArray(lifecycle?.outputs)
    ? lifecycle.outputs
    : [];
  const missionResultOutput = lifecycleOutputs.find(output => output.output_kind === 'content_artifact');
  const deliveryOutputs = lifecycleOutputs.filter(output =>
    ['chat_delivery', 'email_notification', 'email_deliverable'].includes(output.output_kind)
  );
  const documentArtifact = Array.isArray(taskResult.artifacts)
    ? taskResult.artifacts.find(artifact => artifact?.kind === 'document')
    : (taskResult.document_id ? {
        kind: 'document',
        id: taskResult.document_id,
        document_type: taskResult.document_type,
        destination: taskResult.destination,
      } : null);
  const documentDetail = detail?.artifact?.type === 'document' ? detail.artifact.data : null;
  if (documentArtifact && documentDetail?.title) {
    documentArtifact.title = documentDetail.title;
  }
  const documentPreview = documentDetail?.preview || taskResult.document_preview || null;
  const taskParameters = safeObject(detail?.task?.parameters || item.parameters);
  const documentSources = Object.entries(safeObject(taskParameters.context_sources))
    .filter(([, enabled]) => enabled === true)
    .map(([source]) => source.replace(/_/g, ' '));
  const isGithubChangeRequest = isTask && (
    item.capability === 'github_change_request' ||
    detail?.task?.capability === 'github_change_request'
  );
  const isProductRequirementIssue = isTask && (
    item.capability === 'product_requirement_issue' ||
    detail?.task?.capability === 'product_requirement_issue'
  );
  const isDesignRemediation = isTask && (
    item.capability === 'generate_design_remediation_plan' ||
    detail?.task?.capability === 'generate_design_remediation_plan'
  );
  const productRequirementNeedsReview = isProductRequirementIssue
    && !['completed', 'cancelled', 'archived'].includes(effectiveStatus)
    && !taskResult.delivery_summary
    && !(Array.isArray(taskResult.lifecycle_history) && taskResult.lifecycle_history.some(event => event?.event === 'product_requirement_delivery_reported'))
    && (
      effectiveStatus === 'waiting_for_user' ||
      ['human', 'human_review'].includes(String(taskParameters.routing_target || ''))
    );
  const productRequirementDeliveryNeedsReview = isProductRequirementIssue
    && effectiveStatus === 'waiting_for_user'
    && Boolean(taskResult.delivery_summary || (Array.isArray(taskResult.lifecycle_history) && taskResult.lifecycle_history.some(event => event?.event === 'product_requirement_delivery_reported')));
  const isDecisionCapture = isTask && isDecisionCaptureTask({ ...item, result: taskResult });
  const decisionCandidate = taskResult.decision_candidate || null;
  const decisionStatus = taskResult.decision_status || decisionCandidate?.status || null;
  const decisionTheme = decisionStatus === 'committed'
    ? { head: 'green.700' }
    : decisionStatus === 'rejected'
      ? { head: 'red.600' }
      : decisionStatus === 'deferred'
        ? { head: 'yellow.700' }
        : decisionStatus === 'abandoned'
          ? { head: 'gray.500' }
          : { head: 'gray.800' };
  const decisionSummary = decisionCandidate
    ? decisionSummaryLine(decisionCandidate, taskResult, taskParameters)
    : null;
  const decisionUserSaid = decisionCandidate
    ? decisionUserStatement(taskResult, taskParameters)
    : null;
  const decisionSource = decisionCandidate
    ? decisionSourceLabel(taskResult, taskParameters)
    : 'Source';
  const decisionCallReasonText = decisionCandidate ? decisionCallReason(decisionCandidate) : null;
  const decisionReadParts = splitLeadSentence(decisionCallReasonText || decisionSummary || '');
  const decisionTradeoffText = decisionCandidate ? decisionTradeoff(decisionCandidate) : null;
  const decisionImpact = decisionCandidate ? decisionImpactLine(decisionCandidate) : null;
  const decisionConfidence = decisionCandidate ? decisionConfidenceWord(decisionCandidate) : null;
  const decisionWatch = decisionCandidate ? decisionWatchLine(decisionCandidate) : null;
  const decisionIsPending = isDecisionCapture && needsDecisionCommitment({
    ...item,
    status: detail?.task?.status || item.status,
    result: taskResult,
  });
  const suggestedPurpose = suggestedJobPurpose({
    ...item,
    parameters: {
      ...taskParameters,
      pm_purpose: taskParameters.pm_purpose || mission?.scope?.pm_purpose,
    },
  });
  const isCompleted = isPulse || (!isRoutineRecord && effectiveStatus === 'completed');
  const isWaitingForInput = ['waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'waiting_for_credits', 'blocked'].includes(effectiveStatus);
  const isMission = isWorkItem && (
    item.capability === 'agent_mission'
    || detail?.task?.capability === 'agent_mission'
    || !!mission
  );
  const hasMissionOutcome = isMission && (isCompleted || isWaitingForInput);
  const isPulseRoutine = isWorkItem && item.task_type === 'generate_pulse' && !!item.pulse_frequency;
  const objectiveText = isPulseRoutine
    ? 'Regularly reviews your strategy for meaningful changes. If something has shifted, it generates a full strategy pulse with analysis and recommendations. If nothing material has changed, the run is skipped.'
    : isProductRequirementIssue
      ? (taskParameters.requirement || item.reasoning || 'Route this build requirement to the right person or tool.')
    : isDecisionCapture && decisionCandidate
      ? decisionSummaryLine(decisionCandidate, taskResult, taskParameters)
      : (isCompleted && detail?.run?.objective)
      || mission?.objective
      || item.reasoning
      || TASK_TYPE_DESCRIPTION[item.task_type]
      || 'Complete this work for the plan.';
  const githubCardResult = isGithubChangeRequest
    ? {
        id: detail?.task?.id || item.id,
        status: taskResult.state || effectiveStatus,
        summary: taskResult.summary || objectiveText,
        result: {
          ...taskParameters,
          ...taskResult,
          state: taskResult.state || effectiveStatus,
        },
      }
    : null;
  const planText = mission?.completion_policy
    || taskParameters.context
    || (!isCompleted ? item.reasoning : null);
  const outcomeText = taskResult.summary
    || taskResult.value
    || (taskResult.recipient
      ? `The email was sent to ${taskResult.recipient}${taskResult.subject ? ` with the subject “${taskResult.subject}”.` : '.'}`
      : null)
    || (taskResult.pulse_id
      ? `The strategy pulse was created${taskResult.sent ? ' and emailed.' : '.'}`
      : null);
  const needsUserText = taskResult.needs_user
    || taskResult.needs_user_input
    || taskResult.user_prompt
    || taskResult.question
    || null;
  const materialFindings = Array.isArray(taskResult.material_findings)
    ? taskResult.material_findings.filter(Boolean)
    : [];
  const resultSources = Array.isArray(taskResult.sources)
    ? taskResult.sources.filter(source => source?.url)
    : [];
  const missionRunSteps = Array.isArray(detail?.run_steps)
    ? detail.run_steps.filter(step => step.type !== 'handoff')
    : [];
  const completedMissionSteps = missionRunSteps.filter(step => step.status === 'completed');
  const incompleteMissionSteps = missionRunSteps.filter(step => step.status !== 'completed');
  // MissionRunner persists each tool call's raw result on its own run step
  // (agent_run_steps.output), but the mission's own top-level result never
  // copies a document reference forward — so a mission that calls
  // generate_document needs its own derivation here rather than reusing
  // documentArtifact (which only reads taskResult, the top-level result).
  const missionDocumentArtifacts = completedMissionSteps
    .filter(step => step.tool_name === 'generate_document' && step.output)
    .map(step => {
      const output = safeObject(step.output);
      const fromArtifacts = Array.isArray(output.artifacts)
        ? output.artifacts.find(artifact => artifact?.kind === 'document')
        : null;
      return fromArtifacts || (output.document_id ? {
        kind: 'document',
        id: output.document_id,
        document_type: output.document_type,
        destination: output.destination,
      } : null);
    })
    .filter(Boolean);
  const emailResult = taskResult.recipient ? taskResult : null;
  const pulseResult = taskResult.pulse_id ? taskResult : null;
  const pulseContent = detail?.artifact?.type === 'pulse' ? detail.artifact.data : null;
  const trigger = mission?.trigger || {};
  const cadenceText = isPulseRoutine
    ? cadenceLabel(item.pulse_frequency)
    : trigger.type === 'recurring'
      ? `${trigger.frequency ? humanizeMentalModelValue(trigger.frequency) : 'Recurring'}`
      : trigger.type === 'one_time' ? 'Once' : null;
  const nextScheduledAt = isSuggestedWork
    ? null
    : (mission?.next_run_at || item.mission_next_run_at || item.next_run_at || item.scheduled_at);
  const nextRunText = nextScheduledAt ? formatDateTime(nextScheduledAt) : null;
  const dueNow = ['todo', 'pending'].includes(effectiveStatus) && isDueNow(nextScheduledAt);
  const scheduleDisplayText = isSuggestedWork ? 'Not scheduled yet' : (dueNow ? 'Ready now' : nextRunText);
  const plannedActions = !isCompleted && planText && planText !== objectiveText
    ? operatingActionLines(planText)
    : [];
  const missionStatus = mission?.status || item.mission_status;
  const waitingTaskStatuses = ['waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'blocked'];
  const hasCapturedContinuation = !!taskParameters.continuation;
  const canActivateTask = isTask && item.status === 'suggested' && !isProductRequirementIssue;
  const canConfigureNotifications = !isCompleted && (isPulseRoutine || (isMission && !isSuggestedWork));
  const canResumeMission = isMission && !isSuggestedWork && (
    missionStatus === 'paused'
    || (waitingTaskStatuses.includes(effectiveStatus) && hasCapturedContinuation)
  );
  const canResolveBlocker = isMission && !isSuggestedWork && ['blocked', 'waiting_for_credits'].includes(effectiveStatus);
  const canRunNowTask = isTask
    && !isProductRequirementIssue
    && !isSuggestedWork
    && !isRoutineView
    && ['todo', 'pending'].includes(effectiveStatus);
  const canPauseMission = isMission
    && !isSuggestedWork
    && !canResumeMission
    && !canRunNowTask
    && !['suggested', 'paused', 'cancelled', 'completed'].includes(missionStatus);
  const canPauseMissionFromMenu = isMission
    && !isSuggestedWork
    && !canResumeMission
    && !['suggested', 'paused', 'cancelled', 'completed'].includes(missionStatus);
  const canCancelMission = isMission && !isSuggestedWork && !['suggested', 'cancelled', 'completed'].includes(missionStatus);
  const canCancelTask = isTask && !isMission && (['todo', 'pending', 'paused', 'approved', 'awaiting_approval', 'waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'blocked'].includes(effectiveStatus) || (item.capability === 'external_blocker_work' && effectiveStatus === 'running'));
  const hasUserActions = !detailLoading
    && (canActivateTask || canRunNowTask || canPauseMission || canResumeMission || canCancelMission || canCancelTask || decisionIsPending || productRequirementNeedsReview || productRequirementDeliveryNeedsReview);
  const canPausePulse = isPulseRoutine && item.status === 'active' && isRoutineView;
  const canResumePulse = isPulseRoutine && item.status === 'paused' && isRoutineView;
  const canSchedulePulse = isPulseRoutine && item.status === 'unscheduled' && isRoutineView;
  const canEditMissionCadence = isMission
    && (!isCompleted || isRoutineView || isSuggestedWork)
    && !['cancelled', 'completed', 'failed'].includes(missionStatus);
  const canEditPulseCadence = isPulseRoutine && isRoutineView;
  const canEditCadence = canEditMissionCadence || canEditPulseCadence;
  const cadenceValue = isPulseRoutine
    ? (item.pulse_frequency || 'paused')
    : trigger.type === 'recurring'
      ? (trigger.frequency || trigger.schedule || 'weekly')
      : 'once';
  const plannedHeaderMetadata = isTask
    && ['todo', 'pending'].includes(effectiveStatus)
    && !isRoutineView
    ? (nextScheduledAt ? `Planned for ${formatDateTime(nextScheduledAt)}` : null)
    : null;
  const renderDetailPrimaryAction = () => {
    const buttonProps = { size: 'sm', minH: '32px', px: 3.5, borderRadius: 'full', colorScheme: 'gray' };
    if (canActivateTask) return <ActivateJobButton
      isLoading={actionUpdating}
      onActivate={() => handleTaskAction('activate')}
      creditBalance={activationCredit?.creditBalance}
      creditCost={activationCredit?.creditCost}
      creditGated={activationCredit?.creditGated}
      creditsResetAt={activationCredit?.creditsResetAt}
      onTopUp={activationCredit?.onTopUp}
      buttonProps={buttonProps}
    >Schedule now</ActivateJobButton>;
    if (canRunNowTask) return <Button {...buttonProps} onClick={() => {
      const msg = `Run ${jobDisplayLabel(item)} now (task ID: ${item.id})`;
      onClose?.();
      window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
      window.dispatchEvent(new CustomEvent('pabs:runTask', { detail: { message: msg } }));
    }}>Run now</Button>;
    if (canPausePulse) return <Button {...buttonProps} isLoading={cadenceUpdating} onClick={() => handleCadenceChange('paused')}>Pause routine</Button>;
    if (canResumePulse) return <Button {...buttonProps} isLoading={cadenceUpdating} onClick={() => handleCadenceChange(item.pulse_frequency || 'weekly')}>Resume routine</Button>;
    if (canSchedulePulse) return <Button {...buttonProps} onClick={() => setShowScheduleEditor(true)}>Schedule routine</Button>;
    if (canResumeMission) return <Button {...buttonProps} isLoading={actionUpdating} loadingText={isRoutineView ? 'Scheduling…' : 'Running job…'} onClick={() => handleTaskAction('resume')}>{isRoutineView ? 'Schedule routine' : 'Resume job'}</Button>;
    if (canPauseMission) return <Button {...buttonProps} isLoading={actionUpdating} onClick={() => handleTaskAction('pause')}>{isRoutineView ? 'Pause routine' : 'Pause job'}</Button>;
    return null;
  };
  const detailPrimaryAction = !detailLoading ? renderDetailPrimaryAction() : null;
  const hideNowSummary = Boolean(requestReplacesNowSummary && detailPrimaryAction);

  const AGENT_LABEL = {
    pulse_agent:       'Pulse',
    engagement_agent:  'Engagement',
    chat_design_lead:  'PABS chat',
    mission_agent:     'Pabs mission',
    pulse_scheduler:   'Pulse scheduler',
    pulse_reminder:    'Pulse reminder',
    admin:             'Admin',
  };

  return (
    <Flex direction="column" h="full" borderLeft="1px solid" borderColor="gray.100">
      {/* Job identity and actions */}
      <Box pl={4} pr={2} py={3} borderBottomWidth="1px" borderColor="gray.100" flexShrink={0}>
        <HStack justify="space-between" align="flex-start" spacing={2}>
          <Text fontSize="md" fontWeight="600" color="gray.900" lineHeight="1.4" flex={1} minW={0}>
              {label}
          </Text>
          <HStack spacing={1} flexShrink={0}>
          {isTask && (canPauseMissionFromMenu || canCancelMission || canCancelTask || productRequirementNeedsReview) && (
            <Menu placement="bottom-end">
              <MenuButton
                as={IconButton}
                icon={<span className="material-icons-outlined" style={{ fontSize: '16px' }}>more_vert</span>}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                aria-label="Task actions"
                title="Job actions"
              />
              <MenuList minW="170px" fontSize="sm">
                {canPauseMissionFromMenu && !detailPrimaryAction && (
                  <MenuItem icon={<span className="material-icons-outlined" style={{ fontSize: '15px' }}>pause</span>} onClick={() => handleTaskAction('pause')}>Pause</MenuItem>
                )}
                {(canCancelMission || canCancelTask || productRequirementNeedsReview) && (
                  <MenuItem color="red.500" icon={<span className="material-icons-outlined" style={{ fontSize: '15px' }}>cancel</span>} onClick={() => handleTaskAction(canCancelMission ? 'cancel-mission' : 'cancel-task')}>Cancel job</MenuItem>
                )}
              </MenuList>
            </Menu>
          )}
          {isTask && !hasUserActions && ['suggested', 'completed', 'failed', 'cancelled', 'archived'].includes(effectiveStatus) && !canCancelMission && (
            <Tooltip label="Delete job" hasArrow><IconButton icon={<span className="material-icons-outlined" style={{ fontSize: '15px' }}>delete_outline</span>} size="xs" variant="ghost" colorScheme="gray" aria-label="Delete job" isLoading={deleting} onClick={handleDelete}/></Tooltip>
          )}
          <Tooltip label="Close detail" hasArrow><IconButton icon={<span className="material-icons-outlined" style={{ fontSize: '16px' }}>close</span>} size="xs" variant="ghost" colorScheme="gray" aria-label="Close detail" onClick={onClose}/></Tooltip>
          </HStack>
        </HStack>
        {detailPrimaryAction ? (
          <HStack spacing={2} mt={2} flexWrap="wrap">
            {detailPrimaryAction}
            {canEditCadence && effectiveStatus !== 'unscheduled' && (
              <Button size="sm" minH="32px" px={3} borderRadius="full" variant="ghost" colorScheme="gray" onClick={() => setShowScheduleEditor(open => !open)}>
                {showScheduleEditor ? 'Hide schedule' : 'Change schedule'}
              </Button>
            )}
          </HStack>
        ) : (
          <HStack spacing={2} align="center" flexWrap="wrap" mt={1}>
            {isWorkItem && effectiveStatus !== 'unscheduled' && (
              <Badge colorScheme={color} fontSize="10px" px={2} py={0.5} borderRadius="full" flexShrink={0}>
                {status}
              </Badge>
            )}
            {plannedHeaderMetadata ? (
              <Text fontSize="xs" color="gray.500">{plannedHeaderMetadata}</Text>
            ) : detailStateDate && (
              <Text fontSize="xs" color="gray.500">{detailStateDatePrefix}{formatDateTime(detailStateDate)}</Text>
            )}
          </HStack>
        )}
        {showScheduleEditor && canEditMissionCadence && (
          <HStack mt={3} pt={3} borderTop="1px solid" borderColor="gray.100" justify="space-between" spacing={3}>
            <Box minW={0}>
              <Text fontSize="xs" fontWeight="700" color="gray.700">Run cadence</Text>
              <Text fontSize="10px" color="gray.500" mt={0.5}>Choose when Pabs should run this job again.</Text>
            </Box>
            <Select
              size="sm"
              w="132px"
              flexShrink={0}
              value={cadenceValue}
              onChange={event => handleCadenceChange(event.target.value)}
              isDisabled={cadenceUpdating}
              fontSize="xs"
              borderColor="gray.200"
              bg="white"
              aria-label="Job run cadence"
            >
              <option value="once">Once</option>
              <option value="daily">Daily</option>
              <option value="weekdays">Weekdays</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </Select>
          </HStack>
        )}
      </Box>

      <Box ref={detailScrollRef} tabIndex={-1} px={4} pt={2} pb={6} overflowY="auto" flex={1} sx={{ outline: 'none' }}>
        {isTask && detailLoading ? (
          <Flex justify="center" align="center" pt={10}>
            <Spinner size="sm" color="gray.300" />
          </Flex>
        ) : (
          <VStack align="stretch" spacing={5}>
            {!detailLoading && !presentation && (
              <HStack spacing={2} justify="flex-start" align="center" flexWrap="wrap" mt={-2}>
                {isTask && canActivateTask && !detailPrimaryAction && (
                  <ActivateJobButton
                    isLoading={actionUpdating}
                    onActivate={() => handleTaskAction('activate')}
                    creditBalance={activationCredit?.creditBalance}
                    creditCost={activationCredit?.creditCost}
                    creditGated={activationCredit?.creditGated}
                    creditsResetAt={activationCredit?.creditsResetAt}
                    onTopUp={activationCredit?.onTopUp}
                    buttonProps={{
                      size: 'xs',
                      variant: 'solid',
                      colorScheme: 'blue',
                      leftIcon: <span className="material-icons-outlined" style={{ fontSize: '14px' }}>play_arrow</span>,
                    }}
                  >
                    Schedule now
                  </ActivateJobButton>
                )}
                {isTask && canRunNowTask && !detailPrimaryAction && (
                  <Tooltip label="Run this job immediately in chat, instead of waiting for the scheduled time." placement="bottom" hasArrow>
                    <Button
                      size="xs"
                      variant="solid"
                      colorScheme="blue"
                      leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>play_arrow</span>}
                      onClick={() => {
                        const msg = `Run ${jobDisplayLabel(item)} now (task ID: ${item.id})`;
                        onClose?.();
                        window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
                        window.dispatchEvent(new CustomEvent('pabs:runTask', { detail: { message: msg } }));
                      }}
                      aria-label="Run this job now"
                      title="Run this job now"
                    >
                      Run now
                    </Button>
                  </Tooltip>
                )}
                {productRequirementNeedsReview && (
                  <>
                    {productRequirementEditMode ? (
                      <>
                        <Button
                          size="xs"
                          variant="solid"
                          colorScheme="blue"
                          isLoading={productRequirementPendingDecision === 'save_review'}
                          isDisabled={!!productRequirementPendingDecision}
                          onClick={() => handleProductRequirementReview('save_review')}
                        >
                          Save
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          isDisabled={!!productRequirementPendingDecision}
                          onClick={() => {
                            setProductRequirementDraft(productRequirementDraftFromTask(detail?.task || item));
                            setProductRequirementEditMode(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="xs"
                          colorScheme="blue"
                          variant="solid"
                          isLoading={productRequirementPendingDecision === 'approve_handoff'}
                          isDisabled={!!productRequirementPendingDecision}
                          onClick={() => handleProductRequirementReview('approve_handoff')}
                        >
                          Approve handoff
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          leftIcon={<span className="material-icons-outlined" style={{ fontSize: '13px' }}>edit</span>}
                          isDisabled={!!productRequirementPendingDecision}
                          onClick={() => setProductRequirementEditMode(true)}
                        >
                          Edit
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          leftIcon={<span className="material-icons-outlined" style={{ fontSize: '13px' }}>cancel</span>}
                          isDisabled={!!productRequirementPendingDecision}
                          onClick={() => handleTaskAction('cancel-task')}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </>
                )}
                {productRequirementDeliveryNeedsReview && (
                  <>
                    <Button
                      size="xs"
                      colorScheme="green"
                      isLoading={productRequirementPendingDecision === 'accept_delivery'}
                      isDisabled={!!productRequirementPendingDecision}
                      onClick={() => handleProductRequirementReview('accept_delivery')}
                    >
                      Accept delivery
                    </Button>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="orange"
                      isLoading={productRequirementPendingDecision === 'request_followup'}
                      isDisabled={!!productRequirementPendingDecision}
                      onClick={() => handleProductRequirementReview('request_followup')}
                    >
                      Ask builder to follow up
                    </Button>
                  </>
                )}
                {isTask && canPauseMission && !detailPrimaryAction && (
                  <Tooltip label="Pause future runs for this job." placement="bottom" hasArrow>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="gray"
                      leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>pause</span>}
                      isLoading={actionUpdating}
                      onClick={() => handleTaskAction('pause')}
                      aria-label="Pause this job"
                      title="Pause this job"
                    >
                      Pause
                    </Button>
                  </Tooltip>
                )}
                {isTask && canResumeMission && !detailPrimaryAction && (
                  <Tooltip label="Continue this job using the input you already gave in chat." placement="bottom" hasArrow>
                    <Button
                      size="xs"
                      variant="solid"
                      colorScheme="blue"
                      leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>play_arrow</span>}
                      isLoading={actionUpdating}
                      onClick={() => handleTaskAction('resume')}
                      aria-label="Resume this job"
                      title="Resume this job"
                    >
                      Resume Job
                    </Button>
                  </Tooltip>
                )}
                {canPausePulse && !detailPrimaryAction && (
                  <Tooltip label="Pause this routine so it stops running automatically." placement="bottom" hasArrow>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="gray"
                      leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>pause</span>}
                      isLoading={cadenceUpdating}
                      onClick={() => handleCadenceChange('paused')}
                      aria-label="Pause this routine"
                      title="Pause this routine"
                    >
                      Pause
                    </Button>
                  </Tooltip>
                )}
                {canResumePulse && !detailPrimaryAction && (
                  <Button
                    size="xs"
                    variant="solid"
                    colorScheme="blue"
                    leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>play_arrow</span>}
                    isLoading={cadenceUpdating}
                    onClick={() => handleCadenceChange(item.pulse_frequency || 'weekly')}
                  >
                    Resume
                  </Button>
                )}
                {canSchedulePulse && !detailPrimaryAction && (
                  <Button
                    size="xs"
                    variant="solid"
                    colorScheme="blue"
                    leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>schedule</span>}
                    isLoading={cadenceUpdating}
                    onClick={() => handleCadenceChange(item.pulse_frequency || 'weekly')}
                  >
                    Save schedule
                  </Button>
                )}
              </HStack>
            )}

            {isReadyForAiBuilder(item, taskParameters) && !presentation && (
              <>
                <AiBuilderMcpBanner planId={item.plan_id} requirementId={item.id} />
                <GitHubConnectBanner planId={item.plan_id} dismissible />
              </>
            )}

            {/* Canonical job story — state, work, activity, then supporting details. */}
            {(isWorkItem || isPulse) && presentation && (
              <VStack align="stretch" spacing={5}>
                {!storyOwnsNowState
                  && !['proposal', 'scheduled'].includes(presentation?.now?.state)
                  && nowDisplayMode !== 'omit'
                  && (nowDisplayMode !== 'callout' || !(presentation?.work?.length > 0 || presentation?.details?.request?.message))
                  && !hideNowSummary
                  && presentation?.now?.state !== 'needs_action' && (
                <Box
                  border={nowDisplayMode === 'callout' ? '1px solid' : '0'}
                  borderColor={['failed', 'blocked'].includes(presentation?.now?.state) ? 'red.200' : 'orange.200'}
                  bg={nowDisplayMode === 'callout'
                    ? (['failed', 'blocked'].includes(presentation?.now?.state) ? 'red.50' : 'orange.50')
                    : 'transparent'}
                  borderRadius={nowDisplayMode === 'callout' ? 'lg' : 'none'}
                  px={nowDisplayMode === 'callout' ? 3 : 0}
                  py={nowDisplayMode === 'callout' ? 3 : 0}
                >
                  <HStack align="flex-start" spacing={nowDisplayMode === 'callout' ? 2.5 : 0}>
                    {nowDisplayMode === 'callout' && (
                    <Box
                      w="22px"
                      h="22px"
                      borderRadius="full"
                      bg="whiteAlpha.700"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      mt="1px"
                    >
                      <span className="material-icons-outlined" style={{ fontSize: '14px' }}>
                        {presentation?.now?.state === 'needs_action' ? 'reply'
                          : presentation?.now?.state === 'completed' ? 'check'
                            : presentation?.now?.state === 'failed' ? 'error_outline'
                              : presentation?.now?.state === 'running' ? 'autorenew'
                                : 'schedule'}
                      </span>
                    </Box>
                    )}
                    <Box flex={1} minW={0}>
                      {!requestReplacesNowSummary && !(nowDisplayMode === 'callout' && presentation?.now?.state === 'failed') && (
                        <Text
                          fontSize={nowDisplayMode === 'summary' ? '10px' : 'sm'}
                          fontWeight="700"
                          color={nowDisplayMode === 'summary' ? 'gray.400' : 'gray.900'}
                          textTransform={nowDisplayMode === 'summary' ? 'uppercase' : 'none'}
                          letterSpacing={nowDisplayMode === 'summary' ? 'wider' : 'normal'}
                        >
                          {presentation?.now?.title}
                        </Text>
                      )}
                      {!requestReplacesNowSummary && presentation?.now?.message && (
                        <Box fontSize="sm" color="gray.700" lineHeight="1.6" mt={nowDisplayMode === 'summary' ? 1.5 : 1}>
                          <ReactMarkdown components={markdownComponents}>
                            {presentation?.now.message}
                          </ReactMarkdown>
                        </Box>
                      )}
                      {presentation?.now?.schedule_at && !detailPrimaryAction && (
                        <Text fontSize="xs" color="gray.500" mt={1.5}>
                          {formatDateTime(presentation?.now.schedule_at)}
                        </Text>
                      )}
                      <HStack mt={3} spacing={2} flexWrap="wrap">
                        {presentation?.now?.primary_action?.type === 'activate' && canActivateTask && !detailPrimaryAction && (
                          <ActivateJobButton
                            isLoading={actionUpdating}
                            onActivate={() => handleTaskAction('activate')}
                            creditBalance={activationCredit?.creditBalance}
                            creditCost={activationCredit?.creditCost}
                            creditGated={activationCredit?.creditGated}
                            creditsResetAt={activationCredit?.creditsResetAt}
                            onTopUp={activationCredit?.onTopUp}
                            buttonProps={{ size: 'xs', colorScheme: 'blue' }}
                          >
                            Schedule now
                          </ActivateJobButton>
                        )}
                        {presentation?.now?.primary_action?.type === 'reply_in_chat' && (
                          <Button
                            size="xs"
                            colorScheme="orange"
                            onClick={() => {
                              prefillJobChat(item, `About job #${item?.id}: `);
                            }}
                          >
                            {presentation?.now.primary_action.label}
                          </Button>
                        )}
                        {/* A confirm-type question comes with real, self-contained answers from
                            the model. Each option is sent straight to the mission's own resume
                            endpoint as its continuation — deterministic backend code, not the
                            general chat's free-form tool selection. Routing this through chat
                            was the actual bug behind a real incident: a plain-text approval of a
                            mission-proposed learning got misrouted to an unrelated tool (which
                            needs an existing learning ID that doesn't exist yet for something
                            only proposed in prose) and never reached the mission at all. An
                            "I'm not sure" fallback still opens chat, since a genuine discussion
                            has no fixed answer to send directly. */}
                        {presentation?.now?.primary_action?.type === 'confirm_choice' && (
                          <HStack spacing={2} flexWrap="wrap">
                            {(presentation.now.primary_action.options || []).map((option, index) => (
                              <Button
                                key={option}
                                size="xs"
                                colorScheme={index === 0 ? 'blue' : 'gray'}
                                variant={index === 0 ? 'solid' : 'outline'}
                                isLoading={pendingAction === 'resume'}
                                isDisabled={actionUpdating}
                                onClick={() => handleTaskAction('resume', option)}
                              >
                                {option}
                              </Button>
                            ))}
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="gray"
                              onClick={() => {
                                prefillJobChat(item, `Let's talk through job #${item?.id} before I decide: `);
                              }}
                            >
                              I'm not sure
                            </Button>
                          </HStack>
                        )}
                        {presentation?.now?.primary_action?.type === 'run_now' && canRunNowTask && !detailPrimaryAction && (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            onClick={() => {
                              const msg = `Run ${jobDisplayLabel(item)} now (task ID: ${item.id})`;
                              onClose?.();
                              window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
                              window.dispatchEvent(new CustomEvent('pabs:runTask', { detail: { message: msg } }));
                            }}
                          >
                            Run now
                          </Button>
                        )}
                        {presentation?.now?.primary_action?.type === 'resume' && canResumeMission && !detailPrimaryAction && (
                          <Button size="xs" colorScheme="blue" isLoading={pendingAction === 'resume'} isDisabled={actionUpdating} loadingText="Resuming…" onClick={() => handleTaskAction('resume')}>
                            Resume
                          </Button>
                        )}
                        {presentation?.now?.primary_action?.type === 'resume_routine' && canResumeMission && !detailPrimaryAction && (
                          <Button size="xs" colorScheme="blue" isLoading={pendingAction === 'resume'} isDisabled={actionUpdating} loadingText={presentation?.now.primary_action.loading_label || 'Resuming…'} onClick={() => handleTaskAction('resume')}>
                            {presentation?.now.primary_action.label}
                          </Button>
                        )}
                        {presentation?.now?.primary_action?.type === 'resolve_blocker' && canResolveBlocker && (
                          <Button size="xs" colorScheme="blue" isLoading={pendingAction === 'resume'} isDisabled={actionUpdating} loadingText={presentation?.now.primary_action.loading_label || presentation?.now.primary_action.label} onClick={() => handleTaskAction('resume')}>
                            {presentation?.now.primary_action.label}
                          </Button>
                        )}
                        {presentation?.now?.primary_action?.type === 'retry' && isTask && (
                          <Button
                            size="xs"
                            colorScheme="blue"
                            onClick={() => {
                              const msg = `Retry ${jobDisplayLabel(item)} now (task ID: ${item.id})`;
                              onClose?.();
                              window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
                              window.dispatchEvent(new CustomEvent('pabs:runTask', { detail: { message: msg } }));
                            }}
                          >
                            Retry
                          </Button>
                        )}
                        {presentation?.now?.secondary_actions?.some(action => action.type === 'pause') && canPauseMission && !detailPrimaryAction && (
                          <Button size="xs" variant="ghost" colorScheme="gray" isLoading={pendingAction === 'pause'} isDisabled={actionUpdating} onClick={() => handleTaskAction('pause')}>
                            Pause future runs
                          </Button>
                        )}
                        {presentation?.now?.secondary_actions?.some(action => action.type === 'pause_routine') && (canPausePulse || canPauseMission) && !detailPrimaryAction && (
                          <Button
                            size="xs"
                            variant="solid"
                            colorScheme="gray"
                            isLoading={cadenceUpdating || actionUpdating}
                            onClick={() => canPausePulse ? handleCadenceChange('paused') : handleTaskAction('pause')}
                          >
                            Pause routine
                          </Button>
                        )}
                        {isPulseRoutine && item.status !== 'unscheduled' && !detailPrimaryAction && (
                          <Button
                            size="xs"
                            variant="ghost"
                            colorScheme="gray"
                            onClick={() => setShowScheduleEditor(open => !open)}
                          >
                            {showScheduleEditor ? 'Hide schedule' : 'Change schedule'}
                          </Button>
                        )}
                      </HStack>
                      {isPulseRoutine && showScheduleEditor && (
                        <Box mt={3} pt={3} borderTop="1px solid" borderColor="gray.100">
                          <PulseSettingsForm
                            planId={item.plan_id}
                            initial={{
                              frequency: item.pulse_frequency || 'weekly',
                              day: item.pulse_day_of_week ?? 1,
                              dayOfMonth: item.pulse_day_of_week ?? 1,
                              hour: item.pulse_hour ?? 9,
                              tone: item.pulse_tone || 'direct',
                              email: !!item.pulse_email_enabled,
                            }}
                            onSaved={async () => {
                              setShowScheduleEditor(false);
                              await onRefresh?.();
                              window.dispatchEvent(new CustomEvent('pabs:pulseScheduleUpdated', { detail: { planId: item.plan_id } }));
                              toast({ title: 'Schedule updated', status: 'success', duration: 2000, isClosable: true });
                            }}
                          />
                        </Box>
                      )}
                      {presentation?.now?.human_next && (
                        <Box mt={3} pt={3} borderTop="1px solid" borderColor="gray.100">
                          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1}>
                            What you'll need to do
                          </Text>
                          <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                            {presentation?.now.human_next}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </HStack>
                  {isReadyForAiBuilder(item, taskParameters) && (
                    <VStack align="stretch" spacing={2} mt={3}>
                      <AiBuilderMcpBanner planId={item.plan_id} requirementId={item.id} />
                      <GitHubConnectBanner planId={item.plan_id} dismissible />
                    </VStack>
                  )}
                </Box>
                )}

                <Box ref={conversationRef}>
                {timelineItems.length > 0 && (
                  <Box>
                    <VStack align="stretch" spacing={3}>
                      {timelineItems.map(workItem => {
                        if (workItem.kind === 'timeline_state_widget') {
                          return <Box key={workItem.id} ml="42px" bg="gray.800" color="white" borderRadius="lg" px={4} pb={3}>
                            <JobStateWidget stateWidget={workItem.timeline_turn} onResolve={canResolveBlocker ? () => handleTaskAction('resume') : null} resolving={pendingAction === 'resume'} disabled={actionUpdating}/>
                          </Box>;
                        }
                        if (workItem.kind === 'timeline_message') {
                          const turn = workItem.timeline_turn || {};
                          const isPabs = turn.actor === 'pabs';
                          return <Box key={workItem.id}>
                            <Text fontSize="10px" color="gray.400" textAlign={isPabs ? 'left' : 'right'} ml={isPabs ? '42px' : 0} mb={1.5}>
                              {turn.actor_label || (isPabs ? 'Pabs' : 'Plan owner')}{turn.via ? ` via ${turn.via}` : ''}{turn.at ? ` · ${formatDateTime(turn.at)}` : ''}
                            </Text>
                            {isPabs ? <HStack align="flex-start" spacing={2.5}>
                              <Avatar name="Pabs" size="xs" bg="gray.800" color="white" flexShrink={0} mt={1}/>
                              <Box bg="gray.800" color="white" borderRadius="0px 16px 16px 16px" px={4} py={3} flex={1}>
                                {turn.title && <Text fontSize="sm" fontWeight="700">{turn.title}</Text>}
                                {turn.message && <Text fontSize="sm" color="gray.100" lineHeight="1.6" whiteSpace="pre-wrap" mt={turn.title ? 1.5 : 0}>{turn.message}</Text>}
                                {turn.scheduled_at && <Text fontSize="xs" color="gray.300" lineHeight="1.5" mt={2}>Planned for {formatDateTime(turn.scheduled_at)}</Text>}
                                {turn.primary_action?.type === 'copy_ai_prompt' && <Box mt={3}>
                                  <Text fontSize="sm" color="gray.100" mb={2}>{turn.primary_action.label}</Text>
                                  <div className="pabs-install pabs-install-compact" data-appearance="dark"><CopyableCommand value={turn.primary_action.prompt} label="Copy prompt for your AI tool" /></div>
                                </Box>}
                                {turn.primary_action?.type === 'open_learning' && <Button mt={3} size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} onClick={() => {
                                  window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
                                  window.dispatchEvent(new CustomEvent('pabs:openLearning', { detail: { planId: item.plan_id || item._plan_id, learningId: turn.primary_action.learning_id } }));
                                }}>{turn.primary_action.label}</Button>}
                                {turn.secondary_actions?.length > 0 && <HStack spacing={2} mt={3} flexWrap="wrap">
                                  {turn.secondary_actions.map(action => action.type === 'open_origin'
                                    ? <Button key={action.type} size="sm" variant="outline" color="white" borderColor="whiteAlpha.500" _hover={{bg:'whiteAlpha.100'}} as="a"
                                        href={`/plan/${item.plan_id || item._plan_id}?view=decisions#${action.origin?.type === 'next_step' ? 'next-step' : 'blocker'}-${encodeURIComponent(action.origin?.id || '')}`}>{action.label}</Button>
                                    : action.type === 'change_handover'
                                      ? <Button key={action.type} size="sm" variant="outline" color="white" borderColor="whiteAlpha.500" _hover={{bg:'whiteAlpha.100'}}
                                          onClick={() => prefillJobChat(item, `I'd like to change the handover for Job #${item.id}: `)}>{action.label}</Button>
                                      : null)}
                                </HStack>}
                                {turn.primary_action?.type === 'reply_in_chat' && <Button mt={3} size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} onClick={() => {
                                  prefillJobChat(item, `About job #${item?.id}: `);
                                }}>{turn.primary_action.label}</Button>}
                                {turn.primary_action?.type === 'confirm_choice' && <HStack spacing={2} mt={3} flexWrap="wrap">{(turn.primary_action.options || []).map((option, index) => <Button key={option} size="sm" bg={index === 0 ? 'white' : 'transparent'} color={index === 0 ? 'gray.800' : 'white'} variant={index === 0 ? 'solid' : 'outline'} isLoading={pendingAction === 'resume'} isDisabled={actionUpdating} onClick={() => handleTaskAction('resume', option)}>{option}</Button>)}</HStack>}
                              </Box>
                            </HStack> : <Box ml={{ base: 4, md: 10 }} bg={turn.actor === 'ai_tool' ? 'gray.100' : 'blue.50'} borderRadius="16px 0 16px 16px" px={4} py={3}>
                              <Text fontSize="sm" color="gray.800" lineHeight="1.6" whiteSpace="pre-wrap">{turn.message}</Text>
                            </Box>}
                          </Box>;
                        }
                        if (workItem.kind === 'timeline_state') {
                          const turn = workItem.timeline_turn || {};
                          return <Box key={workItem.id}>
                            <Text fontSize="10px" color="gray.400" ml="42px" mb={1.5}>Pabs{turn.at ? ` · ${formatDateTime(turn.at)}` : ''}</Text>
                            <HStack align="flex-start" spacing={2.5}>
                              <Avatar name="Pabs" size="xs" bg="gray.800" color="white" flexShrink={0} mt={1}/>
                              <Box bg="gray.800" color="white" borderRadius="0px 16px 16px 16px" px={4} py={3} flex={1}>
                                {turn.title && <HStack spacing={2}><Box w="7px" h="7px" borderRadius="full" bg={turn.state === 'completed' ? 'green.300' : ['failed', 'blocked'].includes(turn.state) ? 'red.300' : 'orange.300'}/><Text fontSize="sm" fontWeight="700">{turn.title}</Text></HStack>}
                                {turn.message && <Box fontSize="sm" color="gray.100" lineHeight="1.6" mt={turn.title ? 1.5 : 0}><ReactMarkdown components={darkMarkdownComponents}>{turn.message}</ReactMarkdown></Box>}
                                {turn.primary_action?.type === 'resolve_blocker' && canResolveBlocker && <Button mt={3} size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} isLoading={pendingAction === 'resume'} isDisabled={actionUpdating} onClick={() => handleTaskAction('resume')}>{turn.primary_action.label}</Button>}
                                {turn.primary_action?.type === 'retry' && isTask && <Button mt={3} size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} onClick={() => {
                                  const msg = `Retry ${jobDisplayLabel(item)} now (task ID: ${item.id})`;
                                  onClose?.();
                                  window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
                                  window.dispatchEvent(new CustomEvent('pabs:runTask', { detail: { message: msg } }));
                                }}>{turn.primary_action.label}</Button>}
                              </Box>
                            </HStack>
                          </Box>;
                        }
                        if (workItem.kind === 'document' && presentation.work.some(work => work.kind === 'accessibility_assessment')) return null;
                        if (workItem.kind === 'github_change') {
                          return withPabsTurnMeta(workItem,
                            <GithubReviewCard
                              result={githubCardResult}
                              onDecision={handleGithubChangeDecision}
                              isUpdating={githubPendingDecision}
                              reviewMode={githubReviewMode}
                              setReviewMode={setGithubReviewMode}
                              feedback={githubReviewFeedback}
                              setFeedback={setGithubReviewFeedback}
                            />
                          );
                        }

                        if (workItem.kind === 'design_authority_review') {
                          return withPabsTurnMeta(workItem,
                            <DesignAuthorityReviewWork
                              workItem={workItem}
                              planId={item.plan_id}
                              taskId={item.id}
                              onRefresh={onRefresh}
                            />
                          );
                        }

                        if (workItem.kind === 'design_remediation') {
                          return withPabsTurnMeta(workItem, <DesignRemediationWork workItem={workItem} taskId={item.id} planId={item.plan_id} onRefresh={onRefresh}/>);
                        }

                        if (workItem.kind === 'design_exception') {
                          return withPabsTurnMeta(workItem, <DesignExceptionWork workItem={workItem} planId={item.plan_id} onRefresh={onRefresh}/>);
                        }

                        if (workItem.kind === 'accessibility_assessment') {
                          return withPabsTurnMeta(workItem, <AccessibilityAssessmentWork workItem={workItem} planId={item.plan_id} reports={<ReportDocuments documents={presentation.work.filter(work=>work.kind==='document')} onOpen={document=>{
                            const documentId = Number(document.primary_action?.target || String(document.id).split(':')[1]);
                            if (!documentId) return;
                            onCloseDrawer?.();
                            window.dispatchEvent(new CustomEvent('pabs:openDocument',{detail:{documentId}}));
                          }}/>}/>);
                        }

                        if (workItem.kind === 'design_implementation_comparison') {
                          return withPabsTurnMeta(workItem, <DesignImplementationComparisonWork workItem={workItem} planId={item.plan_id}/>);
                        }

                        if (workItem.kind === 'design_system_usage_review') {
                          return withPabsTurnMeta(workItem, <DesignSystemUsageWork workItem={workItem} planId={item.plan_id}/>);
                        }

                        if (workItem.kind === 'mission_result') {
                          return withPabsTurnMeta(workItem, <MissionResultWork workItem={workItem} onReply={() => {
                            prefillJobChat(item, `Continue job #${item?.id} with this response: `);
                          }} onResume={canResumeMission ? response => handleTaskAction('resume', response || null) : null} onChoose={isMission && waitingTaskStatuses.includes(effectiveStatus) ? response => handleTaskAction('resume', response) : null} onResolveState={canResolveBlocker ? () => handleTaskAction('resume') : null} resolvingState={pendingAction === 'resume'} stateActionDisabled={actionUpdating}/>);
                        }

                        if (['text_result', 'analysis'].includes(workItem.kind)) {
                          return withPabsTurnMeta(workItem, <PabsResponse communication={workItem.communication} fallback={workItem.summary || workItem.preview}/>);
                        }

                        const repeatsJobTitle = String(workItem.title || '').trim().toLowerCase()
                          === String(label || '').trim().toLowerCase();
                        const workTitle = repeatsJobTitle
                          ? ({
                              decision: 'Decision outcome',
                              requirement: 'Build requirement',
                              email: 'Email',
                              pulse: 'Strategy pulse',
                              plan_change: 'Plan change',
                              recommendation_set: 'Recommendation outcome',
                            }[workItem.kind] || 'Work produced')
                          : workItem.title;

                        return withPabsTurnMeta(workItem,
                          <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                            <HStack px={3} py={2.5} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2}>
                              <span className="material-icons-outlined" style={{ fontSize: '16px', color: '#718096' }}>
                                {workItem.kind === 'document' ? 'description'
                                  : workItem.kind === 'email' ? 'mail'
                                    : workItem.kind === 'pulse' ? 'radar'
                                      : workItem.kind === 'requirement' ? 'assignment'
                                        : workItem.kind === 'decision' ? 'rule'
                                          : workItem.kind === 'connector_record' ? 'hub'
                                            : workItem.kind === 'plan_change' ? 'edit_note'
                                              : 'inventory_2'}
                              </span>
                              <Text fontSize="sm" fontWeight="600" color="gray.800" flex={1} minW={0} noOfLines={1}>
                                {workTitle}
                              </Text>
                              <Badge fontSize="9px" colorScheme={['final', 'delivered', 'completed', 'created'].includes(workItem.status) ? 'green' : 'orange'}>
                                {String(workItem.status || '').replace(/_/g, ' ')}
                              </Badge>
                            </HStack>
                            <Box px={3} py={3}>
                              {workItem.outcome?.label && (
                                <HStack spacing={1.5} mb={(workItem.summary || workItem.preview) ? 2.5 : 0}>
                                  <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase">
                                    Outcome
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    {workItem.outcome.label}
                                  </Text>
                                </HStack>
                              )}
                              {(workItem.communication || workItem.summary) && !(workItem.kind === 'requirement' && productRequirementEditMode) && (
                                <PabsMessageText communication={workItem.communication} fallback={workItem.summary} fontSize="sm" color="gray.700" lineHeight="1.6"/>
                              )}
                              {workItem.kind === 'requirement' && productRequirementEditMode && (
                                <VStack align="stretch" spacing={3}>
                                  <Box>
                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700" mb={1}>What needs to be built</Text>
                                    <Textarea
                                      value={productRequirementDraft.requirement || ''}
                                      onChange={event => setProductRequirementDraft(previous => ({ ...previous, requirement: event.target.value }))}
                                      rows={5}
                                      resize="vertical"
                                      fontSize="sm"
                                    />
                                  </Box>
                                  <Box>
                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700" mb={1}>Why it matters</Text>
                                    <Textarea
                                      value={productRequirementDraft.strategic_rationale || ''}
                                      onChange={event => setProductRequirementDraft(previous => ({ ...previous, strategic_rationale: event.target.value }))}
                                      rows={3}
                                      resize="vertical"
                                      fontSize="sm"
                                    />
                                  </Box>
                                  <Box>
                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700" mb={1}>Acceptance criteria</Text>
                                    <Textarea
                                      value={productRequirementDraft.acceptance_criteria || ''}
                                      onChange={event => setProductRequirementDraft(previous => ({ ...previous, acceptance_criteria: event.target.value }))}
                                      rows={4}
                                      resize="vertical"
                                      fontSize="sm"
                                    />
                                  </Box>
                                </VStack>
                              )}
                              {workItem.kind === 'email' && workItem.preview && (
                                <Box mt={2.5} fontSize="xs" color="gray.600" lineHeight="1.6" noOfLines={5}>
                                  {String(workItem.preview).replace(/<[^>]+>/g, ' ')}
                                </Box>
                              )}
                              {workItem.kind === 'pulse' && workItem.preview && (
                                <Text mt={2.5} fontSize="xs" color="gray.600" lineHeight="1.6" noOfLines={5} whiteSpace="pre-wrap">
                                  {workItem.preview}
                                </Text>
                              )}
                              {workItem.kind === 'plan_change' && workItem.payload?.change && (
                                <Box mt={workItem.summary ? 3 : 0}>
                                  <StructuredChangeValue value={workItem.payload.change.value} />
                                </Box>
                              )}
                              {workItem.kind === 'analysis' && Array.isArray(workItem.payload?.specialistResults) && workItem.payload.specialistResults.length > 0 && (
                                <Box mt={workItem.summary ? 3 : 0}>
                                  <StructuredChangeValue value={workItem.payload.specialistResults} />
                                </Box>
                              )}
                              {workItem.kind === 'design_brief' && (() => {
                                const brief = safeObject(workItem.payload?.brief);
                                const snapshot = safeObject(workItem.payload?.authority_snapshot);
                                const sources = Array.isArray(snapshot.sources) ? snapshot.sources : [];
                                const list = (title, values) => Array.isArray(values) && values.length > 0 ? (
                                  <Box>
                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700">{title}</Text>
                                    <VStack align="stretch" spacing={1} mt={1}>
                                      {values.map((value, index) => <Text key={index} fontSize="xs" color="gray.700">• {value}</Text>)}
                                    </VStack>
                                  </Box>
                                ) : null;
                                return <VStack align="stretch" spacing={4} mt={workItem.summary ? 3 : 0}>
                                  {list('What this must enable', brief.must_enable)}
                                  {list('Critical states', brief.critical_states)}
                                  {brief.smallest_test && <Box><Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700">Smallest credible test</Text><Text fontSize="xs" color="gray.700" mt={1}>{brief.smallest_test}</Text></Box>}
                                  {list('Evidence to return', brief.evidence_to_return)}
                                  <Box>
                                    <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700">Knowledge used</Text>
                                    <VStack align="stretch" spacing={2} mt={1.5}>{sources.map(source => {
                                      const rules = Array.isArray(source.applicable_rules) ? source.applicable_rules : [];
                                      const applied = rules.length > 0;
                                      return <Box key={source.source_id} p={2.5} bg="gray.50" borderRadius="md">
                                        <HStack justify="space-between" align="start" spacing={3}>
                                          <Box minW={0}><Text fontSize="xs" fontWeight="700" color="gray.800">{source.resource_label || source.label || source.reference}</Text><Text fontSize="10px" color="gray.500" mt={0.5}>{humanizeMentalModelValue(source.source_type || source.provenance?.provider)}{source.source_version ? ` · ${source.source_version}` : ''}</Text></Box>
                                          <Badge fontSize="9px" colorScheme={applied ? 'green' : 'orange'} flexShrink={0}>{applied ? 'Applied' : 'Reference only'}</Badge>
                                        </HStack>
                                        {(rules[0]?.rule || source.rule) && <Text fontSize="xs" color="gray.600" mt={2}>{rules[0]?.rule || source.rule}</Text>}
                                      </Box>;
                                    })}</VStack>
                                  </Box>
                                  {list('Source limitations', snapshot.limitations)}
                                  <HStack spacing={3} flexWrap="wrap"><Text fontSize="10px" color="gray.500">Reference: {workItem.payload?.preparation_reference}</Text>{workItem.payload?.playbook?.version && <Text fontSize="10px" color="gray.500">Playbook {workItem.payload.playbook.version}</Text>}<Text fontSize="10px" color="gray.500">Lifecycle: {humanizeMentalModelValue(workItem.payload?.lifecycle_stage)}</Text></HStack>
                                </VStack>;
                              })()}
                              {workItem.kind === 'connector_record' && workItem.payload?.result && (
                                <Box mt={workItem.summary ? 3 : 0}>
                                  <StructuredChangeValue value={workItem.payload.result} />
                                </Box>
                              )}
                              {workItem.kind === 'recommendation_set' && Array.isArray(workItem.payload?.recommendedNextSteps) && (
                                <VStack align="stretch" spacing={2} mt={workItem.summary ? 3 : 0}>
                                  {workItem.payload.recommendedNextSteps.slice(0, 5).map((step, index) => (
                                    <Box key={step?.id || index}>
                                      <Text fontSize="xs" fontWeight="600" color="gray.800">
                                        {step?.action || step?.title || step?.recommendation || `Recommendation ${index + 1}`}
                                      </Text>
                                      {step?.description && (
                                        <Text fontSize="xs" color="gray.600" mt={0.5} noOfLines={2}>
                                          {step.description}
                                        </Text>
                                      )}
                                    </Box>
                                  ))}
                                </VStack>
                              )}
                              {workItem.kind === 'requirement' && (
                                <VStack align="stretch" spacing={2.5} mt={workItem.summary ? 3 : 0}>
                                  {taskParameters.strategic_rationale && (
                                    <Box>
                                      <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700">Why it matters</Text>
                                      <Text fontSize="xs" color="gray.700" mt={1}>{taskParameters.strategic_rationale}</Text>
                                    </Box>
                                  )}
                                  {Array.isArray(taskParameters.acceptance_criteria) && taskParameters.acceptance_criteria.length > 0 && (
                                    <Box>
                                      <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="700">Acceptance criteria</Text>
                                      {taskParameters.acceptance_criteria.map((criterion, index) => (
                                        <Text key={index} fontSize="xs" color="gray.700" mt={1}>• {criterion}</Text>
                                      ))}
                                    </Box>
                                  )}
                                </VStack>
                              )}
                              {workItem.kind === 'decision' && decisionCandidate && (
                                <VStack align="stretch" spacing={2.5} mt={workItem.summary ? 3 : 0}>
                                  {decisionTradeoffText && <Text fontSize="xs" color="gray.700"><b>Trade-off:</b> {decisionTradeoffText}</Text>}
                                  {decisionImpact && <Text fontSize="xs" color="gray.700"><b>Why it matters:</b> {decisionImpact}</Text>}
                                </VStack>
                              )}
                              {workItem.kind === 'decision' && decisionIsPending && decisionRejectOpen && (
                                <Box mt={3}>
                                  <Text fontSize="xs" color="gray.700" mb={2}>
                                    What made you decide against this? Add a short rationale so the decision remains useful later.
                                  </Text>
                                  <Textarea
                                    value={decisionRejectText}
                                    onChange={event => setDecisionRejectText(event.target.value)}
                                    placeholder="Why you're ruling this out."
                                    rows={3}
                                    resize="vertical"
                                    fontSize="sm"
                                    autoFocus
                                  />
                                </Box>
                              )}
                              <HStack mt={3} spacing={2} flexWrap="wrap">
                                {workItem.kind === 'document' && (
                                  <Button
                                    size="xs"
                                    colorScheme="brand"
                                    variant="outline"
                                    leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>open_in_new</span>}
                                    onClick={() => {
                                      const documentId = Number(workItem.primary_action?.target || String(workItem.id).split(':')[1]);
                                      if (!documentId) return;
                                      onCloseDrawer?.();
                                      window.dispatchEvent(new CustomEvent('pabs:openDocument', { detail: { documentId } }));
                                    }}
                                  >
                                    Open document
                                  </Button>
                                )}
                                {workItem.kind === 'decision' && decisionIsPending && decisionRejectOpen && (
                                  <>
                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      isLoading={decisionCapturePendingDecision === 'reject'}
                                      onClick={async () => {
                                        await handleDecisionCaptureDecision('reject', { commit_note: decisionRejectText.trim() || null });
                                        setDecisionRejectOpen(false);
                                      }}
                                    >
                                      Confirm rejection
                                    </Button>
                                    <Button size="xs" variant="ghost" onClick={() => setDecisionRejectOpen(false)}>Cancel</Button>
                                  </>
                                )}
                                {workItem.kind === 'decision' && decisionIsPending && !decisionRejectOpen && (
                                  <>
                                    <Button size="xs" colorScheme="blue" isLoading={decisionCapturePendingDecision === 'commit'} onClick={() => handleDecisionCaptureDecision('commit')}>
                                      I'll do this
                                    </Button>
                                    <Button size="xs" variant="outline" colorScheme="gray" isDisabled={!!decisionCapturePendingDecision} onClick={() => setDecisionRejectOpen(true)}>
                                      I won't do it
                                    </Button>
                                    <Button size="xs" variant="ghost" colorScheme="gray" onClick={() => dispatchDecisionCaptureChatReview(item, decisionCandidate, taskResult, taskParameters)}>
                                      I'm not sure
                                    </Button>
                                  </>
                                )}
                                {workItem.kind === 'requirement' && productRequirementNeedsReview && productRequirementEditMode && (
                                  <>
                                    <Button size="xs" colorScheme="blue" isLoading={productRequirementPendingDecision === 'save_review'} onClick={() => handleProductRequirementReview('save_review')}>
                                      Save
                                    </Button>
                                    <Button size="xs" variant="ghost" onClick={() => {
                                      setProductRequirementDraft(productRequirementDraftFromTask(detail?.task || item));
                                      setProductRequirementEditMode(false);
                                    }}>
                                      Cancel
                                    </Button>
                                  </>
                                )}
                                {workItem.kind === 'requirement' && productRequirementNeedsReview && !productRequirementEditMode && (
                                  <>
                                    <Button size="xs" colorScheme="blue" isLoading={productRequirementPendingDecision === 'approve_handoff'} onClick={() => handleProductRequirementReview('approve_handoff')}>
                                      Approve handoff
                                    </Button>
                                    <Button size="xs" variant="outline" onClick={() => setProductRequirementEditMode(true)}>Edit</Button>
                                    <Button size="xs" variant="ghost" colorScheme="red" onClick={() => handleTaskAction('cancel-task')}>Reject</Button>
                                  </>
                                )}
                              </HStack>
                            </Box>
                          </Box>
                        );
                      })}
                    </VStack>
                    {!presentation?.story?.turns?.length && nowDisplayMode === 'callout' && presentation?.now?.state !== 'needs_action' && (
                      <Box mt={3}>
                        <Text fontSize="10px" color="gray.400" ml="42px" mb={1.5}>
                          Pabs{(detail?.task?.updated_at || item?.updated_at) ? ` · ${formatDateTime(detail?.task?.updated_at || item?.updated_at)}` : ''}
                        </Text>
                        <HStack align="flex-start" spacing={2.5}>
                          <Avatar name="Pabs" size="xs" bg="gray.800" color="white" flexShrink={0} mt={1}/>
                          <Box
                            bg="gray.800"
                            color="white"
                            borderRadius="0px 16px 16px 16px"
                            px={4}
                            py={3}
                            flex={1}
                            borderLeft="4px solid"
                            borderLeftColor={['failed', 'blocked'].includes(presentation.now.state) ? 'red.300' : 'orange.300'}
                          >
                            {presentation.now.title && <Text fontSize="sm" fontWeight="700">{presentation.now.title}</Text>}
                            {presentation.now.message && <Box fontSize="sm" color="gray.100" lineHeight="1.6" mt={presentation.now.title ? 1.5 : 0}><ReactMarkdown components={darkMarkdownComponents}>{presentation.now.message}</ReactMarkdown></Box>}
                            {presentation.now.primary_action?.type === 'resolve_blocker' && canResolveBlocker && (
                              <Button mt={3} size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} isLoading={pendingAction === 'resume'} isDisabled={actionUpdating} onClick={() => handleTaskAction('resume')}>
                                {presentation.now.primary_action.label}
                              </Button>
                            )}
                          </Box>
                        </HStack>
                      </Box>
                    )}
                    {!presentation?.story?.turns?.length && presentation?.now?.state === 'needs_action' && presentation?.now?.display_mode !== 'omit' && !workOwnsInteraction && (
                      <HStack align="flex-start" spacing={2.5} mt={3}>
                        <Avatar name="Pabs" size="xs" bg="gray.800" color="white" flexShrink={0} mt={1}/>
                        <Box bg="gray.800" color="white" borderRadius="0px 16px 16px 16px" px={4} py={3} flex={1}>
                          {presentation.now.title && <Text fontSize="sm" fontWeight="700" color="white">{presentation.now.title}</Text>}
                          {presentation.now.message && <Text fontSize="sm" color="gray.100" lineHeight="1.6" mt={presentation.now.title ? 1.5 : 0}>{presentation.now.message}</Text>}
                          {presentation.now.primary_action?.type === 'reply_in_chat' && <Button mt={3} size="sm" bg="white" color="gray.800" _hover={{bg:'gray.100'}} onClick={() => {
                            prefillJobChat(item, `About job #${item?.id}: `);
                          }}>{presentation.now.primary_action.label}</Button>}
                          {presentation.now.primary_action?.type === 'confirm_choice' && <HStack spacing={2} mt={3} flexWrap="wrap">
                            {(presentation.now.primary_action.options || []).map((option, index) => <Button key={option} size="sm" bg={index === 0 ? 'white' : 'transparent'} color="white" colorScheme={index === 0 ? undefined : 'whiteAlpha'} variant={index === 0 ? 'solid' : 'outline'} sx={index === 0 ? { color: '#1A202C' } : undefined} isLoading={pendingAction === 'resume'} isDisabled={actionUpdating} onClick={() => handleTaskAction('resume', option)}>{option}</Button>)}
                          </HStack>}
                        </Box>
                      </HStack>
                    )}
                  </Box>
                )}

                {taskResult?.assistant_message && !taskResult?.continuation && (
                  <Box bg="gray.900" color="gray.100" p={4} borderRadius="xl">
                    <Text whiteSpace="pre-wrap" lineHeight="1.6">{presentation?.response?.message || taskResult.assistant_message}</Text>
                  </Box>
                )}
                <Box ref={latestRef} data-job-conversation-end h="1px" aria-hidden="true"/>
                </Box>

                {presentation?.linked_work?.length > 0 && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Related work
                    </Text>
                    <VStack align="stretch" spacing={0} borderTop="1px solid" borderColor="gray.100">
                      {presentation.linked_work.map(linked => (
                        <Flex key={`${linked.record_type || 'task'}:${linked.id}`} as="button" type="button" w="full" minH="44px" py={2.5} borderBottom="1px solid" borderColor="gray.100" align="center" textAlign="left" transition="background-color 140ms ease" _hover={{ bg: 'gray.50' }} _focusVisible={{ bg: 'gray.50', boxShadow: 'inset 0 0 0 2px var(--chakra-colors-gray-300)' }} onClick={() => onOpenLinked?.(linked.id, linked.record_type || 'task')}>
                          <Box minW={0} flex={1}>
                            <Text fontSize="xs" fontWeight="600" color="gray.700" noOfLines={1}>{linked.title}</Text>
                            <Text fontSize="10px" color="gray.500" mt={0.5}>{linked.kind === 'issue' ? 'Issue' : 'Job'} · {linked.status_label}</Text>
                            {linked.relationship_label && <Text fontSize="10px" color="gray.400" mt={0.5}>{linked.relationship_label}</Text>}
                          </Box>
                          {formatDateTime(linked.completed_at || linked.created_at) && <Text fontSize="10px" color="gray.400" ml={3} flexShrink={0} whiteSpace="nowrap">{formatDateTime(linked.completed_at || linked.created_at)}</Text>}
                          <ChevronRightIcon color="gray.300" ml={2} flexShrink={0}/>
                        </Flex>
                      ))}
                    </VStack>
                  </Box>
                )}

                {showNotificationsProminently && !isPulseRoutine && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Notifications
                    </Text>
                    <HStack border="1px solid" borderColor="gray.200" borderRadius="lg" px={3} py={3} justify="space-between">
                      <HStack spacing={2.5}>
                        <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#4A5568' }}>mail</span>
                        <Box>
                          <Text fontSize="sm" fontWeight="500" color="gray.700">Email run summaries</Text>
                          <Text fontSize="xs" color="gray.500">Push notifications remain available in Pabs.</Text>
                        </Box>
                      </HStack>
                      <Switch
                        isChecked={emailEnabled}
                        onChange={event => handleEmailToggle(event.target.checked)}
                        colorScheme="blue"
                        size="sm"
                      />
                    </HStack>
                  </Box>
                )}

                <Box borderTop="1px solid" borderColor="gray.100" pt={3}>
                  <Button
                    variant="ghost"
                    size="sm"
                    px={0}
                    color="gray.600"
                    fontWeight="500"
                    rightIcon={fullDetailsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setFullDetailsOpen(open => !open)}
                    _hover={{ bg: 'transparent', color: 'gray.900' }}
                  >
                    {fullDetailsOpen ? 'Hide details' : 'Details'}
                  </Button>
                </Box>
              </VStack>
            )}

            {/* Legacy fallback for records that predate the canonical projection. */}
            {(isWorkItem || isPulse) && !presentation && (
              <VStack ref={conversationRef} align="stretch" spacing={3}>
                {!isGithubChangeRequest && !isDecisionCapture && !isProductRequirementIssue && (
                <Box>
                  <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                    {isProductRequirementIssue ? 'What needs to be built' : 'Objective'}
                  </Text>
                  <Text fontSize="sm" color="gray.800" lineHeight="1.6">
                    {objectiveText}
                  </Text>
                </Box>
                )}

                {isProductRequirementIssue && (
                  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" px={3} py={3}>
                    <VStack align="stretch" spacing={3}>
                      <HStack spacing={2} flexWrap="wrap">
                        <Badge colorScheme="blue" variant="subtle" fontSize="10px" borderRadius="full" px={2}>
                          {humanizeMentalModelValue(taskParameters.requirement_type || 'feature')}
                        </Badge>
                        <Badge colorScheme="gray" variant="subtle" fontSize="10px" borderRadius="full" px={2}>
                          {productRequirementRouteLabel(taskParameters.routing_target)}
                        </Badge>
                      </HStack>

                      {productRequirementNeedsReview && !productRequirementEditMode && (
                        <Text fontSize="xs" color="gray.500" lineHeight="1.5">
                          Review this requirement before handoff. Edit only if the requirement is not quite right.
                        </Text>
                      )}

                      <Box>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                          What needs to be built
                        </Text>
                        {productRequirementEditMode ? (
                          <Textarea
                            value={productRequirementDraft.requirement || ''}
                            onChange={(event) => setProductRequirementDraft(previous => ({ ...previous, requirement: event.target.value }))}
                            rows={5}
                            resize="vertical"
                            bg="gray.50"
                            fontSize="sm"
                            lineHeight="1.55"
                            className="editable-field-textarea"
                          />
                        ) : (
                          <Text fontSize="sm" color="gray.800" lineHeight="1.6">
                            {taskParameters.requirement || item.reasoning}
                          </Text>
                        )}
                      </Box>

                      {(taskParameters.strategic_rationale || productRequirementEditMode) && (
                        <Box>
                          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1}>
                            Why it matters
                          </Text>
                          {productRequirementEditMode ? (
                            <Textarea
                              value={productRequirementDraft.strategic_rationale || ''}
                              onChange={(event) => setProductRequirementDraft(previous => ({ ...previous, strategic_rationale: event.target.value }))}
                              rows={3}
                              resize="vertical"
                              bg="gray.50"
                              fontSize="sm"
                              lineHeight="1.55"
                              className="editable-field-textarea"
                            />
                          ) : (
                            <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                              {taskParameters.strategic_rationale}
                            </Text>
                          )}
                        </Box>
                      )}

                      {(taskParameters.expected_outcome || productRequirementEditMode) && (
                        <Box>
                          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1}>
                            Expected outcome
                          </Text>
                          {productRequirementEditMode ? (
                            <Textarea
                              value={productRequirementDraft.expected_outcome || ''}
                              onChange={(event) => setProductRequirementDraft(previous => ({ ...previous, expected_outcome: event.target.value }))}
                              rows={2}
                              resize="vertical"
                              bg="gray.50"
                              fontSize="sm"
                              lineHeight="1.55"
                              className="editable-field-textarea"
                            />
                          ) : (
                            <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                              {taskParameters.expected_outcome}
                            </Text>
                          )}
                        </Box>
                      )}

                      {((Array.isArray(taskParameters.acceptance_criteria) && taskParameters.acceptance_criteria.length > 0) || productRequirementEditMode) && (
                        <Box>
                          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                            Acceptance criteria
                          </Text>
                          {productRequirementEditMode ? (
                            <Textarea
                              value={productRequirementDraft.acceptance_criteria || ''}
                              onChange={(event) => setProductRequirementDraft(previous => ({ ...previous, acceptance_criteria: event.target.value }))}
                              rows={5}
                              resize="vertical"
                              bg="gray.50"
                              fontSize="sm"
                              lineHeight="1.55"
                              className="editable-field-textarea"
                            />
                          ) : (
                            <VStack align="stretch" spacing={1.5}>
                              {taskParameters.acceptance_criteria.map((criterion, index) => (
                                <HStack key={`${criterion}-${index}`} align="flex-start" spacing={2}>
                                  <Box w="5px" h="5px" mt="7px" borderRadius="full" bg="gray.400" flexShrink={0} />
                                  <Text fontSize="xs" color="gray.700" lineHeight="1.55">{criterion}</Text>
                                </HStack>
                              ))}
                            </VStack>
                          )}
                        </Box>
                      )}

                      {(taskParameters.evidence_required || productRequirementEditMode) && (
                        <Box>
                          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1}>
                            Evidence required
                          </Text>
                          {productRequirementEditMode ? (
                            <Textarea
                              value={productRequirementDraft.evidence_required || ''}
                              onChange={(event) => setProductRequirementDraft(previous => ({ ...previous, evidence_required: event.target.value }))}
                              rows={2}
                              resize="vertical"
                              bg="gray.50"
                              fontSize="sm"
                              lineHeight="1.55"
                              className="editable-field-textarea"
                            />
                          ) : (
                            <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                              {taskParameters.evidence_required}
                            </Text>
                          )}
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}

                {isSuggestedWork && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      PM lifecycle fit
                    </Text>
                    <HStack spacing={2} align="center">
                      <Badge colorScheme="blue" variant="subtle" fontSize="10px" borderRadius="full" px={2}>
                        {suggestedPurpose}
                      </Badge>
                      <Text fontSize="xs" color="gray.600" lineHeight="1.4">
                        {suggestedJobPurposeReason(suggestedPurpose)}
                      </Text>
                    </HStack>
                  </Box>
                )}

                {isGithubChangeRequest && (
                  <GithubReviewCard
                    result={githubCardResult}
                    onDecision={handleGithubChangeDecision}
                    isUpdating={githubPendingDecision}
                    reviewMode={githubReviewMode}
                    setReviewMode={setGithubReviewMode}
                    feedback={githubReviewFeedback}
                    setFeedback={setGithubReviewFeedback}
                  />
                )}

                {isDecisionCapture && decisionCandidate && (
                  <Box borderTop="1px solid" borderBottom="1px solid" borderColor="gray.100" py={4}>
                    <VStack align="stretch" spacing={3}>
                      <Box alignSelf="flex-end" maxW="86%">
                        <Text fontSize="xs" color="gray.500" fontWeight="500" mb={1} textAlign="right">
                          {decisionSource}
                        </Text>
                        <Box bg="#E3F2FD" color="gray.800" borderRadius="16px 0px 16px 16px" px={3} py={2.5}>
                          <Text fontSize="sm" lineHeight="1.5">
                            {decisionUserSaid || decisionSummary}
                          </Text>
                        </Box>
                      </Box>

                      <HStack align="flex-start" spacing={2.5} maxW="96%">
                        <Avatar
                          name="Pabs"
                          size="xs"
                          bg="gray.800"
                          color="white"
                          flexShrink={0}
                          mt={1}
                        />
                        <Box bg="gray.50" color="gray.800" border="1px solid" borderColor="gray.100" borderRadius="0px 16px 16px 16px" px={3} py={2.5} flex={1}>
                          <Text fontSize="sm" lineHeight="1.55">
                            {decisionReadParts.lead && (
                              <Text as="span" fontWeight="700" color="gray.900">
                                {decisionReadParts.lead}
                              </Text>
                            )}
                            {decisionReadParts.rest && ` ${decisionReadParts.rest}`}
                          </Text>

                          {decisionImpact && (
                            <Text fontSize="sm" lineHeight="1.55" mt={2}>
                              <Text as="span" fontWeight="600" color="gray.900">Why it matters: </Text>
                              {decisionImpact}
                            </Text>
                          )}

                          {decisionTradeoffText && (
                            <Text fontSize="sm" lineHeight="1.55" mt={2}>
                              <Text as="span" fontWeight="600" color="gray.900">Trade-off: </Text>
                              {decisionTradeoffText}
                            </Text>
                          )}

                          {decisionWatch && decisionStatus !== 'rejected' && decisionStatus !== 'abandoned' && (
                            <Text fontSize="sm" lineHeight="1.55" mt={2}>
                              <Text as="span" fontWeight="600" color="gray.900">
                                {decisionStatus === 'committed' ? "What I'm watching: " : "If you go ahead, what I'll watch: "}
                              </Text>
                              {`${decisionWatch.charAt(0).toLowerCase()}${decisionWatch.slice(1)}`}. That's the signal I'll track to tell you later whether this call is working.
                            </Text>
                          )}

                          {decisionConfidence && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                              {decisionConfidence}.
                            </Text>
                          )}
                        </Box>
                      </HStack>

                      <Box pt={1}>
                        <Text fontSize="sm" color={decisionTheme.head} lineHeight="1.55">
                          <Text as="span" fontWeight="600">Your call: </Text>
                          {decisionIsPending
                            ? (decisionStatus === 'deferred'
                                ? "you set this aside for now. It stays open as a blocker until you make the call."
                                : "nothing is locked until you say so.")
                            : decisionStatus === 'committed'
                              ? "you committed to build this. I'll compare what happens next against it."
                              : decisionStatus === 'rejected'
                                ? "you decided not to build this. I'll keep it as a decision, so it's clear what you ruled out and why."
                                : "you closed this. I'll keep the trail, but it won't count as a decision."}
                        </Text>
                      </Box>

                      {decisionIsPending && !decisionRejectOpen && (
                        <HStack spacing={2} flexWrap="wrap" align="center">
                          <Tooltip
                            label="Records that you're committing to build this. Pabs doesn't build it for you. You execute in your tool, and Pabs tracks whether the work matched this call."
                            hasArrow
                            placement="top"
                          >
                            <Button
                              size="sm"
                              px={3}
                              bg="gray.900"
                              color="white"
                              _hover={{ bg: 'gray.700' }}
                              flexShrink={0}
                              fontWeight="600"
                              isLoading={decisionCapturePendingDecision === 'commit'}
                              isDisabled={!!decisionCapturePendingDecision}
                              onClick={() => handleDecisionCaptureDecision('commit')}
                            >
                              I'll do this
                            </Button>
                          </Tooltip>
                          <Tooltip
                            label="Records that you're deciding NOT to build this. It counts as a real decision and keeps your rationale, so it's clear what you ruled out and why."
                            hasArrow
                            placement="top"
                          >
                            <Button
                              size="sm"
                              px={3}
                              variant="outline"
                              colorScheme="gray"
                              flexShrink={0}
                              fontWeight="600"
                              isDisabled={!!decisionCapturePendingDecision}
                              onClick={() => {
                                setDecisionRejectText('');
                                setDecisionRejectOpen(true);
                              }}
                            >
                              I won't do it
                            </Button>
                          </Tooltip>
                          <Tooltip
                            label="Not ready to call it. Opens the chat so you can talk it through with Pabs. The decision stays open."
                            hasArrow
                            placement="top"
                          >
                            <Button
                              size="sm"
                              px={3}
                              variant="ghost"
                              colorScheme="gray"
                              flexShrink={0}
                              fontWeight="600"
                              isDisabled={!!decisionCapturePendingDecision}
                              onClick={() => dispatchDecisionCaptureChatReview(
                                item,
                                decisionCandidate,
                                taskResult,
                                taskParameters
                              )}
                            >
                              I'm not sure
                            </Button>
                          </Tooltip>
                        </HStack>
                      )}

                      {decisionIsPending && decisionRejectOpen && (
                        <Box>
                          <Text fontSize="sm" color="gray.700" lineHeight="1.5" mb={2}>
                            What made you decide against this? A line of rationale keeps it useful later. Optional, but it's the part future-you will want.
                          </Text>
                          <Textarea
                            value={decisionRejectText}
                            onChange={(e) => setDecisionRejectText(e.target.value)}
                            placeholder="Why you're ruling this out."
                            variant="filled"
                            bg="gray.50"
                            color="gray.800"
                            fontSize="sm"
                            rows={3}
                            resize="vertical"
                            className="editable-field-textarea"
                            _focus={{ borderColor: "gray.900", boxShadow: "none", bg: "white" }}
                            autoFocus
                          />
                          <HStack spacing={2} mt={2}>
                            <Button
                              size="sm"
                              px={3}
                              bg="red.600"
                              color="white"
                              _hover={{ bg: 'red.700' }}
                              fontWeight="600"
                              isLoading={decisionCapturePendingDecision === 'reject'}
                              isDisabled={!!decisionCapturePendingDecision}
                              onClick={async () => {
                                await handleDecisionCaptureDecision('reject', { commit_note: decisionRejectText.trim() || null });
                                setDecisionRejectOpen(false);
                              }}
                            >
                              I won't do it
                            </Button>
                            <Button
                              size="sm"
                              px={3}
                              variant="ghost"
                              colorScheme="gray"
                              fontWeight="600"
                              isDisabled={!!decisionCapturePendingDecision}
                              onClick={() => setDecisionRejectOpen(false)}
                            >
                              Cancel
                            </Button>
                          </HStack>
                        </Box>
                      )}
                    </VStack>
                  </Box>
                )}

                {!isCompleted && !isWaitingForInput && isPulseRoutine && !presentation?.details?.request?.message && (
                  <Box borderTop="1px solid" borderColor="gray.100" pt={3}>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      What Pabs will do
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {[
                        'Check your strategy and recent activity for anything that has meaningfully shifted.',
                        'Decide whether to generate a full pulse or skip this run. If nothing important has changed, it skips quietly.',
                        'If warranted, produce a full strategy pulse with analysis, recommendations, and next steps.',
                        'Send you an email summary if email is enabled for this plan.',
                      ].map((line, index) => (
                        <HStack key={index} align="flex-start" spacing={2.5}>
                          <Text fontSize="10px" fontWeight="700" color="gray.400" lineHeight="1.9" minW="18px">
                            {String(index + 1).padStart(2, '0')}
                          </Text>
                          <Text fontSize="sm" color="gray.700" lineHeight="1.6">{line}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                {!isCompleted && !isWaitingForInput && !isPulseRoutine && !presentation?.details?.request?.message && plannedActions.length > 0 && (
                  <Box borderTop="1px solid" borderColor="gray.100" pt={3}>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      What Pabs will do
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {plannedActions.map((action, index) => (
                        <HStack key={`${action}-${index}`} align="flex-start" spacing={2.5}>
                          <Text fontSize="10px" fontWeight="700" color="gray.400" lineHeight="1.9" minW="18px">
                            {String(index + 1).padStart(2, '0')}
                          </Text>
                          <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                            {action}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                {!isCompleted && !isWaitingForInput && (scheduleDisplayText || cadenceText || canEditCadence || isSuggestedWork) && (
                  <HStack justify="space-between" spacing={3} pt={1}>
                    <HStack spacing={2} flexShrink={0}>
                      <span className="material-icons-outlined" style={{ fontSize: '16px', color: '#718096' }}>
                        schedule
                      </span>
                      <Text fontSize="xs" fontWeight="600" color="gray.600">
                        {isSuggestedWork ? 'Status' : 'Scheduled'}
                      </Text>
                    </HStack>
                    <HStack spacing={2} justify="flex-end">
                      {(scheduleDisplayText || !canEditCadence) && (
                        <Text fontSize="xs" color="gray.500" textAlign="right">
                          {scheduleDisplayText || 'Waiting to be scheduled'}
                          {!canEditCadence && cadenceText ? ` · ${cadenceText}` : ''}
                        </Text>
                      )}
                      {canEditCadence && (
                        <Select
                          size="sm"
                          w="130px"
                          value={cadenceValue}
                          onChange={event => handleCadenceChange(event.target.value)}
                          isDisabled={cadenceUpdating}
                          fontSize="xs"
                          borderColor="gray.200"
                        >
                          {isPulseRoutine
                            ? <option value="paused">Paused</option>
                            : <option value="once">Once</option>
                          }
                          <option value="daily">Daily</option>
                          <option value="weekdays">Weekdays</option>
                          <option value="weekly">Weekly</option>
                          <option value="monthly">Monthly</option>
                        </Select>
                      )}
                    </HStack>
                  </HStack>
                )}

                {/* This generic "reply in chat" box predates resolve_blocker/resume having
                    their own correct primary action up in the main callout. For blocked and
                    waiting_for_credits, showing both meant two boxes telling different stories —
                    one offering to resume, the other offering to "reply in chat" about a
                    decision that isn't actually needed. Excluded here; still shown for
                    waiting_for_user/approval/evidence, which don't have their own complete
                    top-callout action for every case yet. */}
                {hasMissionOutcome && isWaitingForInput && !isDesignRemediation && !['blocked', 'waiting_for_credits'].includes(effectiveStatus) && (
                  <Box border="1px solid" borderColor="orange.200" bg="orange.50" borderRadius="lg" px={3} py={3}>
                    <HStack align="flex-start" spacing={2.5}>
                      <Box
                        w="22px"
                        h="22px"
                        borderRadius="full"
                        bg="orange.100"
                        color="orange.700"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        mt="1px"
                      >
                        <span className="material-icons-outlined" style={{ fontSize: '14px' }}>reply</span>
                      </Box>
                      <Box flex={1} minW={0}>
                        <Text fontSize="10px" fontWeight="700" color="orange.700" textTransform="uppercase" letterSpacing="wider" mb={1}>
                          What Pabs needs from you
                        </Text>
                        <Text fontSize="sm" color="gray.800" lineHeight="1.6">
                          {needsUserText || 'Reply in chat with your decision or the extra context Pabs should use to continue this job.'}
                        </Text>
                        <HStack mt={3} spacing={2}>
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="orange"
                            onClick={() => {
                              prefillJobChat(item, `About job #${item?.id}: `);
                            }}
                          >
                            Reply in chat
                          </Button>
                          {isMission && presentation?.work?.length > 0 && (
                            <Button
                              size="xs"
                              colorScheme="orange"
                              isLoading={actionUpdating}
                              onClick={() => handleTaskAction('acknowledge')}
                            >
                              Approve, no changes
                            </Button>
                          )}
                          {canPauseMission && (
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="gray"
                              isLoading={actionUpdating}
                              onClick={() => handleTaskAction('pause')}
                            >
                              Pause job
                            </Button>
                          )}
                        </HStack>
                      </Box>
                    </HStack>
                  </Box>
                )}

                {hasMissionOutcome && missionResultOutput && (
                  <Box border="1px solid" borderColor="blue.200" bg="blue.50" borderRadius="lg" px={3} py={3}>
                    <HStack align="flex-start" spacing={2.5}>
                      <Box
                        w="22px"
                        h="22px"
                        borderRadius="full"
                        bg="blue.100"
                        color="blue.700"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <span className="material-icons-outlined" style={{ fontSize: '14px' }}>inventory_2</span>
                      </Box>
                      <Box flex={1} minW={0}>
                        <Text fontSize="10px" fontWeight="700" color="blue.700" textTransform="uppercase" letterSpacing="wider">
                          What Pabs produced
                        </Text>
                        <Text fontSize="sm" color="gray.800" mt={1.5} lineHeight="1.6">
                          {missionResultOutput.summary || missionResultOutput.title || 'Mission result'}
                        </Text>
                        <Badge mt={2} fontSize="9px" colorScheme={missionResultOutput.status === 'final' ? 'green' : 'orange'}>
                          {missionResultOutput.status === 'final' ? 'Final output' : 'Draft awaiting completion'}
                        </Badge>
                      </Box>
                    </HStack>
                  </Box>
                )}

                {hasMissionOutcome && (
                  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" px={3} py={3}>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      Pabs found
                    </Text>
                    <Box fontSize="sm" color="gray.800" lineHeight="1.65" mb={materialFindings.length ? 2.5 : 0}>
                      {(taskResult.summary || outcomeText) ? (
                        <ReactMarkdown components={markdownComponents}>
                          {taskResult.summary || outcomeText}
                        </ReactMarkdown>
                      ) : (
                        <Text fontSize="sm" color="gray.800">
                          {isWaitingForInput
                            ? 'The mission ran and is waiting for your response.'
                            : 'The mission completed and returned its findings.'}
                        </Text>
                      )}
                    </Box>
                    {materialFindings.length > 0 && (
                      <VStack align="stretch" spacing={2}>
                        {materialFindings.map((finding, index) => (
                          <HStack key={`${finding}-${index}`} align="flex-start" spacing={2}>
                            <Box w="5px" h="5px" mt="7px" borderRadius="full" bg="gray.400" flexShrink={0} />
                            <Text fontSize="xs" color="gray.700" lineHeight="1.55">{finding}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                    {taskResult.next_trigger && (
                      <HStack mt={3} pt={2.5} borderTop="1px solid" borderColor="gray.100" align="flex-start">
                        <span className="material-icons-outlined" style={{ fontSize: '15px', color: '#718096' }}>update</span>
                        <Text fontSize="xs" color="gray.600">{taskResult.next_trigger}</Text>
                      </HStack>
                    )}
                  </Box>
                )}

                {hasMissionOutcome && deliveryOutputs.length > 0 && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Delivery history
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {deliveryOutputs.map(output => (
                        <HStack
                          key={output.id}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          px={3}
                          py={2.5}
                          align="flex-start"
                        >
                          <span className="material-icons-outlined" style={{ fontSize: '16px', color: '#718096' }}>
                            {output.output_kind.startsWith('email') ? 'mail' : 'chat'}
                          </span>
                          <Box flex={1} minW={0}>
                            <Text fontSize="xs" fontWeight="600" color="gray.700">
                              {output.output_kind === 'chat_delivery' ? 'Sent to plan chat' : output.title || 'Email notification'}
                            </Text>
                            {output.delivery_ref && (
                              <Text fontSize="10px" color="gray.500" mt={0.5}>{output.delivery_ref}</Text>
                            )}
                          </Box>
                          <Badge fontSize="9px" colorScheme={output.status === 'delivered' ? 'green' : 'gray'}>
                            {output.status}
                          </Badge>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}

                {canConfigureNotifications && (
                  <Box borderTop="1px solid" borderColor="gray.100" pt={3}>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={3}>
                      Notifications
                    </Text>
                    <VStack align="stretch" spacing={0} border="1px solid" borderColor="gray.100" borderRadius="lg" overflow="hidden">
                      <HStack px={3} py={3} justify="space-between" bg="gray.50">
                        <HStack spacing={2.5}>
                          <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#4A5568' }}>notifications</span>
                          <VStack align="flex-start" spacing={0}>
                            <Text fontSize="sm" fontWeight="500" color="gray.700">Push notification</Text>
                            <Text fontSize="xs" color="gray.500">Sent to the Pabs web app</Text>
                          </VStack>
                        </HStack>
                        <Badge colorScheme="blue" fontSize="9px" px={2} py={0.5} borderRadius="full">Always on</Badge>
                      </HStack>
                      <Divider borderColor="gray.100" />
                      <HStack px={3} py={3} justify="space-between">
                        <HStack spacing={2.5}>
                          <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#4A5568' }}>mail</span>
                          <VStack align="flex-start" spacing={0}>
                            <Text fontSize="sm" fontWeight="500" color="gray.700">Email</Text>
                            <Text fontSize="xs" color="gray.500">
                              {isMission ? 'Send each run summary to your account email' : 'Sent to your account email'}
                            </Text>
                          </VStack>
                        </HStack>
                        <Switch
                          isChecked={emailEnabled}
                          onChange={e => handleEmailToggle(e.target.checked)}
                          colorScheme="blue"
                          size="sm"
                        />
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {isCompleted && emailResult && (
                  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                    <HStack px={3} py={2.5} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2}>
                      <span className="material-icons-outlined" style={{ fontSize: '16px', color: '#4A5568' }}>mail</span>
                      <Text fontSize="sm" fontWeight="600" color="gray.800">Email sent</Text>
                      <Badge colorScheme="green" fontSize="9px" ml="auto">Delivered</Badge>
                    </HStack>
                    <Box px={3} py={3}>
                      <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wider">To</Text>
                      <Text fontSize="xs" color="gray.700" mb={2}>{emailResult.recipient}</Text>
                      <Text fontSize="10px" color="gray.400" textTransform="uppercase" letterSpacing="wider">Subject</Text>
                      <Text fontSize="sm" color="gray.800" fontWeight="600">{emailResult.subject || 'Email from Pabs'}</Text>
                    </Box>
                    <Box borderTop="1px solid" borderColor="gray.100" bg="white">
                      {emailResult.body ? (
                        <iframe
                          srcDoc={emailResult.body}
                          title="Email preview"
                          sandbox="allow-same-origin"
                          style={{ width: '100%', border: 'none', minHeight: '220px', display: 'block' }}
                          onLoad={e => {
                            const doc = e.target.contentDocument;
                            if (doc) e.target.style.height = doc.documentElement.scrollHeight + 'px';
                          }}
                        />
                      ) : (
                        <Text px={3} py={3} fontSize="xs" color="gray.400" fontStyle="italic">Email body was not stored.</Text>
                      )}
                    </Box>
                    {emailResult.sent_at && (
                      <Text px={3} py={2} fontSize="10px" color="gray.400" borderTop="1px solid" borderColor="gray.100">
                        Sent {formatDateTime(emailResult.sent_at)}
                      </Text>
                    )}
                  </Box>
                )}

                {isCompleted && pulseResult && (
                  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                    <HStack px={3} py={2.5} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2}>
                      <span className="material-icons-outlined" style={{ fontSize: '16px', color: '#4A5568' }}>radar</span>
                      <Text fontSize="sm" fontWeight="600" color="gray.800">Strategy pulse</Text>
                      <Badge colorScheme="green" fontSize="9px" ml="auto">Created</Badge>
                    </HStack>
                    {pulseContent?.content && (
                      <Text px={3} py={3} fontSize="xs" color="gray.700" lineHeight="1.7" whiteSpace="pre-wrap">
                        {pulseContent.content}
                      </Text>
                    )}
                    <HStack px={3} py={2} borderTop="1px solid" borderColor="gray.100" justify="space-between">
                      <Text fontSize="10px" color="gray.400">
                        {pulseResult.is_nudge ? 'Nudge' : 'Full pulse'}
                      </Text>
                      <Text fontSize="10px" color={pulseResult.sent ? 'green.600' : 'gray.400'}>
                        {pulseResult.sent ? 'Emailed' : 'Saved in Pabs'}
                      </Text>
                    </HStack>
                  </Box>
                )}

                {hasMissionOutcome && (
                  <>
                    {completedMissionSteps.length > 0 && (
                      <Box>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                          What Pabs did
                        </Text>
                        <VStack align="stretch" spacing={1.5}>
                          {completedMissionSteps.map((step, index) => (
                            <HStack key={step.id || `${step.sequence}-${index}`} align="flex-start" spacing={2.5}>
                              <Box
                                mt="2px"
                                w="18px"
                                h="18px"
                                borderRadius="full"
                                bg="green.50"
                                color="green.600"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                flexShrink={0}
                              >
                                <span className="material-icons-outlined" style={{ fontSize: '12px' }}>
                                  check
                                </span>
                              </Box>
                              <Box flex={1}>
                                <Text fontSize="xs" color="gray.700" lineHeight="1.5">
                                  {step.name || step.tool_name || 'Mission step'}
                                </Text>
                              </Box>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    )}

                    {missionDocumentArtifacts.map((artifact, index) => (
                      <Box key={artifact.id || index} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <HStack px={3} py={2.5} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2} align="flex-start">
                          <span className="material-icons-outlined" style={{ fontSize: '16px', lineHeight: 1.35, color: '#718096', flexShrink: 0 }}>description</span>
                          <Text fontSize="sm" fontWeight="600" color="gray.700" noOfLines={1} flex={1} minW={0}>
                            {String(artifact.document_type || 'Document')
                              .replace(/_/g, ' ')
                              .replace(/\b\w/g, character => character.toUpperCase())}
                          </Text>
                          <Badge colorScheme="green" fontSize="9px" flexShrink={0}>Created</Badge>
                        </HStack>
                        <Box px={3} py={3}>
                          <Text fontSize="sm" fontWeight="600" color="gray.800" lineHeight="1.5">
                            {artifact.title || 'Generated document'}
                          </Text>
                          <Text fontSize="xs" color="gray.500" mt={1.5}>
                            Pabs created this document as part of the mission.
                          </Text>
                          <Button
                            mt={3}
                            size="xs"
                            colorScheme="brand"
                            variant="outline"
                            leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>open_in_new</span>}
                            onClick={() => {
                              const documentId = Number(artifact.id);
                              if (!documentId) return;
                              onCloseDrawer?.();
                              window.dispatchEvent(new CustomEvent('pabs:openDocument', { detail: { documentId } }));
                            }}
                          >
                            Open document
                          </Button>
                        </Box>
                      </Box>
                    ))}

                    {resultSources.length > 0 && (
                      <Box>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                          Sources
                        </Text>
                        <VStack align="stretch" spacing={1}>
                          {resultSources.slice(0, 4).map(source => (
                            <Box
                              key={source.url}
                              as="a"
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              fontSize="xs"
                              color="blue.500"
                              textDecoration="underline"
                              noOfLines={1}
                            >
                              {source.title || source.url}
                            </Box>
                          ))}
                          {resultSources.length > 4 && (
                            <Text fontSize="10px" color="gray.400">+ {resultSources.length - 4} more in full detail</Text>
                          )}
                        </VStack>
                      </Box>
                    )}
                  </>
                )}

                {isCompleted && !emailResult && !pulseResult && !isMission && !documentArtifact && (
                  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" px={3} py={3}>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      Result
                    </Text>
                    <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                      {outcomeText || 'This task completed successfully.'}
                    </Text>
                  </Box>
                )}

                {isCompleted && documentArtifact && !presentation?.work?.some(work => work.kind === 'document') && (
                  <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                    <HStack px={3} py={2.5} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2} align="flex-start">
                      <span className="material-icons-outlined" style={{ fontSize: '16px', lineHeight: 1.35, color: '#718096', flexShrink: 0 }}>description</span>
                      <Text fontSize="sm" fontWeight="600" color="gray.700" noOfLines={1} flex={1} minW={0}>
                        {String(documentArtifact.document_type || 'Document')
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, character => character.toUpperCase())}
                      </Text>
                      <Badge colorScheme="green" fontSize="9px" flexShrink={0}>Created</Badge>
                    </HStack>
                    <Box px={3} py={3}>
                      <Text fontSize="sm" fontWeight="600" color="gray.800" lineHeight="1.5" mb={documentPreview ? 1.5 : 0}>
                        {documentArtifact.title || 'Generated document'}
                      </Text>
                      {documentPreview ? (
                        <Text fontSize="xs" color="gray.600" lineHeight="1.6" noOfLines={3}>
                          {documentPreview}
                        </Text>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          {documentSources.length > 0
                            ? `Created using ${documentSources.join(', ')}.`
                            : 'The requested document was created and saved to this plan.'}
                        </Text>
                      )}
                      <Button
                        mt={3}
                        size="xs"
                        colorScheme="brand"
                        variant="outline"
                        leftIcon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>open_in_new</span>}
                        onClick={() => {
                          const documentId = Number(documentArtifact.id);
                          if (!documentId) return;
                          onCloseDrawer?.();
                          window.dispatchEvent(new CustomEvent('pabs:openDocument', { detail: { documentId } }));
                        }}
                      >
                        Open document
                      </Button>
                    </Box>
                  </Box>
                )}

                <Box ref={latestRef} data-job-conversation-end h="1px" aria-hidden="true"/>
                <Button
                  variant="ghost"
                  size="sm"
                  alignSelf="flex-start"
                  px={0}
                  color="gray.600"
                  fontWeight="500"
                  rightIcon={fullDetailsOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  onClick={() => setFullDetailsOpen(open => !open)}
                  _hover={{ bg: 'transparent', color: 'gray.900' }}
                >
                  {fullDetailsOpen ? 'Hide full detail' : 'View full detail'}
                </Button>
              </VStack>
            )}

            <Collapse in={presentation ? fullDetailsOpen : (!isTask || fullDetailsOpen)} animateOpacity style={{ width: '100%' }}>
              <VStack align="stretch" spacing={5}>
            {presentation && (
              <VStack align="stretch" spacing={4}>
                {presentation.activity?.length > 0 && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>Activity</Text>
                    <VStack align="stretch" spacing={1.5}>
                      {[...presentation.activity].reverse().map(activity => {
                        const activityStatus = activity.type.startsWith('step_') ? activity.type.slice(5) : activity.type;
                        const isFailed = activityStatus === 'failed';
                        const isWaiting = activityStatus === 'waiting' || activityStatus.startsWith('waiting_for') || activityStatus === 'blocked';
                        return <HStack key={activity.id} as={activity.child_task_id ? 'button' : 'div'} type={activity.child_task_id ? 'button' : undefined} w="full" align="flex-start" spacing={2.5} textAlign="left" borderRadius="md" py={activity.child_task_id ? 1 : 0} px={activity.child_task_id ? 1 : 0} mx={activity.child_task_id ? -1 : 0} transition="background-color 140ms ease" _hover={activity.child_task_id ? { bg: 'gray.50' } : undefined} _focusVisible={activity.child_task_id ? { bg: 'gray.50', boxShadow: '0 0 0 2px var(--chakra-colors-gray-200)' } : undefined} onClick={activity.child_task_id ? () => onOpenLinked?.(activity.child_task_id) : undefined}>
                          <Box mt="2px" w="18px" h="18px" borderRadius="full" bg={isFailed ? 'red.50' : isWaiting ? 'orange.50' : 'green.50'} color={isFailed ? 'red.600' : isWaiting ? 'orange.600' : 'green.600'} display="flex" alignItems="center" justifyContent="center" flexShrink={0}><span className="material-icons-outlined" style={{ fontSize: '12px' }}>{isFailed ? 'close' : isWaiting ? 'more_horiz' : 'check'}</span></Box>
                          <Box flex={1}><Text fontSize="xs" color="gray.700" lineHeight="1.5">{activity.label}</Text>{activity.at && <Text fontSize="10px" color="gray.400">{formatDateTime(activity.at)}</Text>}</Box>
                          {activity.child_task_id && <ChevronRightIcon color="gray.300" mt="2px" flexShrink={0}/>}
                        </HStack>;
                      })}
                    </VStack>
                  </Box>
                )}
                {[['What your AI tool was asked', presentation.details?.handover?.output], ['Useful completion', presentation.details?.handover?.completion], ['Files and sources', presentation.details?.handover?.access]]
                  .filter(([, value]) => value).map(([label, value]) => (
                    <Box key={label}>
                      <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>{label}</Text>
                      <Text fontSize="sm" color="gray.700" lineHeight="1.6" whiteSpace="pre-wrap" overflowWrap="anywhere">{value}</Text>
                    </Box>
                  ))}
                {presentation.details?.objective && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      Objective
                    </Text>
                    <Text fontSize="sm" color="gray.700" lineHeight="1.6">{presentation.details.objective}</Text>
                  </Box>
                )}
                {(presentation.details?.schedule?.scheduled_at || presentation.details?.schedule?.next_run_at) && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      Schedule
                    </Text>
                    <Text fontSize="xs" color="gray.700">
                      {formatDateTime(presentation.details.schedule.next_run_at || presentation.details.schedule.scheduled_at)}
                    </Text>
                  </Box>
                )}
                {presentation.details?.notifications?.configurable && !showNotificationsProminently && !isPulseRoutine && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Notifications
                    </Text>
                    <HStack border="1px solid" borderColor="gray.100" borderRadius="lg" px={3} py={3} justify="space-between">
                      <HStack spacing={2.5}>
                        <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#4A5568' }}>mail</span>
                        <Box>
                          <Text fontSize="sm" fontWeight="500" color="gray.700">Email run summaries</Text>
                          <Text fontSize="xs" color="gray.500">Push notifications remain available in Pabs.</Text>
                        </Box>
                      </HStack>
                      <Switch
                        isChecked={emailEnabled}
                        onChange={event => handleEmailToggle(event.target.checked)}
                        colorScheme="blue"
                        size="sm"
                      />
                    </HStack>
                  </Box>
                )}
                {presentation.details?.sources?.length > 0 && (
                  <Box>
                    <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                      Sources
                    </Text>
                    <VStack align="stretch" spacing={1}>
                      {presentation.details.sources.map(source => (
                        <Box
                          key={source.url || source.title}
                          as={source.url ? 'a' : 'div'}
                          href={source.url || undefined}
                          target={source.url ? '_blank' : undefined}
                          rel={source.url ? 'noopener noreferrer' : undefined}
                          fontSize="xs"
                          color={source.url ? 'blue.500' : 'gray.600'}
                          textDecoration={source.url ? 'underline' : 'none'}
                        >
                          {source.title || source.url}
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}
            {isTask && (
              <Box pt={1}>
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                  Audit and technical detail
                </Text>
              </Box>
            )}
            {/* Reasoning — PABS's decision note */}
            {isTask && item.reasoning && !isMission && (
              <Box bg="gray.50" borderRadius="md" px={3} py={3}>
                <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                  {isDecisionCapture ? "Pabs' recommendation" : 'PABS decided'}
                </Text>
                <Text fontSize="sm" color="gray.700" lineHeight="1.6">{item.reasoning}</Text>
              </Box>
            )}

            {/* Output — shown first for completed non-mission tasks (email, pulse) */}
            {isTask && !presentation && taskResult && Object.keys(taskResult).length > 0 && !emailResult && !pulseResult && (() => {
              const r = taskResult;
              const isEmail = !!r.recipient;
              const isPulse = !!r.pulse_id;
              const pulseContent = detail?.artifact?.type === 'pulse' ? detail.artifact.data : null;
              if (!isEmail && !isPulse) return null;

              return (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={3}>
                      Output
                    </Text>

                    {/* Email output */}
                    {isEmail && (
                      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <HStack px={3} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2}>
                          <span className="material-icons-outlined" style={{ fontSize: '15px', color: '#718096' }}>email</span>
                          <Text fontSize="xs" fontWeight="600" color="gray.700">Email</Text>
                          <Badge colorScheme="green" fontSize="9px" ml="auto">Delivered</Badge>
                        </HStack>
                        <Box px={3} py={2}>
                          <Text fontSize="xs" color="gray.400">To</Text>
                          <Text fontSize="xs" color="gray.700" mb={2}>{r.recipient}</Text>
                          <Text fontSize="xs" color="gray.400">Subject</Text>
                          <Text fontSize="xs" color="gray.700" fontWeight="500" mb={2}>{r.subject}</Text>
                          {r.sent_at && (
                            <>
                              <Text fontSize="xs" color="gray.400">Sent</Text>
                              <Text fontSize="xs" color="gray.700">{formatDateTime(r.sent_at)}</Text>
                            </>
                          )}
                        </Box>
                        <Box px={3} py={3} borderTop="1px solid" borderColor="gray.100">
                          <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                            Body
                          </Text>
                          {r.body ? (
                            <Box
                              fontSize="xs" color="gray.600" lineHeight="1.6"
                              maxH="200px" overflowY="auto"
                              dangerouslySetInnerHTML={{ __html: r.body }}
                            />
                          ) : (
                            <Text fontSize="xs" color="gray.400" fontStyle="italic">
                              Body not stored for this task. Available on tasks run after today.
                            </Text>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Pulse output */}
                    {isPulse && (
                      <Box border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
                        <HStack px={3} py={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" spacing={2}>
                          <span className="material-icons-outlined" style={{ fontSize: '15px', color: '#718096' }}>radar</span>
                          <Text fontSize="xs" fontWeight="600" color="gray.700">Strategy pulse</Text>
                          <Badge colorScheme="green" fontSize="9px" ml="auto">Stored</Badge>
                        </HStack>
                        <Box px={3} py={2}>
                          {r.subject && (
                            <>
                              <Text fontSize="xs" color="gray.400">Subject</Text>
                              <Text fontSize="xs" color="gray.700" fontWeight="500" mb={2}>{r.subject}</Text>
                            </>
                          )}
                          <Text fontSize="xs" color="gray.400">Type</Text>
                          <Text fontSize="xs" color="gray.700" mb={2}>{r.is_nudge ? 'Nudge (no new changes)' : 'Full pulse'}</Text>
                          <Text fontSize="xs" color="gray.400">Email</Text>
                          <Text fontSize="xs" color={r.sent ? 'green.600' : 'gray.500'}>{r.sent ? 'Sent' : 'Not sent'}</Text>
                        </Box>
                        {pulseContent?.content && (
                          <Box px={3} py={3} borderTop="1px solid" borderColor="gray.100">
                            <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                              Content
                            </Text>
                            <Text fontSize="xs" color="gray.600" lineHeight="1.6" whiteSpace="pre-wrap" maxH="200px" overflowY="auto">
                              {pulseContent.content}
                            </Text>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              );
            })()}

            {isTask && detail?.task?.capability === 'agent_mission' && detail?.run && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                    Run metadata
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    {(() => {
                      const metadata = safeObject(detail.run.metadata);
                      const workModel = safeObject(metadata.work_model);
                      const purposes = [
                        workModel.primary_purpose,
                        ...(Array.isArray(workModel.supporting_purposes) ? workModel.supporting_purposes : []),
                      ].filter(Boolean);
                      const uniquePurposes = [...new Set(purposes)];
                      const pmThemes = Array.isArray(workModel.pm_themes)
                        ? [...new Set(workModel.pm_themes.filter(Boolean))]
                        : [];
                      if (!uniquePurposes.length && !workModel.working_mode && !pmThemes.length) return null;
                      return (
                        <Box pb={1}>
                          <Text
                            fontSize="10px"
                            fontWeight="700"
                            color="gray.400"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            mb={1.5}
                          >
                            Pabs PM function
                          </Text>
                          <Flex gap={1.5} wrap="wrap">
                            {uniquePurposes.map((purpose, index) => (
                              <Badge
                                key={purpose}
                                fontSize="9px"
                                colorScheme={index === 0 ? 'purple' : 'gray'}
                                variant={index === 0 ? 'solid' : 'subtle'}
                              >
                                {humanizeMentalModelValue(purpose)}
                              </Badge>
                            ))}
                            {workModel.working_mode && (
                              <Badge fontSize="9px" colorScheme="blue" variant="outline">
                                {humanizeMentalModelValue(workModel.working_mode)} mode
                              </Badge>
                            )}
                          </Flex>
                          {pmThemes.length > 0 && (
                            <Text fontSize="10px" color="gray.500" mt={1.5} lineHeight="1.45">
                              {pmThemes.join(' · ')}
                            </Text>
                          )}
                        </Box>
                      );
                    })()}
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.500">Run state</Text>
                      <Badge
                        fontSize="9px"
                        colorScheme={detail.run.status === 'completed' ? 'green' : detail.run.status === 'failed' ? 'red' : 'blue'}
                      >
                        {String(detail.run.status || '').replace(/_/g, ' ')}
                      </Badge>
                    </HStack>
                    {(() => {
                      const result = safeObject(detail.task?.result);
                      const sources = Array.isArray(result.sources)
                        ? result.sources.filter(source => source?.url)
                        : [];
                      const additionalSources = sources.slice(4);
                      if (!additionalSources.length) return null;
                      return (
                        <Box
                          borderWidth="1px"
                          borderColor="gray.200"
                          borderRadius="md"
                          px={3}
                          py={3}
                          bg="white"
                        >
                          <Text
                            fontSize="10px"
                            fontWeight="700"
                            color="gray.400"
                            textTransform="uppercase"
                            letterSpacing="wider"
                            mb={1.5}
                          >
                            Additional sources
                          </Text>
                          <VStack align="stretch" spacing={1}>
                            {additionalSources.map(source => (
                              <Box
                                key={source.url}
                                as="a"
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                fontSize="xs"
                                color="blue.500"
                                textDecoration="underline"
                                noOfLines={1}
                              >
                                {source.title || source.url}
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      );
                    })()}
                    {(detail.mission_runs || []).length > 1 && (
                      <Text fontSize="10px" color="gray.500">
                        {detail.mission_runs.length} attempts ·{' '}
                        {detail.mission_runs.filter(run => run.status === 'failed').length} failed before completion
                      </Text>
                    )}
                    {incompleteMissionSteps.length > 0 && (
                      <Box>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                          Steps not completed
                        </Text>
                        <VStack align="stretch" spacing={1}>
                          {incompleteMissionSteps.map((step, index) => (
                            <HStack key={step.id || `${step.sequence}-${index}`} align="flex-start" spacing={2}>
                              <Text fontSize="xs" color="gray.500" flex={1}>
                                {step.name || step.tool_name || 'Mission step'}
                              </Text>
                              <Badge
                                fontSize="9px"
                                colorScheme={STEP_STATUS_COLOR[step.status] || 'gray'}
                                flexShrink={0}
                              >
                                {runStepStatusLabel(step)}
                              </Badge>
                            </HStack>
                          ))}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                </Box>
              </>
            )}

            {/* Timeline */}
            <Box>
              <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                Timeline
              </Text>
              <VStack align="stretch" spacing={1}>
                {item.created_at && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">Created</Text>
                    <Text fontSize="xs" color="gray.700">{formatDateTime(item.created_at)}</Text>
                  </HStack>
                )}
                {isTask && nextScheduledAt && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">{taskCadence(item) ? 'Next run' : 'Scheduled'}</Text>
                    <Text fontSize="xs" color="gray.700">{formatDateTime(nextScheduledAt)}</Text>
                  </HStack>
                )}
                {isTask && item.started_at && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">Started</Text>
                    <Text fontSize="xs" color="gray.700">{formatDateTime(item.started_at)}</Text>
                  </HStack>
                )}
                {(item.completed_at) && (
                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">Completed</Text>
                    <Text fontSize="xs" color="gray.700">{formatDateTime(item.completed_at)}</Text>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Mission context — authority, trigger, scope */}
            {isTask && detail?.mission && (() => {
              const m = detail.mission;
              const authority = m.authority || {};
              const AUTHORITY_LABEL = { automatic: 'Automatic', ask: 'Ask me first', never: 'Never' };
              const AUTHORITY_COLOR = { automatic: 'green', ask: 'yellow', never: 'gray' };
              const AUTHORITY_KEYS = {
                research: 'Research',
                connector_reads: 'Read connectors',
                store_learnings: 'Store learnings',
                regenerate_reports: 'Regenerate reports',
                change_plan_content: 'Change plan content',
                email_plan_owner: 'Email me',
                external_write: 'Write to external tools',
                external_outreach: 'External outreach',
              };
              const nonDefaultAuthority = Object.entries(AUTHORITY_KEYS).filter(([k]) => authority[k] && authority[k] !== 'ask');
              if (!nonDefaultAuthority.length) return null;
              return (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Permissions
                    </Text>
                    <VStack align="stretch" spacing={2}>
                      {nonDefaultAuthority.map(([k, label]) => (
                        <HStack key={k} justify="space-between">
                          <Text fontSize="xs" color="gray.500">{label}</Text>
                          <Badge fontSize="9px" colorScheme={AUTHORITY_COLOR[authority[k]] || 'gray'}>
                            {AUTHORITY_LABEL[authority[k]] || authority[k]}
                          </Badge>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </>
              );
            })()}

            <Divider />

            {/* Agent + workflow */}
            {isTask && (
              <Box>
                <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                  Execution
                </Text>
                <VStack align="stretch" spacing={1}>
                  {item.created_by && (
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.500">Agent</Text>
                      <Text fontSize="xs" color="gray.700">{AGENT_LABEL[item.created_by] || item.created_by}</Text>
                    </HStack>
                  )}
                  {item.task_type && (
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.500">Type</Text>
                      <Text fontSize="xs" color="gray.700">{TASK_TYPE_LABEL[item.task_type] || item.task_type}</Text>
                    </HStack>
                  )}
                  {item.priority && (
                    <HStack justify="space-between">
                      <Text fontSize="xs" color="gray.500">Priority</Text>
                      <Text fontSize="xs" color="gray.700" textTransform="capitalize">{item.priority}</Text>
                    </HStack>
                  )}
                </VStack>
              </Box>
            )}

            {/* Tools — expandable cards, driven by workflow/tool metadata API */}
            {isTask && (() => {
              // Resolve capability: from fetched detail or fall back to task_type
              const capability = detail?.task?.capability || item.task_type;
              const workflow = workflowMeta[capability];
              const steps = workflow?.steps || [];
              if (!steps.length) return null;
              const stepRows = Array.isArray(item.steps) ? item.steps : [];
              const stepByName = stepRows.reduce((acc, step) => {
                acc[step.step_key || step.name] = step;
                return acc;
              }, {});

              // Build tool card data from registry metadata
              const tools = steps.map(toolName => {
                const toolDef = toolMeta[toolName];
                const stepRow = stepByName[toolName];
                if (!toolDef) return { name: toolName, description: null, artifact: null, inputSchema: [], output: [], step: stepRow };

                // Input schema from params.properties (JSON Schema)
                const props = toolDef.params?.properties || {};
                const inputSchema = Object.entries(props).map(([key, schema]) => ({
                  param: key,
                  type: Array.isArray(schema.type) ? schema.type.join(' | ') : (schema.type || 'any'),
                  desc: schema.description || (schema.enum ? schema.enum.join(' | ') : ''),
                }));

                // Completed output: result.properties keys intersected with the
                // full detail result. Planned tasks use their task inputs below.
                // separately so params are not mislabeled as generated output.
                const resultProps = toolDef.result?.properties || {};
                const stepOutput = stepRow?.output && typeof stepRow.output === 'object' ? stepRow.output : null;
                const r = stepOutput || taskResult || {};
                const output = Object.entries(resultProps)
                  .filter(([k]) => r[k] !== undefined && r[k] !== null)
                  .map(([k, schema]) => ({
                    label: schema.description || k.replace(/_/g, ' '),
                    value: k === 'sent_at' ? formatDateTime(r[k]) : String(r[k]),
                  }));

                // For engagement tasks, surface key params as scheduled inputs until
                // the workflow actually runs. Once completed, output remains output.
                if (toolName === 'generate_email_content' && Object.keys(taskParameters).length > 0 && item.status !== 'completed') {
                  const p = taskParameters;
                  if (p.email_type) output.unshift({ label: 'Email type', value: p.email_type });
                  if (p.angle) output.push({ label: 'Angle', value: p.angle });
                  if (p.goal) output.push({ label: 'Goal', value: p.goal });
                  if (p.context) output.push({ label: 'Context', value: String(p.context).replace(/\s+/g, ' ').slice(0, 220) });
                }

                return { name: toolName, description: toolDef.description, artifact: toolDef.artifact, inputSchema, output, step: stepRow };
              }).filter(Boolean);

              if (!tools.length) return null;

              return (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Tools
                    </Text>
                    <Accordion allowMultiple>
                      {tools.map((tool, i) => {
                        const output = tool.output;
                        const stepStatus = tool.step?.status || (item.status === 'completed' ? 'completed' : 'not_started');
                        return (
                          <AccordionItem key={tool.name} border="none" mb={1}>
                            <AccordionButton
                              px={3} py={2}
                              bg="gray.50" borderRadius="md"
                              _hover={{ bg: 'gray.100' }}
                              _expanded={{ bg: 'gray.100', borderBottomRadius: 0 }}
                            >
                              <HStack flex={1} spacing={2} align="center">
                                <Text fontSize="10px" color="gray.400" fontFamily="mono" flexShrink={0}>{i + 1}</Text>
                                <Text fontSize="xs" color="gray.700" fontFamily="mono" fontWeight="500">{tool.name}</Text>
                                {tool.artifact && (
                                  <Badge fontSize="9px" colorScheme="purple" variant="subtle" flexShrink={0}>
                                    {tool.artifact}
                                  </Badge>
                                )}
                                <Badge fontSize="9px" colorScheme={STEP_STATUS_COLOR[stepStatus] || 'gray'} variant="subtle" flexShrink={0}>
                                  {STEP_STATUS_LABEL[stepStatus] || stepStatus}
                                </Badge>
                              </HStack>
                              <AccordionIcon color="gray.400" />
                            </AccordionButton>
                            <AccordionPanel
                              px={3} py={3}
                              bg="gray.50"
                              borderTop="1px solid"
                              borderColor="gray.200"
                              borderBottomRadius="md"
                            >
                              <Text fontSize="xs" color="gray.500" mb={3}>{tool.description}</Text>
                              {tool.step?.error_message && (
                                <Text fontSize="xs" color="red.500" mb={3}>
                                  {userSafeFailureMessage({ error_message: tool.step.error_message }, {})}
                                </Text>
                              )}

                              {/* Schema */}
                              {tool.inputSchema?.length > 0 && (
                                <Box mb={output.length ? 3 : 0}>
                                  <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                                    Input schema
                                  </Text>
                                  <VStack align="stretch" spacing={1}>
                                    {tool.inputSchema.map(s => (
                                      <HStack key={s.param} align="flex-start" spacing={2}>
                                        <Text fontSize="10px" fontFamily="mono" color="brand.600" flexShrink={0} pt="1px">{s.param}</Text>
                                        <Text fontSize="10px" color="gray.400" flexShrink={0} pt="1px">{s.type}</Text>
                                        <Text fontSize="10px" color="gray.500">{s.desc}</Text>
                                      </HStack>
                                    ))}
                                  </VStack>
                                </Box>
                              )}

                              {/* Output / scheduled inputs */}
                              {output.length > 0 && (
                                <Box>
                                  <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={1.5}>
                                    {item.status === 'completed' ? 'Run output' : 'Scheduled inputs'}
                                  </Text>
                                  <VStack align="stretch" spacing={1}>
                                    {output.map(row => (
                                      <HStack key={row.label} justify="space-between" align="flex-start">
                                        <Text fontSize="xs" color="gray.400" flexShrink={0}>{row.label}</Text>
                                        <Text fontSize="xs" color="gray.700" textAlign="right">{row.value}</Text>
                                      </HStack>
                                    ))}
                                  </VStack>
                                </Box>
                              )}
                            </AccordionPanel>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </Box>
                </>
              );
            })()}


            {/* Pulse content preview */}
            {isPulse && item.content && (
              <Box>
                <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                  Content
                </Text>
                <Text fontSize="xs" color="gray.600" whiteSpace="pre-wrap" noOfLines={12}>
                  {item.content}
                </Text>
              </Box>
            )}

            {/* Parameters — show what data was passed to this task */}
            {isTask && Object.keys(taskParameters).length > 0 && (() => {
              const p = taskParameters;
              const keys = Object.keys(p || {}).filter(k => k !== 'context' && p[k] !== null && p[k] !== undefined);
              if (!keys.length) return null;
              return (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Data
                    </Text>
                    {keys.map(k => {
                      const v = p[k];
                      const isArr = Array.isArray(v);
                      const isObj = v && typeof v === 'object' && !isArr;
                      return (
                        <Box key={k} mb={3}>
                          <Text fontSize="10px" fontWeight="600" color="gray.500" textTransform="capitalize" mb={1}>
                            {k.replace(/_/g, ' ')}
                          </Text>
                          {isArr ? (
                            <VStack align="stretch" spacing={2}>
                              {v.map((entry, i) => (
                                <Box key={i} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                                  {typeof entry === 'object'
                                    ? Object.entries(entry).map(([ek, ev]) => (
                                        <HStack key={ek} align="flex-start" spacing={2} mb={0.5}>
                                          <Text fontSize="10px" color="gray.400" flexShrink={0} minW="70px" textTransform="capitalize">{ek.replace(/_/g, ' ')}</Text>
                                          <Text fontSize="10px" color="gray.700" wordBreak="break-word">{String(ev)}</Text>
                                        </HStack>
                                      ))
                                    : <Text fontSize="xs" color="gray.700">{String(entry)}</Text>
                                  }
                                </Box>
                              ))}
                            </VStack>
                          ) : isObj ? (
                            <Box p={2} bg="gray.50" borderRadius="md">
                              <Text fontSize="10px" color="gray.700" fontFamily="mono" whiteSpace="pre-wrap">{JSON.stringify(v, null, 2)}</Text>
                            </Box>
                          ) : (
                            <Text fontSize="xs" color="gray.700" lineHeight="1.6">{String(v)}</Text>
                          )}
                        </Box>
                      );
                    })}
                    {p.context && (
                      <Box mt={1}>
                        <Text fontSize="10px" fontWeight="600" color="gray.400" mb={1}>Context note</Text>
                        <Text fontSize="xs" color="gray.500" fontStyle="italic" lineHeight="1.5">{p.context}</Text>
                      </Box>
                    )}
                  </Box>
                </>
              );
            })()}

            {/* Result — for plan update tasks */}
            {isTask && Object.keys(taskResult).length > 0 && (() => {
              const r = taskResult;
              if (!r || r.recipient || r.pulse_id) return null; // email/pulse handled above
              const rawValue = r.rawValue;
              const value = r.value;
              if (!rawValue && !value) return null;
              let parsed = null;
              if (rawValue) { try { parsed = JSON.parse(rawValue); } catch { parsed = rawValue; } }
              return (
                <>
                  <Divider />
                  <Box>
                    <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={2}>
                      Result
                    </Text>
                    {value && <Text fontSize="xs" color="green.600" mb={parsed ? 2 : 0}>{value}</Text>}
                    {Array.isArray(parsed) && (
                      <VStack align="stretch" spacing={2}>
                        {parsed.map((entry, i) => (
                          <Box key={i} p={2} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.100">
                            {typeof entry === 'object'
                              ? Object.entries(entry).map(([ek, ev]) => (
                                  <HStack key={ek} align="flex-start" spacing={2} mb={0.5}>
                                    <Text fontSize="10px" color="gray.400" flexShrink={0} minW="70px" textTransform="capitalize">{ek.replace(/_/g, ' ')}</Text>
                                    <Text fontSize="10px" color="gray.700" wordBreak="break-word">{String(ev)}</Text>
                                  </HStack>
                                ))
                              : <Text fontSize="xs" color="gray.700">{String(entry)}</Text>
                            }
                          </Box>
                        ))}
                      </VStack>
                    )}
                    {typeof parsed === 'string' && (
                      <Text fontSize="xs" color="gray.700" lineHeight="1.6">{parsed}</Text>
                    )}
                  </Box>
                </>
              );
            })()}

              </VStack>
            </Collapse>
          </VStack>
        )}
      </Box>
      <ConfirmActionDialog
        isOpen={!!confirmAction}
        leastDestructiveRef={leastDestructiveRef}
        title={confirmAction?.title}
        body={confirmAction?.body}
        confirmLabel={confirmAction?.confirmLabel}
        confirmColorScheme={confirmAction?.confirmColorScheme}
        isLoading={actionUpdating || deleting}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => {
          const action = confirmAction?.onConfirm;
          if (!action) return;
          await action();
          setConfirmAction(null);
        }}
      />
    </Flex>
  );
}

function TasksTab({ userId, planFilter, initialTaskId, initialStatusTab = 'Planned', onCloseDrawer, hideProjectContext, detailPortalContainerRef }) {
  const [data,         setData]        = useState(null);
  const linkedJobRecords = useMemo(() => (data?.plans || []).flatMap(plan =>
    (plan.items || []).map(record => ({ ...record, plan_id: record.plan_id || plan.plan_id, _plan_id: plan.plan_id, plan_name: record.plan_name || plan.plan_name }))
  ), [data]);
  const [loading,      setLoading]     = useState(true);
  const [loadError,    setLoadError]   = useState(false);
  const [statusTab,    setStatusTab]   = useState(initialStatusTab);
  const [selectedItem,        setSelectedItem]        = useState(null);
  const [selectedItemIsRoutine, setSelectedItemIsRoutine] = useState(false);
  const selectedItemIsRoutineRef = useRef(selectedItemIsRoutine);
  selectedItemIsRoutineRef.current = selectedItemIsRoutine;
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const detailCloseTimerRef = useRef(null);
  // Below `md`: detail replaces the whole drawer as a full overlay. At `md`+: unchanged
  // desktop master-detail split (list shrinks, detail sits beside it as its own column).
  const isDetailOverlayMode = useBreakpointValue({ base: true, md: false });
  const [toolMeta,      setToolMeta]      = useState({});
  const [workflowMeta, setWorkflowMeta] = useState({});
  const [completingItems, setCompletingItems] = useState(new Set());
  const [suggestionsGenerating, setSuggestionsGenerating] = useState(false);
  const [showSuggestedJobs, setShowSuggestedJobs] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const dataRef = useRef(null);
  const suggestionRequestRef = useRef(new Set());
  const leastDestructiveRef = useRef(null);
  const toast = useToast();
  const {
    planStatus,
    creditBalance,
    isLoading: limitsLoading,
    hasSavedPaymentMethod,
    creditsResetAt,
    isTopUpModalOpen,
    topUpModalMode,
    topUpReason,
    openTopUpModal,
    closeTopUpModal,
  } = usePlanLimits();
  const agentTaskCreditEntry = planStatus?.credits?.costs?.['agent-task'];
  const agentTaskCreditCost = agentTaskCreditEntry?.credits ?? agentTaskCreditEntry ?? 1;
  const agentTaskCreditGated = agentTaskCreditEntry?.isGated ?? true;
  const activationCredit = {
    creditBalance: limitsLoading ? null : creditBalance,
    creditCost: agentTaskCreditCost,
    creditGated: agentTaskCreditGated,
    creditsResetAt,
    onTopUp: () => openTopUpModal('topup', 'credits'),
  };

  useEffect(() => {
    setStatusTab(initialStatusTab);
  }, [initialStatusTab]);

  const loadTasks = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetchWithAuth(`/api/agent/tasks/user/${userId}?compact=1`);
      if (!response.ok) throw new Error(`Jobs request failed with status ${response.status}`);
      const nextData = await response.json();
      setData(nextData);
      setLoadError(false);
      setLoading(false);
      // Sync the open detail panel with fresh item data (e.g. after pause/resume)
      setSelectedItem(prev => {
        if (!prev || !nextData?.plans) return prev;
        const allItems = nextData.plans.flatMap(plan =>
          (plan.items || []).map(i => ({ ...i, _plan_id: plan.plan_id, plan_name: plan.plan_name }))
        );
        const isRoutine = selectedItemIsRoutineRef.current;
        const refreshed = findRefreshedJobRecord(allItems, prev, isRoutine);
        if (refreshed) {
          setStatusTab(tab => tab === statusTabForItem(prev, isRoutine)
            ? statusTabForItem(refreshed, isRoutine) : tab);
        }
        return refreshed ?? prev;
      });
    } catch {
      setLoadError(true);
      setLoading(false);
    }
  }, [userId]);

  const generateSuggestions = useCallback(async ({ allowWithPlanned = false } = {}) => {
    if (!planFilter || planFilter === 'all') return;
    setSuggestionsGenerating(true);
    try {
      const response = await fetchWithAuth(`/api/agent/tasks/suggestions/plan/${planFilter}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowWithPlanned }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'Failed to create suggestions');
      }
      await loadTasks();
      setShowSuggestedJobs(true);
    } catch (err) {
      console.warn('[JobsView] suggestion generation failed:', err.message);
      toast({ title: err.message || 'Could not create suggested jobs', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setSuggestionsGenerating(false);
    }
  }, [planFilter, loadTasks, toast]);

  useEffect(() => {
    if (!userId) return;
    loadTasks();

    // Fetch tool + workflow metadata for dynamic tool card rendering
    fetchWithAuth('/api/agent/tasks/metadata/tools')
      .then(r => r.json())
      .then(tools => {
        const map = {};
        tools.forEach(t => { map[t.name] = t; });
        setToolMeta(map);
      })
      .catch(() => {});

    fetchWithAuth('/api/agent/tasks/metadata/workflows')
      .then(r => r.json())
      .then(workflows => {
        const map = {};
        workflows.forEach(w => { map[w.name] = w; });
        setWorkflowMeta(map);
      })
      .catch(() => {});
  }, [userId, loadTasks]);

  // Keep dataRef current so the polling closure always sees the latest data
  useEffect(() => { dataRef.current = data; }, [data]);

  // Poll every 5s while there are running jobs; detect completed transitions
  useEffect(() => {
    if (!userId) return;
    const intervalId = setInterval(async () => {
      const current = dataRef.current;
      const allItems = current?.plans?.flatMap(p => p.items || []) || [];
      const runningItems = allItems.filter(i => effectiveTaskStatus(i) === 'running');
      try {
        const response = await fetchWithAuth(`/api/agent/tasks/user/${userId}/running-statuses`);
        if (!response.ok) return;
        const statusData = await response.json();
        const nextStatuses = Array.isArray(statusData?.tasks) ? statusData.tasks : [];
        if (nextStatuses.some(next => !runningItems.some(prev => String(prev.id) === String(next.id)))) {
          await loadTasks();
          return;
        }
        const changedItems = runningItems.filter(prev => {
          const match = nextStatuses.find(next => String(next.id) === String(prev.id));
          return !match || effectiveTaskStatus(match) !== 'running';
        });
        if (changedItems.length > 0) {
          const nextDataResponse = await fetchWithAuth(`/api/agent/tasks/user/${userId}?compact=1`);
          if (!nextDataResponse.ok) return;
          const nextData = await nextDataResponse.json();
          const nextItems = nextData?.plans?.flatMap(p => p.items || []) || [];
          const newlyCompleted = changedItems.filter(prev => {
            const match = findRefreshedJobRecord(nextItems, prev);
            return match && effectiveTaskStatus(match) === 'completed';
          });
          if (newlyCompleted.length === 0) {
            setData(nextData);
            return;
          }
          const keys = new Set(newlyCompleted.map(jobRecordKey));
          setCompletingItems(prev => new Set([...prev, ...keys]));
          setTimeout(() => {
            setCompletingItems(prev => {
              const next = new Set(prev);
              keys.forEach(k => next.delete(k));
              return next;
            });
            setData(nextData);
            setSelectedItem(prev => {
              if (!prev || !nextData?.plans) return prev;
              const items = nextData.plans.flatMap(p =>
                (p.items || []).map(i => ({ ...i, _plan_id: p.plan_id, plan_name: p.plan_name }))
              );
              const isRoutine = selectedItemIsRoutineRef.current;
              const refreshed = findRefreshedJobRecord(items, prev, isRoutine);
              if (refreshed) {
                setStatusTab(tab => tab === statusTabForItem(prev, isRoutine)
                  ? statusTabForItem(refreshed, isRoutine) : tab);
              }
              return refreshed ?? prev;
            });
          }, 1800);
        }
      } catch {}
    }, 5000);
    return () => clearInterval(intervalId);
  }, [userId, loadTasks]);

  useEffect(() => {
    if (!userId || !data?.plans || statusTab !== 'Planned' || planFilter === 'all') return;
    const plan = data.plans.find(p => String(p.plan_id) === String(planFilter));
    if (!plan) return;

    const items = (plan.items || []).map(item => ({ ...item, _plan_id: plan.plan_id, plan_name: item.plan_name || plan.plan_name }));
    const hasPlannedJobs = items.some(item => {
      if (item.record_type !== 'task') return false;
      const status = effectiveTaskStatus(item);
      if (!['todo', 'pending', 'paused'].includes(status)) return false;
      return !taskCadence(item);
    });
    const key = String(planFilter);

    if (hasPlannedJobs || suggestionRequestRef.current.has(key)) return;

    suggestionRequestRef.current.add(key);
    generateSuggestions();
  }, [userId, data, statusTab, planFilter, generateSuggestions]);

  useEffect(() => {
    setShowSuggestedJobs(false);
  }, [planFilter, statusTab]);

  const openDetail = useCallback((item, isRoutine = false) => {
    clearTimeout(detailCloseTimerRef.current);
    setSelectedItem(item);
    setSelectedItemIsRoutine(isRoutine);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsDetailPanelOpen(true));
    });
  }, []);

  const openLinkedDetail = useCallback((taskId, recordType = 'task') => {
    const target = findLinkedJobRecord(linkedJobRecords, taskId, recordType);
    if (!target) return;
    const isRoutine = target.record_type === 'routine';
    setStatusTab(statusTabForItem(target, isRoutine));
    openDetail(target, isRoutine);
  }, [linkedJobRecords, openDetail]);

  // data refetches constantly (polling, after every mutation) and this effect
  // depended on [data, initialTaskId] with no memory of having already
  // applied it — so every refetch re-selected the initialTaskId job, snapping
  // the panel back to it even after the user had manually clicked into a
  // different item (most visibly, any Routines-tab item, since a routine's
  // own detail fetch triggers the next data refresh almost immediately).
  const appliedInitialTaskIdRef = useRef(null);
  useEffect(() => {
    if (!initialTaskId || !data) return;
    if (appliedInitialTaskIdRef.current === initialTaskId) return;
    const items = data.plans.flatMap(plan =>
      (plan.items || []).map(item => ({ ...item, _plan_id: plan.plan_id, plan_name: plan.plan_name }))
    );
    const target = items.find(i => String(i.id) === String(initialTaskId));
    if (target) {
      appliedInitialTaskIdRef.current = initialTaskId;
      const isRoutine = target.record_type === 'routine';
      setStatusTab(statusTabForItem(target, isRoutine));
      clearTimeout(detailCloseTimerRef.current);
      setSelectedItem(target);
      setSelectedItemIsRoutine(isRoutine);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsDetailPanelOpen(true));
      });
    }
  }, [data, initialTaskId]);

  const closeDetail = useCallback(() => {
    setIsDetailPanelOpen(false);
    clearTimeout(detailCloseTimerRef.current);
    detailCloseTimerRef.current = setTimeout(() => {
      setSelectedItem(null);
      setSelectedItemIsRoutine(false);
    }, 320);
  }, []);

  const removeItemFromData = useCallback((id) => {
    setData(prev => {
      if (!prev?.plans) return prev;
      return {
        ...prev,
        plans: prev.plans.map(plan => ({
          ...plan,
          items: plan.items.filter(it => it.id !== id),
        })),
      };
    });
  }, []);

  const performListAction = useCallback(async (action, item) => {
    const missionId = item.mission_id;

    let url, method, body = {};
    if (action === 'activate') {
      url = `/api/agent/tasks/${item.id}/activate`;
      method = 'POST';
    } else if (action === 'pause') {
      url = `/api/agent/missions/${missionId}/pause`;
      method = 'POST';
    } else if (action === 'resume') {
      url = `/api/agent/missions/${missionId}/resume`;
      method = 'POST';
      body = { next_run_at: new Date().toISOString(), execute_now: true };
    } else if (action === 'pause_pulse_routine') {
      url = `/api/pulse/${item._plan_id || item.plan_id}/schedule/pause`;
      method = 'POST';
    } else if (action === 'resume_pulse_routine') {
      url = `/api/pulse/${item._plan_id || item.plan_id}/schedule`;
      method = 'POST';
      body = {
        frequency: item.pulse_frequency || 'weekly',
        day_of_week: item.pulse_day_of_week ?? 1,
        hour: item.pulse_hour ?? 9,
        timezone: item.pulse_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        tone: item.pulse_tone || 'direct',
        email: !!item.pulse_email_enabled,
      };
    } else if (action === 'cancel') {
      url = (missionId && !['cancelled', 'completed'].includes(item.mission_status))
        ? `/api/agent/missions/${missionId}/cancel`
        : `/api/agent/tasks/${item.id}/cancel`;
      method = 'POST';
    } else if (action === 'delete') {
      url = `/api/agent/tasks/${item.id}`;
      method = 'DELETE';
    }

    try {
      const res = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: method !== 'DELETE' ? JSON.stringify(body) : undefined,
      });
      if (res.ok) {
        const labels = { activate: 'Job activated', pause: 'Mission paused', resume: 'Mission resumed', pause_pulse_routine: 'Routine paused', resume_pulse_routine: 'Routine scheduled', cancel: 'Cancelled', delete: 'Deleted' };
        window.dispatchEvent(new CustomEvent('pabs:agentTasksUpdated', { detail: { planId: item.plan_id || item._plan_id } }));
        toast({ title: labels[action], status: 'success', duration: 2000, isClosable: true });
        if (action === 'delete') {
          removeItemFromData(item.id);
          if (selectedItem?.id === item.id) closeDetail();
        } else {
          if (action === 'cancel' && selectedItem?.id === item.id) closeDetail();
          await loadTasks();
        }
      } else {
        const payload = await res.json().catch(() => ({}));
        if (action === 'activate' && isCreditLimitError(payload.error)) {
          return { needsCredits: true, neededCredits: parseNeededCredits(payload.error), error: payload.error };
        }
        toast({ title: payload.error || 'Action failed', status: 'error', duration: 3000, isClosable: true });
      }
    } catch {
      toast({ title: 'Action failed', status: 'error', duration: 3000, isClosable: true });
    }
    return null;
  }, [removeItemFromData, selectedItem, closeDetail, loadTasks, toast]);

  const handleListAction = useCallback((action, item, e) => {
    e?.stopPropagation();
    if (action === 'cancel' || action === 'delete') {
      setConfirmAction({
        ...destructiveActionCopy(action, item),
        onConfirm: () => performListAction(action, item),
      });
      return null;
    }
    return performListAction(action, item);
  }, [performListAction]);

  const runListJobNow = useCallback((item, label, event) => {
    event?.stopPropagation();
    const message = `Run ${label} now (task ID: ${item.id})`;
    window.dispatchEvent(new CustomEvent('pabs:closeJobs'));
    window.dispatchEvent(new CustomEvent('pabs:runTask', { detail: { message } }));
  }, []);

  if (loading) return <Box pt={8} textAlign="center"><Spinner size="sm" color="gray.400" /></Box>;
  // A failed fetch is not an empty account. Keep the tabs so the drawer still reads as
  // the jobs surface, but pass no counts: we do not know them, and a zero would claim
  // the list loaded and found nothing.
  if (loadError) return (
    <Box display="flex" flexDirection="column" h="full">
      <SidePanelTabs
        ariaLabel="Job status" idPrefix="jobs-tab"
        tabs={STATUS_TABS}
        activeValue={statusTab}
        onChange={setStatusTab}
        counts={null}
      />
      <Box pt={10} textAlign="center" px={4}>
        <VStack spacing={3}>
          <Text fontSize="sm" color="gray.700">Could not load your jobs.</Text>
          <Text fontSize="xs" color="gray.500">
            This is a connection problem, not an empty plan. Your jobs are still there.
          </Text>
          <Button
            size="xs"
            colorScheme="blue"
            onClick={() => { setLoading(true); setLoadError(false); loadTasks(); }}
          >
            Try again
          </Button>
        </VStack>
      </Box>
    </Box>
  );
  if (!data?.plans?.length) return (
    <Box pt={10} textAlign="center">
      <Text fontSize="sm" color="gray.400">No activity yet. PABS will show its work here.</Text>
    </Box>
  );

  // Flatten all items across plans, attach plan info, sort by date desc
  const allRecords = data.plans.flatMap(plan =>
    plan.items.map(item => ({
      ...item,
      plan_name: item.plan_name || plan.plan_name,
      _plan_id: plan.plan_id,
    }))
  ).filter(item => !isSystemNotificationTask(item));
  const clientActivity=allRecords.filter(isClientActivity);
  const allItems=allRecords.filter(item=>!isClientActivity(item));

  const activeTab = STATUS_TABS.find(t => t.label === statusTab);

  const routines = Array.from(
    allItems
      .filter(item =>
        effectiveTaskStatus(item) !== 'suggested'
        && taskCadence(item)
        && (item.record_type === 'task' || item.record_type === 'routine')
      )
      .reduce((map, item) => {
        const key = routineKey(item);
        if (!map.has(key)) map.set(key, item);
        return map;
      }, new Map())
      .values()
  );

  const tabCounts = Object.fromEntries(STATUS_TABS.map(tab => {
    if(tab.activity) return [tab.label,clientActivity.filter(item=>planFilter==='all' || String(item._plan_id)===planFilter).length];
    const planFiltered = planFilter === 'all' ? allItems : allItems.filter(i => String(i._plan_id) === planFilter);
    const count = tab.routine
      ? routines.filter(i => planFilter === 'all' || String(i._plan_id) === planFilter).length
      : tab.statuses
      ? planFiltered.filter(i => {
          const s = i.record_type === 'pulse' ? 'completed' : effectiveTaskStatus(i);
          if (isQueuedBlockerHandoff(i)) {
            if (tab.label !== 'Planned') return false;
          } else if (isProductRequirementItem(i) && ['todo', 'pending'].includes(s)) {
            if (tab.label !== 'In progress') return false;
          } else if (!tab.statuses.includes(s)) {
            return false;
          }
          if (tab.label === 'Planned' && taskCadence(i) && s !== 'suggested') return false;
          return true;
        }).length
      : planFiltered.length;
    return [tab.label, count];
  }));

  const filtered = (activeTab?.activity ? clientActivity : activeTab?.routine ? routines : allItems).filter(item => {
    if (activeTab?.statuses) {
      const itemStatus = item.record_type === 'pulse' ? 'completed' : effectiveTaskStatus(item);
      if (isQueuedBlockerHandoff(item)) {
        if (activeTab.label !== 'Planned') return false;
      } else if (isProductRequirementItem(item) && ['todo', 'pending'].includes(itemStatus)) {
        if (activeTab.label !== 'In progress') return false;
      } else if (!activeTab.statuses.includes(itemStatus)) {
        return false;
      }
      if (activeTab.label === 'Planned' && taskCadence(item) && itemStatus !== 'suggested') return false;
    }
    if (planFilter !== 'all' && String(item._plan_id) !== planFilter) return false;
    return true;
  }).sort((a, b) => {
    const aDate = new Date(scheduledDisplayDate(a, {
      routine: !!activeTab?.routine,
      tabLabel: activeTab?.label,
    }));
    const bDate = new Date(scheduledDisplayDate(b, {
      routine: !!activeTab?.routine,
      tabLabel: activeTab?.label,
    }));
    return bDate - aDate;
  });

  const suggestedItems = allItems.filter(item => {
    if (activeTab?.label !== 'Planned') return false;
    if (item.record_type !== 'task') return false;
    if (effectiveTaskStatus(item) !== 'suggested') return false;
    if (planFilter !== 'all' && String(item._plan_id) !== planFilter) return false;
    return true;
  });

  const showingSuggested = activeTab?.label === 'Planned' && filtered.length === 0 && suggestedItems.length > 0;
  const showingSuggestedOnDemand = activeTab?.label === 'Planned' && filtered.length > 0 && showSuggestedJobs && suggestedItems.length > 0;
  const showSuggestedToggle = activeTab?.label === 'Planned' && filtered.length > 0 && (suggestedItems.length > 0 || planFilter !== 'all');
  const visibleItems = filtered;

  const activeRoutinesCount = routines.filter(item => {
    if (planFilter !== 'all' && String(item._plan_id) !== planFilter) return false;
    return routineDisplayStatus(item) === 'active';
  }).length;
  const schedulableRoutinesCount = routines.filter(item => {
    if (planFilter !== 'all' && String(item._plan_id) !== planFilter) return false;
    return routineDisplayStatus(item) === 'unscheduled';
  }).length;
  const inProgressItems = allItems.filter(item => {
    if (isQueuedBlockerHandoff(item)) return false;
    if (planFilter !== 'all' && String(item._plan_id) !== planFilter) return false;
    const itemStatus = item.record_type === 'pulse' ? 'completed' : effectiveTaskStatus(item);
    if (isProductRequirementItem(item) && ['todo', 'pending'].includes(itemStatus)) return true;
    return ['running', 'awaiting_approval', 'waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'blocked'].includes(itemStatus);
  });
  const openDecisionCount = inProgressItems.filter(item => needsDecisionCommitment(item)).length;
  const nonDecisionInProgressCount = Math.max(0, inProgressItems.length - openDecisionCount);

  return (
    <Flex align="stretch" minH={0} flex={1} overflow="hidden" maxW="full">
      {/* List column */}
      <Box
        flex={{ base: '1', md: selectedItem ? '0 0 40%' : '1' }}
        minW={0}
        maxW={{ base: 'full', md: selectedItem ? '40%' : 'full' }}
        boxSizing="border-box"
        overflowY="auto"
        overflowX="hidden"
        pr={{ base: 0, md: selectedItem ? 3 : 0 }}
        display="flex"
        flexDirection="column"
        h="full"
      >

      {/* Status tabs */}
      <SidePanelTabs
        ariaLabel="Job status" idPrefix="jobs-tab"
        tabs={STATUS_TABS}
        activeValue={statusTab}
        onChange={setStatusTab}
        counts={tabCounts}
      />

      {/* List */}
      {activeTab?.activity && <Text fontSize="sm" color="gray.600" py={3}>Calls from connected clients, including setup checks and errors. These are separate from product Jobs.</Text>}
      {visibleItems.length === 0 ? (
        <Box pt={activeTab?.label === 'Planned' ? 10 : 6} pb={activeTab?.label === 'Planned' ? 10 : 0} textAlign="center" px={4}>
          <VStack spacing={2}>
            <Text fontSize="sm" color="gray.500">
              {activeTab?.label === 'Planned' ? (
                <>
                  No jobs yet.
                  {openDecisionCount > 0 && (
                    <>
                      {' '}
                      <Text as="span" color="blue.500" cursor="pointer" textDecoration="underline"
                        textDecorationStyle="dotted" onClick={() => setStatusTab('In progress')}>
                        {openDecisionCount} decision{openDecisionCount === 1 ? '' : 's'} need{openDecisionCount === 1 ? 's' : ''} your call.
                      </Text>
                    </>
                  )}
                  {nonDecisionInProgressCount > 0 && (
                    <>
                      {' '}
                      <Text as="span" color="blue.500" cursor="pointer" textDecoration="underline"
                        textDecorationStyle="dotted" onClick={() => setStatusTab('In progress')}>
                        {nonDecisionInProgressCount} job{nonDecisionInProgressCount === 1 ? '' : 's'} in progress.
                      </Text>
                    </>
                  )}
                  {schedulableRoutinesCount > 0 && (
                    <>
                      {' '}
                      <Text as="span" color="blue.500" cursor="pointer" textDecoration="underline"
                        textDecorationStyle="dotted" onClick={() => setStatusTab('Routines')}>
                        {schedulableRoutinesCount} routine{schedulableRoutinesCount === 1 ? '' : 's'} ready to schedule.
                      </Text>
                    </>
                  )}
                  {activeRoutinesCount > 0 && (
                    <>
                      {' '}
                      <Text as="span" color="blue.500" cursor="pointer" textDecoration="underline"
                        textDecorationStyle="dotted" onClick={() => setStatusTab('Routines')}>
                        {activeRoutinesCount} active routine{activeRoutinesCount === 1 ? '' : 's'} running in the background.
                      </Text>
                    </>
                  )}
                </>
              )
              : activeTab?.label === 'Routines' ? 'No routines set up yet.'
              : activeTab?.label === 'In progress' ? 'Nothing running right now.'
              : activeTab?.label === 'Completed' ? 'No completed jobs yet.'
              : activeTab?.label === 'Failed' ? 'No failed jobs.'
              : activeTab?.label === 'Cancelled' ? 'No cancelled jobs.'
              : activeTab?.activity ? 'No client activity yet.'
              : 'Nothing here yet.'}
            </Text>
            {activeTab?.label === 'Planned' && suggestionsGenerating && (
              <HStack spacing={2} color="gray.400" justify="center">
                <Spinner size="xs" />
                <Text fontSize="xs">Preparing suggestions...</Text>
              </HStack>
            )}
          </VStack>
        </Box>
      ) : (
        <>
        {showingSuggested ? (
          <Box pt={3}>
            <Box mb={3}>
              <Text fontSize="sm" color="gray.600" fontWeight="700">
                Suggested by Pabs
              </Text>
              <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="1.4">
                Pabs can run these PM jobs using this plan's evidence, lifecycle, and connected context.
              </Text>
            </Box>
            <SimpleGrid columns={{ base: 1, md: selectedItem ? 1 : 2 }} spacing={3}>
              {suggestedItems.map((item, i) => {
                const label = jobDisplayLabel(item);
                const purpose = suggestedJobPurpose(item);
                const cadence = cadenceLabel(suggestedJobCadence(item));
                const worksWith = suggestedJobWorksWith(item);
                return (
                  <Box
                    key={`${item.record_type}-${item.id}-${i}`}
                    role="group"
                    p={3}
                    minH="126px"
                    bg={selectedItem?.id === item.id && selectedItem?.record_type === item.record_type ? 'gray.100' : 'gray.50'}
                    border="1px solid"
                    borderColor={selectedItem?.id === item.id && selectedItem?.record_type === item.record_type ? 'gray.800' : 'transparent'}
                    borderRadius="md"
                    cursor="pointer"
                    transition="background-color 0.15s, border-color 0.15s"
                    _hover={{ bg: 'white', borderColor: 'gray.800' }}
                    onClick={() => openDetail(item, false)}
                  >
                    <HStack align="flex-start" spacing={2.5}>
                      <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#111827', marginTop: '1px' }}>
                        {suggestedJobIcon(item)}
                      </span>
                      <Box minW={0} flex={1}>
                        <HStack spacing={2} align="center" mb={1}>
                          <Text fontSize="xs" fontWeight="700" color="gray.900" noOfLines={1} flex={1} lineHeight="1.35">
                            {label}
                          </Text>
                          <Box position="relative" w="68px" h="18px" flexShrink={0}>
                            <Badge
                              position="absolute"
                              right={0}
                              top="1px"
                              colorScheme="gray"
                              variant="subtle"
                              fontSize="8px"
                              borderRadius="full"
                              px={1.5}
                              transition="opacity 0.15s, transform 0.15s"
                              _groupHover={{ opacity: 0, transform: 'translateX(-6px)' }}
                            >
                              Suggested
                            </Badge>
                            <ActivateJobButton
                              onActivate={() => handleListAction('activate', item)}
                              creditBalance={activationCredit.creditBalance}
                              creditCost={activationCredit.creditCost}
                              creditGated={activationCredit.creditGated}
                              creditsResetAt={activationCredit.creditsResetAt}
                              onTopUp={activationCredit.onTopUp}
                              stopPropagation
                              buttonProps={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                size: 'xs',
                                h: '18px',
                                px: 2,
                                fontSize: '8px',
                                colorScheme: 'gray',
                                borderRadius: 'full',
                                opacity: 0,
                                transform: 'translateX(8px)',
                                transition: 'opacity 0.15s, transform 0.15s',
                                _groupHover: { opacity: 1, transform: 'translateX(0)' },
                              }}
                            >
                              Schedule now
                            </ActivateJobButton>
                          </Box>
                        </HStack>
                        <HStack spacing={1.5} mb={1.5} align="center">
                          <Badge colorScheme="blue" variant="subtle" fontSize="8px" borderRadius="full" flexShrink={0} px={1.5}>
                            {purpose}
                          </Badge>
                          <Text fontSize="10px" color="gray.500" noOfLines={1} lineHeight="1.3">
                            {suggestedJobPurposeReason(purpose)}
                          </Text>
                        </HStack>
                        <Text fontSize="11px" color="gray.700" lineHeight="1.45" noOfLines={2}>
                          {jobCardDescription(item)}
                        </Text>
                        <VStack align="stretch" spacing={0.5} mt={2.5}>
                          <HStack spacing={1.5} color="gray.500">
                            <span className="material-icons-outlined" style={{ fontSize: '13px' }}>event_repeat</span>
                            <Text fontSize="10px">{cadence}</Text>
                          </HStack>
                          {worksWith.length > 0 && (
                            <HStack spacing={1.5} color="gray.500">
                              <span className="material-icons-outlined" style={{ fontSize: '13px' }}>hub</span>
                              <Text fontSize="10px" noOfLines={1}>Works with {worksWith.join(', ')}</Text>
                            </HStack>
                          )}
                          {!hideProjectContext && item.plan_name && (
                            <Text fontSize="10px" color="gray.500" noOfLines={1}>{item.plan_name}</Text>
                          )}
                        </VStack>
                      </Box>
                    </HStack>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        ) : (
        <Box bg="white">
          {visibleItems.map((item, i) => {
            if(isClientActivity(item)) {
              const state=effectiveTaskStatus(item),params=safeObject(item.parameters),result=safeObject(item.result);
              return <Box as="button" key={`activity-${item.id}`} type="button" w="full" textAlign="left" px={4} py={4}
                borderBottom="1px solid" borderColor="gray.100" _hover={{bg:'gray.50'}} _focusVisible={{outline:'2px solid',outlineColor:'blue.500',outlineOffset:'-2px'}}
                onClick={()=>openDetail(item,false)}>
                <HStack justify="space-between" align="start" spacing={3}>
                  <Text fontSize="sm" fontWeight="600" color="gray.800" overflowWrap="anywhere">{clientActivityLabel(item)}</Text>
                  <Badge colorScheme={STATUS_COLOR[state] || 'gray'} flexShrink={0}>{state}</Badge>
                </HStack>
                <Text fontSize="xs" color="gray.600" mt={1}>{params.client || result.client || 'Connected client'}{item.completed_at ? ` · ${formatDateTime(item.completed_at)}` : ''}</Text>
              </Box>;
            }
            const isTask  = item.record_type === 'task';
            const isRoutine = item.record_type === 'routine';
            const isWorkItem = isTask || isRoutine;
            const label   = isWorkItem ? jobDisplayLabel(item) : 'Strategy pulse';
            const itemStatus = activeTab?.routine ? routineDisplayStatus(item) : effectiveTaskStatus(item);
            const color   = isWorkItem ? (STATUS_COLOR[itemStatus] || 'gray') : 'green';
            const status  = isWorkItem ? productRequirementStatusLabel(item, itemStatus) : 'Done';
            const date    = scheduledDisplayDate(item, {
              routine: !!activeTab?.routine,
              tabLabel: activeTab?.label,
            });
            const cueTag  = isWorkItem ? jobCueTag(item) : 'Direction';
            // Terminal tabs (Completed/Failed/Cancelled): compact cards showing just the
            // title (+ lane tag), no plan-name, description, date, or status tag (the tab
            // already says the status). In progress: date sits below the project name.
            const isTerminalTab = ['Completed', 'Failed', 'Cancelled'].includes(activeTab?.label);
            const isInProgressTab = activeTab?.label === 'In progress';

            const isRunning = itemStatus === 'running';
            const itemKey = jobRecordKey(item);
            const isCompleting = completingItems.has(itemKey);
            const isPulseRoutineRow = activeTab?.routine && item.task_type === 'generate_pulse' && item.pulse_frequency;
            const isMissionRow = item.capability === 'agent_mission' && item.mission_id;
            const isPlannedRunnable = activeTab?.label === 'Planned'
              && isTask
              && !isProductRequirementItem(item)
              && ['todo', 'pending'].includes(itemStatus);
            const waitingRowStatuses = ['awaiting_approval', 'waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'blocked'];
            const rowPrimaryAction = isPlannedRunnable
              ? { label: 'Run now', run: event => runListJobNow(item, label, event) }
              : activeTab?.routine && itemStatus === 'active'
                ? { label: 'Pause routine', run: event => isPulseRoutineRow ? handleListAction('pause_pulse_routine', item, event) : handleListAction('pause', item, event) }
                : activeTab?.routine && ['paused', 'unscheduled'].includes(itemStatus)
                  ? { label: 'Schedule routine', run: event => isPulseRoutineRow ? handleListAction('resume_pulse_routine', item, event) : itemStatus === 'paused' && isMissionRow ? handleListAction('resume', item, event) : (event.stopPropagation(), openDetail(item, true)) }
                  : isInProgressTab && isMissionRow && waitingRowStatuses.includes(itemStatus) && safeObject(item.parameters).continuation
                    ? { label: 'Resume job', run: event => handleListAction('resume', item, event) }
                    : isInProgressTab
                      ? { label: waitingRowStatuses.includes(itemStatus) ? 'Review' : 'View job', run: event => { event.stopPropagation(); openDetail(item, false); } }
                      : null;

            return (
              <Box
                key={`${item.record_type}-${item.id}-${i}`}
                px={4} py={3}
                position="relative"
                borderBottom="1px solid"
                borderColor="gray.100"
                _last={{ borderBottom: 'none' }}
                bg={isCompleting ? 'green.50' : (selectedItem?.id === item.id && selectedItem?.record_type === item.record_type ? 'brand.100' : 'white')}
                _hover={{
                  bg: isCompleting ? 'green.50' : (selectedItem?.id === item.id && selectedItem?.record_type === item.record_type ? 'brand.100' : 'gray.50'),
                  boxShadow: isCompleting ? 'none' : 'inset 0 0 0 1px #1A202C',
                }}
                cursor={isCompleting ? 'default' : 'pointer'}
                tabIndex={isCompleting ? -1 : 0}
                onClick={isCompleting ? undefined : () => openDetail(item, !!activeTab?.routine)}
                onKeyDown={isCompleting ? undefined : event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); openDetail(item, !!activeTab?.routine); } }}
                borderLeft={(isRunning || isCompleting) ? '3px solid' : '0px solid'}
                borderLeftColor={isCompleting ? 'green.400' : 'brand.800'}
                sx={{
                  ...(isRunning && !isCompleting ? {
                    animation: 'pulseBorder 1.5s ease-in-out infinite',
                    '@keyframes pulseBorder': {
                      '0%, 100%': { borderLeftColor: '#1a202c' },
                      '50%': { borderLeftColor: 'rgba(26,32,44,0.2)' },
                    },
                  } : {}),
                  ...(isCompleting ? {
                    animation: 'fadeOutCard 1.8s ease-out forwards',
                    '@keyframes fadeOutCard': {
                      '0%': { opacity: 1 },
                      '55%': { opacity: 1 },
                      '100%': { opacity: 0 },
                    },
                  } : {}),
                }}
                role="group"
              >
                {isCompleting && (
                  <Box
                    position="absolute" top={0} left={0} right={0} bottom={0}
                    display="flex" alignItems="center" justifyContent="center"
                    zIndex={1} pointerEvents="none"
                    sx={{
                      animation: 'fadeInOverlay 0.3s ease-out',
                      '@keyframes fadeInOverlay': {
                        from: { opacity: 0 },
                        to: { opacity: 1 },
                      },
                    }}
                  >
                    <HStack spacing={1.5}>
                      <span className="material-icons-outlined" style={{ fontSize: '15px', color: '#38a169' }}>check_circle</span>
                      <Text fontSize="sm" fontWeight="500" color="green.600">Job complete</Text>
                    </HStack>
                  </Box>
                )}
                <Box opacity={isCompleting ? 0.15 : 1}>
                <Flex justify="space-between" align="flex-start">
                {/* Title truncates (noOfLines) and the tag group never shrinks, so the tag
                    stays on the title's row at any width; the "more options" menu stays
                    outside this group so it never competes for that space. */}
                <Flex
                  flex={1}
                  minW={0}
                  justify="space-between"
                  align="flex-start"
                  wrap="nowrap"
                  gap={2}
                >
                  <VStack align="flex-start" spacing={0} minW={0} flex="1">
                    <Text fontSize="sm" fontWeight="500" color="gray.800" noOfLines={1}>{label}</Text>
                    {!isTerminalTab && !hideProjectContext && item.plan_name && (
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>{item.plan_name}</Text>
                    )}
                    {isInProgressTab && date && (
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>{formatDateTime(date)}</Text>
                    )}
                  </VStack>
                  <HStack spacing={1.5} flexShrink={0} align="center" minH="22px" flexWrap="wrap">
                    {!isTerminalTab && (
                      <Box position="relative" h="24px" minW={rowPrimaryAction ? '96px' : 'auto'}>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                          h="24px"
                          transition="opacity 0.15s ease, transform 0.15s ease"
                          _groupHover={rowPrimaryAction ? { opacity: 0, transform: 'translateX(-6px)' } : undefined}
                          _groupFocusWithin={rowPrimaryAction ? { opacity: 0, transform: 'translateX(-6px)' } : undefined}
                        >
                          {isRunning ? (
                            <HStack spacing={1} h="22px"><Spinner size="xs" color="brand.800" speed="0.9s" thickness="2px"/><Text fontSize="10px" color="brand.700" fontWeight="500">Running</Text></HStack>
                          ) : (
                            <Badge colorScheme={color} fontSize="10px" px={2} h="22px" display="inline-flex" alignItems="center" borderRadius="full">{status}</Badge>
                          )}
                        </Box>
                        {rowPrimaryAction && (
                          <Button
                            position="absolute"
                            right={0}
                            top={0}
                            size="xs"
                            h="24px"
                            px={2.5}
                            fontSize="10px"
                            borderRadius="full"
                            colorScheme="gray"
                            opacity={0}
                            transform="translateX(8px)"
                            transition="opacity 0.15s ease, transform 0.15s ease"
                            _groupHover={{ opacity: 1, transform: 'translateX(0)' }}
                            _groupFocusWithin={{ opacity: 1, transform: 'translateX(0)' }}
                            onClick={rowPrimaryAction.run}
                          >{rowPrimaryAction.label}</Button>
                        )}
                      </Box>
                    )}
                    {cueTag && !selectedItem && (
                      activeTab?.label === 'Completed' && date ? (
                        <Box position="relative" h="22px" minW="88px" overflow="hidden">
                          <Badge
                            colorScheme="gray"
                            variant="subtle"
                            fontSize="10px"
                            px={2}
                            h="22px"
                            display="inline-flex"
                            alignItems="center"
                            borderRadius="full"
                            position="absolute"
                            top={0}
                            right={0}
                            whiteSpace="nowrap"
                            opacity={1}
                            transform="translateY(0)"
                            transition="opacity 0.18s ease, transform 0.18s ease"
                            _groupHover={{ opacity: 0, transform: 'translateY(-6px)' }}
                          >
                            {cueTag}
                          </Badge>
                          <HStack
                            spacing={1}
                            position="absolute"
                            top={0}
                            right={0}
                            h="22px"
                            whiteSpace="nowrap"
                            opacity={0}
                            transform="translateY(6px)"
                            transition="opacity 0.18s ease, transform 0.18s ease"
                            _groupHover={{ opacity: 1, transform: 'translateY(0)' }}
                          >
                            <Box as="span" className="material-icons-outlined" fontSize="12px" color="gray.400">
                              check_circle
                            </Box>
                            <Text fontSize="10px" color="gray.500">{formatDateTime(date)}</Text>
                          </HStack>
                        </Box>
                      ) : (
                        <Badge
                          colorScheme="gray"
                          variant="subtle"
                          fontSize="10px"
                          px={2}
                          h="22px"
                          display="inline-flex"
                          alignItems="center"
                          borderRadius="full"
                        >
                          {cueTag}
                        </Badge>
                      )
                    )}
                  </HStack>
                </Flex>
                {isTask && (() => {
                      const isMissionItem = item.capability === 'agent_mission';
                      const missionStatus = item.mission_status;
                      const itemParameters = safeObject(item.parameters);
                      const waitingTaskStatuses = ['waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'blocked'];
                      const taskTerminal = ['completed', 'failed'].includes(itemStatus);
                      const showResume  = isMissionItem && item.mission_id && (
                        missionStatus === 'paused'
                        || (waitingTaskStatuses.includes(itemStatus) && !!itemParameters.continuation)
                      );
                      const showPause   = !taskTerminal && !showResume && isMissionItem && item.mission_id && !['paused', 'cancelled', 'completed'].includes(missionStatus);
                      // Cancel must stay available even when a mission is also resumable —
                      // a paused / needs-review mission can be resumed OR cancelled. Do not gate on !showResume.
                      const showCancel  = !taskTerminal && itemStatus !== 'suggested' && (
                        (isMissionItem && item.mission_id && !['cancelled', 'completed'].includes(missionStatus))
                        || ['todo', 'pending', 'paused', 'approved', 'awaiting_approval', 'waiting_for_user', 'waiting_for_approval', 'waiting_for_evidence', 'blocked'].includes(itemStatus)
                      );
                      const showDelete  = (itemStatus === 'suggested' || ['cancelled', 'failed', 'completed'].includes(itemStatus)) && !showResume;
                      if (!showPause && !showResume && !showCancel && !showDelete) return null;
                      return (
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={IconButton}
                            icon={<span className="material-icons-outlined" style={{ fontSize: '16px' }}>more_vert</span>}
                            size="xs" variant="ghost" colorScheme="gray"
                            aria-label="More options"
                            title="Job actions"
                            onClick={e => e.stopPropagation()}
                          />
                          <MenuList minW="150px" fontSize="sm" onClick={e => e.stopPropagation()}>
                            {showPause && (
                              <MenuItem
                                icon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>pause</span>}
                                onClick={e => handleListAction('pause', item, e)}
                              >
                                Pause
                              </MenuItem>
                            )}
                            {showResume && (
                              <MenuItem
                                icon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>play_arrow</span>}
                                onClick={e => handleListAction('resume', item, e)}
                              >
                                Resume Job
                              </MenuItem>
                            )}
                            {showCancel && (
                              <MenuItem
                                icon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>cancel</span>}
                                onClick={e => handleListAction('cancel', item, e)}
                              >
                                Cancel
                              </MenuItem>
                            )}
                            {showDelete && (
                              <MenuItem
                                color="red.500"
                                icon={<span className="material-icons-outlined" style={{ fontSize: '14px' }}>delete</span>}
                                onClick={e => handleListAction('delete', item, e)}
                              >
                                Delete
                              </MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      );
                    })()}
                </Flex>
                {activeTab?.routine && taskCadence(item) && itemStatus !== 'unscheduled' ? (
                  <>
                    <HStack spacing={1.5} mt={1}>
                      <Text fontSize="xs" color="gray.700" fontWeight="500">{cadenceLabel(taskCadence(item))}</Text>
                      {date && <Text fontSize="xs" color="gray.500">· {formatDateTime(date)}</Text>}
                    </HStack>
                    {isWorkItem && jobCardDescription(item) && (
                      <Text fontSize="xs" color="gray.600" noOfLines={1} mt={0.5}>{jobCardDescription(item)}</Text>
                    )}
                  </>
                ) : isTerminalTab ? null : (
                  <HStack justify="space-between" align="center" mt={1.5}>
                    <Text fontSize="xs" color="gray.600" noOfLines={1} flex={1} minW={0}>
                      {isWorkItem ? jobCardDescription(item) : ''}
                    </Text>
                    <HStack spacing={1} flexShrink={0} ml={2} align="center">
                      {date && !isInProgressTab && (
                        <Text fontSize="xs" color="gray.500">
                          {activeTab?.label === 'Planned' && isTask && !isProductRequirementItem(item) && ['todo', 'pending'].includes(itemStatus) && !isRunning && !isCompleting
                            ? `Planned for ${formatDateTime(date)}`
                            : formatDateTime(date)}
                        </Text>
                      )}
                    </HStack>
                  </HStack>
                )}
                </Box>
              </Box>
            );
          })}
        </Box>
        )}
        </>
      )}

      {activeTab?.label === 'Planned' && activeRoutinesCount > 0 && filtered.length > 0 && (
        <Text fontSize="xs" color="gray.500" px={1} pt={4}>
          You also have{' '}
          <Text
            as="span"
            color="blue.500"
            cursor="pointer"
            textDecoration="underline"
            textDecorationStyle="dotted"
            onClick={() => setStatusTab('Routines')}
          >
            {activeRoutinesCount} active routine{activeRoutinesCount === 1 ? '' : 's'} running in the background.
          </Text>
        </Text>
      )}

      {(showingSuggested || showingSuggestedOnDemand) && (
        <Box pt={6} mt="auto" pb={6}>
          <Box mb={3}>
            <Text fontSize="sm" color="gray.600" fontWeight="700">
              Suggested by Pabs
            </Text>
            <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="1.4">
              Jobs Pabs can run using this plan's strategy, evidence, lifecycle, and connected sources.
            </Text>
          </Box>
          <SimpleGrid columns={{ base: 1, md: selectedItem ? 1 : 2 }} spacing={3}>
            {suggestedItems.map((item, i) => {
              const label = jobDisplayLabel(item);
              const purpose = suggestedJobPurpose(item);
              const cadence = cadenceLabel(suggestedJobCadence(item));
              const worksWith = suggestedJobWorksWith(item);
              return (
                <Box
                  key={`${item.record_type}-${item.id}-${i}`}
                  role="group"
                  p={3}
                  minH="126px"
                  bg={selectedItem?.id === item.id && selectedItem?.record_type === item.record_type ? 'gray.100' : 'gray.50'}
                  border="1px solid"
                  borderColor={selectedItem?.id === item.id && selectedItem?.record_type === item.record_type ? 'gray.800' : 'transparent'}
                  borderRadius="md"
                  cursor="pointer"
                  transition="background-color 0.15s, border-color 0.15s"
                  _hover={{ bg: 'white', borderColor: 'gray.800' }}
                  onClick={() => openDetail(item, false)}
                >
                  <HStack align="flex-start" spacing={2.5}>
                    <span className="material-icons-outlined" style={{ fontSize: '18px', color: '#111827', marginTop: '1px' }}>
                      {suggestedJobIcon(item)}
                    </span>
                    <Box minW={0} flex={1}>
                      <HStack spacing={2} align="center" mb={1}>
                        <Text fontSize="xs" fontWeight="700" color="gray.900" noOfLines={1} flex={1} lineHeight="1.35">
                          {label}
                        </Text>
                        <Box position="relative" w="68px" h="18px" flexShrink={0}>
                          <Badge
                            position="absolute"
                            right={0}
                            top="1px"
                            colorScheme="gray"
                            variant="subtle"
                            fontSize="8px"
                            borderRadius="full"
                            px={1.5}
                            transition="opacity 0.15s, transform 0.15s"
                            _groupHover={{ opacity: 0, transform: 'translateX(-6px)' }}
                          >
                            Suggested
                          </Badge>
                          <ActivateJobButton
                            onActivate={() => handleListAction('activate', item)}
                            creditBalance={activationCredit.creditBalance}
                            creditCost={activationCredit.creditCost}
                            creditGated={activationCredit.creditGated}
                            creditsResetAt={activationCredit.creditsResetAt}
                            onTopUp={activationCredit.onTopUp}
                            stopPropagation
                            buttonProps={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              size: 'xs',
                              h: '18px',
                              px: 2,
                              fontSize: '8px',
                              colorScheme: 'gray',
                              borderRadius: 'full',
                              opacity: 0,
                              transform: 'translateX(8px)',
                              transition: 'opacity 0.15s, transform 0.15s',
                              _groupHover: { opacity: 1, transform: 'translateX(0)' },
                            }}
                          >
                            Schedule now
                          </ActivateJobButton>
                        </Box>
                      </HStack>
                      <HStack spacing={1.5} mb={1.5} align="center">
                        <Badge colorScheme="blue" variant="subtle" fontSize="8px" borderRadius="full" flexShrink={0} px={1.5}>
                          {purpose}
                        </Badge>
                        <Text fontSize="10px" color="gray.500" noOfLines={1} lineHeight="1.3">
                          {suggestedJobPurposeReason(purpose)}
                        </Text>
                      </HStack>
                      <Text fontSize="11px" color="gray.700" lineHeight="1.45" noOfLines={2}>
                        {jobCardDescription(item)}
                      </Text>
                      <VStack align="stretch" spacing={0.5} mt={2.5}>
                        <HStack spacing={1.5} color="gray.500">
                          <span className="material-icons-outlined" style={{ fontSize: '13px' }}>event_repeat</span>
                          <Text fontSize="10px">{cadence}</Text>
                        </HStack>
                        {worksWith.length > 0 && (
                          <HStack spacing={1.5} color="gray.500">
                            <span className="material-icons-outlined" style={{ fontSize: '13px' }}>hub</span>
                            <Text fontSize="10px" noOfLines={1}>Works with {worksWith.join(', ')}</Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  </HStack>
                </Box>
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      {showSuggestedToggle && !showingSuggestedOnDemand && (
        <Flex justify="center" pt={4} pb={2} mt="auto">
          <Button
            size="md"
            variant="outline"
            colorScheme="gray"
            px={6}
            leftIcon={
              suggestionsGenerating
                ? <Spinner size="sm" />
                : <span className="material-icons-outlined" style={{ fontSize: '18px' }}>auto_awesome</span>
            }
            isDisabled={suggestionsGenerating}
            onClick={() => {
              if (suggestedItems.length > 0) {
                setShowSuggestedJobs(value => !value);
              } else {
                generateSuggestions({ allowWithPlanned: true });
              }
            }}
          >
            {suggestedItems.length > 0 ? 'View suggested jobs' : 'Generate suggested jobs'}
          </Button>
        </Flex>
      )}

      </Box> {/* end list column */}

      {/* Detail panel.
          Desktop (md+): unchanged from before — its own column beside the (now 48%-wide) list.
          Mobile (below md): full-drawer overlay, portaled to the DrawerContent node so it
          covers the drawer's own header/close button too, matching LearningSidebar's overlay. */}
      {selectedItem && (() => {
        const detailContent = isClientActivity(selectedItem) ? <ClientActivityDetail item={selectedItem} onClose={closeDetail}/> : (
          <TaskDetailPanel
            item={selectedItem}
            onClose={closeDetail}
            onOpenLinked={openLinkedDetail}
            relatedRecords={linkedJobRecords}
            toolMeta={toolMeta}
            workflowMeta={workflowMeta}
            onRefresh={loadTasks}
            isRoutineView={selectedItemIsRoutine}
            activationCredit={activationCredit}
            onCloseDrawer={onCloseDrawer}
            onDelete={(id) => {
              removeItemFromData(id);
              if (selectedItem?.id === id) closeDetail();
            }}
          />
        );

        if (isDetailOverlayMode) {
          const overlay = (
            <Box
              position="absolute"
              inset={0}
              zIndex={10}
              bg="white"
              overflowY="auto"
              overflowX="hidden"
              boxShadow="-12px 0 32px rgba(15, 23, 42, 0.12)"
              transform={isDetailPanelOpen ? 'translateX(0)' : 'translateX(100%)'}
              transition="transform 320ms cubic-bezier(0.22, 1, 0.36, 1)"
              pointerEvents={isDetailPanelOpen ? 'auto' : 'none'}
              aria-hidden={!isDetailPanelOpen}
            >
              {detailContent}
            </Box>
          );
          return detailPortalContainerRef
            ? <Portal containerRef={detailPortalContainerRef}>{overlay}</Portal>
            : overlay;
        }

        return (
          <Box
            flex={1} minW={0} maxW="full" overflowY="auto" overflowX="hidden"
            transform={isDetailPanelOpen ? 'translateX(0)' : 'translateX(100%)'}
            transition="transform 320ms cubic-bezier(0.22, 1, 0.36, 1)"
          >
            {detailContent}
          </Box>
        );
      })()}
      <ConfirmActionDialog
        isOpen={!!confirmAction}
        leastDestructiveRef={leastDestructiveRef}
        title={confirmAction?.title}
        body={confirmAction?.body}
        confirmLabel={confirmAction?.confirmLabel}
        isLoading={false}
        onClose={() => setConfirmAction(null)}
        onConfirm={async () => {
          const action = confirmAction?.onConfirm;
          if (!action) return;
          await action();
          setConfirmAction(null);
        }}
      />
      <CreditTopUpModal
        isOpen={isTopUpModalOpen}
        onClose={closeTopUpModal}
        hasSavedPaymentMethod={hasSavedPaymentMethod}
        planType={planStatus?.planType || 'free'}
        initialMode={topUpModalMode}
        resetAt={creditsResetAt}
        reason={topUpReason}
      />
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------
function ClientActivityDetail({item,onClose}) {
  const [record,setRecord]=useState(null),[error,setError]=useState(false);
  useEffect(()=>{
    let active=true;setRecord(null);setError(false);
    fetchWithAuth(`/api/agent/tasks/${item.id}/detail`).then(async response=>{if(!response.ok)throw Error('Activity unavailable');return response.json();}).then(data=>{if(!data.task)throw Error('Activity unavailable');if(active)setRecord(data.task);}).catch(()=>{if(active)setError(true);});
    return ()=>{active=false;};
  },[item.id]);
  const current=record || item,result=safeObject(current.result),params=safeObject(current.parameters),failure=result.failure || {};
  return <Flex direction="column" h="full" minW={0} overflow="hidden">
    <HStack px={4} py={3} justify="space-between" align="start" borderBottom="1px solid" borderColor="gray.100">
      <Text fontWeight="600" overflowWrap="anywhere">{clientActivityLabel(current)}</Text>
      <IconButton aria-label="Close activity detail" size="sm" variant="ghost" flexShrink={0} onClick={onClose} icon={<span className="material-icons-outlined">close</span>}/>
    </HStack>
    <VStack align="stretch" spacing={4} p={4} overflowY="auto" minH={0}>
      {error ? <Text role="alert">Could not load this activity. Close and reopen it to retry.</Text> : !record ? <Spinner size="sm"/> : <>
        <HStack justify="space-between"><Text fontSize="sm" color="gray.600">{params.client || result.client || 'Connected client'}</Text><Badge colorScheme={STATUS_COLOR[current.status] || 'gray'}>{current.status}</Badge></HStack>
        <Text fontSize="sm" color="gray.600">{formatDateTime(current.completed_at || current.created_at)}</Text>
        {(current.error_message || result.error_message) && <Box role="alert" bg="red.50" color="red.800" p={3} borderRadius="md"><Text fontSize="sm" whiteSpace="pre-wrap" overflowWrap="anywhere">{current.error_message || result.error_message}</Text></Box>}
        {Array.isArray(failure.field_errors) && failure.field_errors.map((field,index)=><Text key={index} fontSize="sm" overflowWrap="anywhere">{field}</Text>)}
        {failure.next_action && <Text fontSize="sm">{failure.next_action}</Text>}
        <Text fontSize="sm" color="gray.600">This records a client call. It does not schedule or run a product Job.</Text>
        <Box as="details"><Box as="summary" cursor="pointer" fontSize="sm" color="gray.700" py={2}>Call details</Box>
          <Text as="pre" fontSize="xs" whiteSpace="pre-wrap" overflowWrap="anywhere" bg="gray.50" p={3} borderRadius="md">{JSON.stringify(require('../shared/mcpActivityData').redactActivityData({arguments:params.args,calls:result.calls,validation:result.failure,credit_usage:result.credit_usage,token_usage:result.token_usage}),null,2)}</Text>
        </Box>
      </>}
    </VStack>
  </Flex>;
}

const JobsView = ({ defaultTab = 'Tasks', planFilter = 'all', initialTaskId, onCloseDrawer, hideProjectContext, detailPortalContainerRef }) => {
  const { userDetails } = useContext(UserContext);
  const initialStatusTab = defaultTab === 'Workflows'
    ? 'Routines'
    : defaultTab === 'Needs decision'
      ? 'In progress'
      : 'Planned';

  return (
    <Flex direction="column" flex={1} minH={0} h="full" maxW="full" overflowX="hidden">
      <Box flex={1} minH={0} maxW="full" px={{ base: 4, md: 6 }} pt={3} pb={4} overflowY="hidden" overflowX="hidden" display="flex" flexDirection="column">
        <TasksTab
          userId={userDetails?.id}
          planFilter={planFilter}
          initialTaskId={initialTaskId}
          initialStatusTab={initialStatusTab}
          onCloseDrawer={onCloseDrawer}
          hideProjectContext={hideProjectContext}
          detailPortalContainerRef={detailPortalContainerRef}
        />
      </Box>
    </Flex>
  );
};

export default JobsView;
